import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  addEdge,
  Background,
  Controls,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type ReactFlowInstance
} from "@xyflow/react";
import dagre from "@dagrejs/dagre";
import {
  OperationCanvasNode,
  type NodeRunStatus,
  type OperationCanvasNodeData as CanvasNodeData,
  type OperationCanvasNodeType as CanvasNode
} from "../components/OperationCanvasNode.js";
import {
  aiProviderContextInputStatus,
  inferEdgeTypeFromConnection,
  isPortsConnectionValid,
  loopPairAccent,
  NODE_LABELS,
  normalizeGraphPort,
  supportsSchemaLock,
  type OperationGraphNodeType
} from "../lib/operationPorts.js";
import { loopPairLinkId, loopPairName, nodeToLoopPair, resolveLoopPairs } from "../lib/loopTopology.js";
import { OperationInspectorOperationLevel } from "../components/operationInspector/OperationInspectorOperationLevel.js";
import { type PipelineRecommendation } from "../components/operationInspector/ArchitectChat.js";
import {
  computeConfigDiff,
  computeBindingDiff,
  type ModelConfigChange,
  type ModelTrainConfig,
  type ModelBindingConfig,
  type ModelNodeKind,
  type ApplyModelConfigResult,
} from "../lib/modelConfigChange.js";
import { OperationInspectorProviderScope } from "../components/operationInspector/OperationInspectorProviderScope.js";
import type { ColumnDomain } from "../components/ColumnDomainControl.js";
import { nextOverrideMap, typeValidationMessage, type TypeValidationFailures } from "../lib/columnDomain.js";
import { loadNodeCreationShapes, nodeCreationShape } from "../lib/nodeCreationShapes.js";
import { findingsBadgeTitle, groupFindings, humanizeField, type NodeFindingsSummary } from "../lib/operationFindings.js";
import { RunDetailPanel } from "../components/operationInspector/RunDetailPanel.js";
import { WriteConflictBanner } from "../components/WriteConflictBanner.js";
import { layoutFromPositions, readWriteConflict, type WriteConflict } from "../lib/writeConflict.js";
import { createSingleFlight } from "../lib/singleFlight.js";
import { OperationInspectorPlexApi } from "../components/operationInspector/OperationInspectorPlexApi.js";
import { OperationInspectorTransform } from "../components/operationInspector/OperationInspectorTransform.js";
import { OperationInspectorPersist } from "../components/operationInspector/OperationInspectorPersist.js";
import { OperationInspectorPersistentProvider } from "../components/operationInspector/OperationInspectorPersistentProvider.js";
import { OperationInspectorLoopStart } from "../components/operationInspector/OperationInspectorLoopStart.js";
import { OperationInspectorLoopEnd } from "../components/operationInspector/OperationInspectorLoopEnd.js";
import { OperationInspectorPlatformProvider } from "../components/operationInspector/OperationInspectorPlatformProvider.js";
import { OperationInspectorFileProvider } from "../components/operationInspector/OperationInspectorFileProvider.js";
import { OperationInspectorModelTrain } from "../components/operationInspector/OperationInspectorModelTrain.js";
import { OperationInspectorModelPredict } from "../components/operationInspector/OperationInspectorModelPredict.js";
import { OperationInspectorModelEvaluate } from "../components/operationInspector/OperationInspectorModelEvaluate.js";
import { NodeAdvisorPanel } from "../components/NodeAdvisorPanel.js";
import { OperationInspectorReactiveViz } from "../components/operationInspector/OperationInspectorReactiveViz.js";
import { OperationInspectorKpiCard } from "../components/operationInspector/OperationInspectorKpiCard.js";
import { OperationInspectorTanTable } from "../components/operationInspector/OperationInspectorTanTable.js";
import { OperationInspectorWatch } from "../components/operationInspector/OperationInspectorWatch.js";
import { OperationInspectorAiProvider } from "../components/operationInspector/OperationInspectorAiProvider.js";
import { OperationInspectorEdge } from "../components/operationInspector/OperationInspectorEdge.js";
import { type NodeOutputRecord, type NodeRunHistoryEntry, type NodeMetadata } from "../components/operationInspector/nodeInspectorWidgets.js";
import "@xyflow/react/dist/style.css";
import { OperationNodeOutputsProvider } from "../components/OperationNodeOutputsContext.js";
import { MLNodeRunProvider } from "../components/MLNodeRunContext.js";
import { InspectorFlyout } from "../components/inspector/InspectorFlyout.js";
import { InspectorTabs } from "../components/inspector/InspectorTabs.js";
import type { InspectorSlot } from "../components/inspector/inspectorModel.js";
import { NodeNavDropdown, type NodeNavItem } from "../components/inspector/NodeNavDropdown.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState, NotAvailableState } from "../components/States.js";
import { apiFetch, ApiRequestError, isNotAvailable } from "../lib/api.js";
import { useOperationRun } from "../hooks/useOperationRun.js";
import {
  historyRowOutcome,
  isActiveRunStatus,
  nodeBadgeForRun,
  upstreamClosureIds,
  type NodeBadgeStatus,
  type OperationRun,
  type OperationRunNode,
} from "../lib/operationRun.js";
import { AI_USAGE_LIMIT_COOLDOWN_MS, isAiUsageLimitError } from "../lib/aiRateLimit.js";
import { fetchExecutionLog, type ExecutionLogEntry } from "../lib/executionLog.js";
import { setFeedbackContext, clearFeedbackContext } from "../lib/feedbackContext.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import type { QueryColumnMeta, QueryResultsPayload } from "../components/QueryResultsGrid.js";

type OperationKind = "unclassified" | "visualize";
type OperationStatus = "draft" | "invalid" | "published" | "paused" | "validate";
/**
 * The node types this editor can author.
 *
 * An ALIAS, not a third list. This was a hand-maintained union duplicating
 * `OperationGraphNodeType` — which is itself the console's mirror of the hub's
 * SIGNATURES, held to it by a drift gate. A third copy behind that gate would
 * drift silently, because nothing checked it: adding a type to the mirror and
 * forgetting this one produced a type error at best and a missing palette
 * entry at worst.
 */
type NodeType = OperationGraphNodeType;
type EdgeType = "data" | "scope";

type ProviderRef = { type: "shared"; provider_id: string } | { type: "local"; local_provider_id: string };

type BindingSource =
  | { type: "default" }
  | { type: "const"; value: string | number | boolean | null }
  | { type: "window"; window: string; field: "start_at" | "end_at" | "start_date" | "end_date" }
  | { type: "scope"; column: string }
  | { type: "variable"; variable: string };

type WindowDef =
    | {
        name: string;
        type: "relative";
        start_offset: { value: number; unit: "minute" | "hour" | "day" | "week" | "month" | "year" };
        end_offset: { value: number; unit: "minute" | "hour" | "day" | "week" | "month" | "year" };
      }
    | {
        name: string;
        type: "absolute";
        start_at: string;
        end_at: string;
    };

type OperationVariableType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";
type OperationVariableDef = {
  name: string;
  type: OperationVariableType;
  value: string | number | boolean | null;
  description?: string | null;
};

type ProviderNodeConfig = {
  provider: ProviderRef;
  bindings: Record<string, BindingSource>;
  preview?: { sample_rows?: number };
};

type TransformSpecV1 = { specVersion: "transform.v1"; output: "table" | "series"; ops: unknown[]; series?: unknown };
type TransformSpecV2 = { specVersion: "transform.v2"; output: "table" | "series"; sql: string; series?: unknown };
type TransformSpec = TransformSpecV1 | TransformSpecV2;
type TransformChatMessage = { id: string; role: "user" | "assistant"; text: string };
type TransformLayerStats = { runAt: string | null; durationMs: number | null; rowCount: number | null };



type OperationNode = {
  id: string;
  type: NodeType;
  label: string;
  description?: string | null;
  config: unknown;
  /**
   * ADR-0159 §3.4 — READ-ONLY legacy. Positions moved to the operation's
   * `layout` channel; specs written before that still carry this and the
   * canvas still reads it as a fallback, but nothing writes it any more. The
   * persist seam strips it, so it empties out as pipelines are saved.
   */
  ui?: { x: number; y: number; w?: number; h?: number };
};

type OperationEdge = {
  id: string;
  edge_type: EdgeType;
  from_node: string;
  from_port: string;
  to_node: string;
  to_port: string;
};

type OperationSpecV1 = {
  version: "operation.v1";
  status: OperationStatus;
  tags: string[];
  windows: WindowDef[];
  variables?: OperationVariableDef[];
  execution_policy: Record<string, unknown>;
  output_policy: Record<string, unknown>;
  graph: {
    nodes: OperationNode[];
    edges: OperationEdge[];
    ui?: { viewport?: { x: number; y: number; zoom: number } };
  };
  local_providers?: Record<string, unknown>;
};

type Operation = {
  id: string;
  kind: OperationKind;
  name: string;
  description: string | null;
  status: OperationStatus;
  previous_status: OperationStatus | null;
  status_reason: string | null;
  tags: string[];
  spec: unknown;
  created_at?: string;
  updated_at?: string;
  owner_email?: string | null;
  /** ADR-0146: per-node schema-lock state. The frozen shape itself is never
   *  sent — `frozen` says whether one has been captured yet. */
  node_schema_locks?: Record<string, { locked_at: string; frozen: boolean }>;
  /**
   * ADR-0159: canvas placement, on its own channel beside the spec. Dragging a
   * step writes here and nowhere else, so a drag can never carry — or revert —
   * structure the way it did while positions lived inside the spec.
   */
  layout?: { nodes?: Record<string, { x: number; y: number }> };
  /** ADR-0159: who last wrote this row, and the surface's name in plain words. */
  last_write_actor_label?: string;
  last_write_source_display?: string;
};


type ProviderParamType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";

type ProviderParameter = {
  name: string;
  type: ProviderParamType;
  required: boolean;
  default?: string | number | boolean | null;
  description?: string | null;
};

type ProviderDefinitionDraft = {
  /** ADR-0085 Phase 2: explicit kind discriminator. Absent = "connector_sql" for back-compat. */
  kind?: "connector_sql" | "plex_api";
  name: string;
  description?: string | null;
  /** Set when kind is "connector_sql" (default). */
  connector_id?: string;
  /** Set when kind is "plex_api". */
  integration_id?: string;
  source_spec:
    | { specVersion: "source.v1"; kind: "sql"; sql: string }
    | { datasource_id: number; inputs_template?: string };
  parameters: ProviderParameter[];
  schema_contract: { columns: QueryColumnMeta[] } | null;
  /** When true, SQL matches production-locked behavior (read-only in the inspector; persisted in spec). */
  source_sql_locked?: boolean;
  /** ADR-0146: the provider's schema lock. Read-only here — the lock is managed
   *  on the provider (one lock, every consuming operation); the inspector only
   *  points at it. */
  schema_locked?: boolean;
};

type DataProvider = {
  id: string;
  name: string;
  description?: string | null;
  /** ADR-0146: provider-level schema lock state (never the frozen shape itself). */
  schema_locked?: boolean;
  connector_id: string | null;
  source_spec?: { specVersion: "source.v1"; kind: "sql"; sql: string };
  parameters?: ProviderParameter[];
  last_source_result?: QueryResultsPayload | null;
  last_run_at?: string | null;
  source_sql_locked?: boolean;
};

type Connector = { id: string; name: string };

type ValidationIssue = {
  path: string;
  code: string;
  message: string;
  level: "error" | "warning";
};

type GraphSummary = {
  node_count: number;
  edge_count: number;
  fan_in_points: string[];
  output_nodes: Array<{ node_id: string; output_type: "visualization"; terminal: boolean }>;
};

type ProviderPreviewState = {
  loading: boolean;
  error: string | null;
  result: QueryResultsPayload | null;
};

type RunDetailEntry = {
  id: string;
  run_at: string;
  node_type: string;
  // ADR-0128: run-based history feeds the node's run status
  // (from_cache / succeeded / failed / skipped), not just the legacy
  // shared-provider success/failed; and duration is null when never measured.
  duration_ms: number | null;
  status: string;
  row_count: number | null;
  error: string | null;
  resolved_sql: string;
  resolved_params: Record<string, string | number | boolean | null>;
  /** ADR-0085 Phase 2: captured request shape for API-call providers (Plex today). */
  api_call?: {
    /** e.g. "plex_datasource_execute". Drives the label on the detail panel. */
    kind: string;
    /** Human-readable summary line (e.g. "Plex datasource 1791"). */
    summary?: string | null;
    /** The literal body that was sent to the upstream API. */
    body: unknown;
  } | null;
};

type CanvasEdge = Edge<{ edge_type: EdgeType }>;
type SaveOptions = { silent?: boolean; localProvidersOverride?: Record<string, ProviderDefinitionDraft> };

const WINDOW_UNITS: Array<"minute" | "hour" | "day" | "week" | "month" | "year"> = ["minute", "hour", "day", "week", "month", "year"];
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Strip characters that don't belong in an identifier name as the user types. */
/**
 * ADR-0146: a node bound to a SHARED provider takes its shape from the provider
 * row, so its lock is managed on the provider (one lock, every consuming
 * operation) — the in-op control is hidden and a pointer note shown instead.
 */
function isNodeBoundToSharedProvider(node: { data: { config?: Record<string, unknown> } }): boolean {
  const ref = node.data.config?.provider as { type?: string } | null | undefined;
  return ref?.type === "shared";
}

/** ADR-0146: pill tooltip. Always a full absolute timestamp — never relative. */
function schemaLockTitle(lock: { locked_at: string; frozen: boolean }): string {
  const when = new Date(lock.locked_at).toLocaleString();
  return lock.frozen
    ? `Schema locked ${when} — output shape is frozen. A run that changes the shape is flagged for validation. Unlock to re-freeze.`
    : `Schema locked ${when} — the shape will be frozen on the next successful run.`;
}

function sanitizeIdentifier(raw: string): string {
  // Remove anything that isn't a letter, digit, or underscore
  let cleaned = raw.replace(/[^A-Za-z0-9_]/g, "");
  // Strip leading digits (must start with letter or underscore)
  cleaned = cleaned.replace(/^[0-9]+/, "");
  return cleaned;
}

function sqlLiteral(value: string | number | boolean | null): string {
  if (value === null) return "NULL";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "NULL";
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  return `'${value.replace(/'/g, "''")}'`;
}

function addOffset(base: Date, offset: { value: number; unit: "minute" | "hour" | "day" | "week" | "month" | "year" }): Date {
  const next = new Date(base.getTime());
  if (offset.unit === "minute") next.setUTCMinutes(next.getUTCMinutes() + offset.value);
  else if (offset.unit === "hour") next.setUTCHours(next.getUTCHours() + offset.value);
  else if (offset.unit === "day") next.setUTCDate(next.getUTCDate() + offset.value);
  else if (offset.unit === "week") next.setUTCDate(next.getUTCDate() + offset.value * 7);
  else if (offset.unit === "month") next.setUTCMonth(next.getUTCMonth() + offset.value);
  else next.setUTCFullYear(next.getUTCFullYear() + offset.value);
  return next;
}

function toIsoDate(value: string): string {
  return value.slice(0, 10);
}

function substituteColonParams(sql: string, valuesByParam: Record<string, string | number | boolean | null>): string {
  return sql.replace(/(^|[^:]):([A-Za-z_][A-Za-z0-9_]*)\b/g, (full, prefix: string, name: string) => {
    if (!(name in valuesByParam)) return full;
    return `${prefix}${sqlLiteral(valuesByParam[name])}`;
  });
}

function normalizeTagsFromInput(input: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of input.split(",")) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/**
 * ADR-0133 Amendment 12 — the canonical starting config for a new step is
 * SUBSTRATE, owned by the hub, so the console and the MCP door cannot produce
 * different objects for the same intent. This is now a reader: the shapes are
 * fetched once per session by `loadNodeCreationShapes()` and served from
 * `src/lib/nodeCreationShapes.ts`.
 *
 * The per-type literals that used to live here moved verbatim to
 * `ops/nodeAuthoringContracts.ts` on the hub. Do not reintroduce them here —
 * a second copy is the drift this change exists to remove, and the drift-gate
 * on the hub cannot see a copy that lives in this repo.
 *
 * Canvas placement and default label remain console UI and stay in this file.
 */
function defaultNodeConfig(type: NodeType): Record<string, unknown> {
  return nodeCreationShape(type);
}

function createDefaultLocalProviderDraft(connectorId: string, nameSeed: string): ProviderDefinitionDraft {
  return {
    kind: "connector_sql",
    name: nameSeed,
    description: "",
    connector_id: connectorId,
    source_spec: { specVersion: "source.v1", kind: "sql", sql: "" },
    parameters: [],
    schema_contract: null,
    source_sql_locked: false
  };
}

function createDefaultPlexLocalProviderDraft(integrationId: string | null, nameSeed: string): ProviderDefinitionDraft {
  return {
    kind: "plex_api",
    name: nameSeed,
    description: "",
    integration_id: integrationId ?? "",
    // datasource_id: 0 fails validation (must be positive); user must set a real id.
    source_spec: { datasource_id: 0 },
    parameters: [],
    schema_contract: null,
    source_sql_locked: false
  };
}

function coerceLocalProviderDraft(raw: unknown, connectorFallback: string): ProviderDefinitionDraft {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createDefaultLocalProviderDraft(connectorFallback, "Provider");
  }
  const r = raw as Record<string, unknown>;
  const name = typeof r.name === "string" ? r.name : "";
  const description = r.description == null ? null : String(r.description);
  const rawKind = typeof r.kind === "string" ? r.kind : "connector_sql";

  // ADR-0085 Phase 2: discriminate on kind.
  if (rawKind === "plex_api") {
    const integration_id = typeof r.integration_id === "string" ? r.integration_id : "";
    const srcRaw = r.source_spec;
    let datasource_id = 0;
    if (srcRaw && typeof srcRaw === "object" && !Array.isArray(srcRaw)) {
      const s = srcRaw as Record<string, unknown>;
      if (typeof s.datasource_id === "number") datasource_id = s.datasource_id;
    }
    const parameters = Array.isArray(r.parameters) ? (r.parameters as ProviderParameter[]) : [];
    // ADR-0085 Phase 2: preserve inputs_template + schema_contract round-trip.
    const inputs_template =
      typeof (r.source_spec as { inputs_template?: unknown } | null)?.inputs_template === "string"
        ? ((r.source_spec as { inputs_template?: string }).inputs_template as string)
        : undefined;
    let schema_contract: ProviderDefinitionDraft["schema_contract"] = null;
    const sc = r.schema_contract;
    if (sc && typeof sc === "object" && !Array.isArray(sc) && "columns" in sc && Array.isArray((sc as { columns: unknown }).columns)) {
      schema_contract = { columns: (sc as { columns: QueryColumnMeta[] }).columns };
    }
    return {
      kind: "plex_api",
      name,
      description,
      integration_id,
      source_spec: inputs_template !== undefined ? { datasource_id, inputs_template } : { datasource_id },
      parameters,
      schema_contract,
      source_sql_locked: false
    };
  }

  const connector_id = typeof r.connector_id === "string" && r.connector_id.trim() ? r.connector_id : connectorFallback;
  const srcRaw = r.source_spec;
  let source_spec: ProviderDefinitionDraft["source_spec"] = { specVersion: "source.v1", kind: "sql", sql: "" };
  if (srcRaw && typeof srcRaw === "object" && !Array.isArray(srcRaw)) {
    const s = srcRaw as Record<string, unknown>;
    if (typeof s.sql === "string") {
      source_spec = { specVersion: "source.v1", kind: "sql", sql: s.sql };
    }
  }
  const parameters = Array.isArray(r.parameters) ? (r.parameters as ProviderParameter[]) : [];
  let schema_contract: ProviderDefinitionDraft["schema_contract"] = null;
  const sc = r.schema_contract;
  if (sc && typeof sc === "object" && !Array.isArray(sc) && "columns" in sc && Array.isArray((sc as { columns: unknown }).columns)) {
    schema_contract = { columns: (sc as { columns: QueryColumnMeta[] }).columns };
  }
  return {
    kind: "connector_sql",
    name,
    description,
    connector_id,
    source_spec,
    parameters,
    schema_contract,
    source_sql_locked: Boolean(r.source_sql_locked)
  };
}

function normalizeLocalProvidersRecord(raw: Record<string, unknown>, connectorFallback: string): Record<string, ProviderDefinitionDraft> {
  const out: Record<string, ProviderDefinitionDraft> = {};
  for (const [id, entry] of Object.entries(raw)) {
    out[id] = coerceLocalProviderDraft(entry, connectorFallback);
  }
  return out;
}

function materialDefinitionSnapshot(provider: ProviderDefinitionDraft): string {
  return JSON.stringify({
    connector_id: provider.connector_id,
    source_spec: provider.source_spec,
    parameters: provider.parameters
  });
}

function areSchemaColumnsEqual(left: QueryColumnMeta[] | null | undefined, right: QueryColumnMeta[] | null | undefined): boolean {
  return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
}

function defaultSpec(status: OperationStatus, tags: string[]): OperationSpecV1 {
  return {
    version: "operation.v1",
    status,
    tags,
    windows: [],
    variables: [],
    execution_policy: {},
    output_policy: {},
    graph: {
      nodes: [],
      edges: []
    },
    local_providers: {}
  };
}

function parseSpecV1(raw: unknown, status: OperationStatus, tags: string[]): OperationSpecV1 {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return defaultSpec(status, tags);
  const rec = raw as Record<string, unknown>;
  if (rec.version !== "operation.v1") return defaultSpec(status, tags);
  const graph = rec.graph && typeof rec.graph === "object" && !Array.isArray(rec.graph) ? (rec.graph as Record<string, unknown>) : {};
  const nodes = Array.isArray(graph.nodes) ? (graph.nodes as OperationNode[]) : [];
  const edges = Array.isArray(graph.edges) ? (graph.edges as OperationEdge[]) : [];
  return {
    version: "operation.v1",
    status,
    tags,
    windows: Array.isArray(rec.windows) ? (rec.windows as WindowDef[]) : [],
    variables: Array.isArray(rec.variables) ? (rec.variables as OperationVariableDef[]) : [],
    execution_policy: rec.execution_policy && typeof rec.execution_policy === "object" && !Array.isArray(rec.execution_policy)
      ? (rec.execution_policy as Record<string, unknown>)
      : {},
    output_policy: rec.output_policy && typeof rec.output_policy === "object" && !Array.isArray(rec.output_policy)
      ? (rec.output_policy as Record<string, unknown>)
      : {},
    graph: {
      nodes,
      edges,
      ui: graph.ui && typeof graph.ui === "object" && !Array.isArray(graph.ui) ? (graph.ui as OperationSpecV1["graph"]["ui"]) : undefined
    },
    local_providers: rec.local_providers && typeof rec.local_providers === "object" && !Array.isArray(rec.local_providers)
      ? (rec.local_providers as Record<string, unknown>)
      : {}
  };
}

function toCanvasNode(node: OperationNode, layout?: Record<string, { x: number; y: number }>): CanvasNode {
  const nodeType = node.type;
  // ADR-0159 — the layout channel is authoritative; `node.ui` is read only as a
  // fallback for specs written before positions moved out, and is never
  // written back. Once the seam has stripped a given spec, this falls away.
  const placed = layout?.[node.id];
  return {
    id: node.id,
    type: "operationNode",
    position: { x: placed?.x ?? node.ui?.x ?? 0, y: placed?.y ?? node.ui?.y ?? 0 },
    data: {
      nodeType,
      label: node.label || NODE_LABELS[nodeType],
      description: node.description ?? null,
      config: node.config && typeof node.config === "object" && !Array.isArray(node.config)
        ? (node.config as Record<string, unknown>)
        : defaultNodeConfig(nodeType)
    }
  };
}

function stripPortHandle(h: string | null | undefined): string {
  if (!h) return "";
  const at = h.indexOf("@");
  return at > 0 ? h.slice(0, at) : h;
}

function toCanvasEdge(edge: OperationEdge, fromNodeType: string, toNodeType: string): CanvasEdge {
  const et = edge.edge_type;
  const fromPort = normalizeGraphPort({ port: edge.from_port, nodeType: fromNodeType, direction: "out", edgeType: et });
  const toPort = normalizeGraphPort({ port: edge.to_port, nodeType: toNodeType, direction: "in", edgeType: et });
  const scopeEdge = et === "scope";
  return {
    id: edge.id,
    source: edge.from_node,
    sourceHandle: fromPort,
    target: edge.to_node,
    targetHandle: toPort,
    markerEnd: { type: MarkerType.ArrowClosed },
    data: { edge_type: et },
    animated: scopeEdge,
    style: scopeEdge
      ? { stroke: "var(--op-port-scope)", strokeDasharray: "4 4" }
      : { stroke: "var(--op-port-tabular)" }
  };
}

function canvasEdgeToOperationEdge(edge: CanvasEdge, canvasNodes: CanvasNode[]): OperationEdge {
  const src = canvasNodes.find((n) => n.id === edge.source);
  const tgt = canvasNodes.find((n) => n.id === edge.target);
  const rawFrom = stripPortHandle(edge.sourceHandle);
  const rawTo = stripPortHandle(edge.targetHandle);
  const st = src?.data.nodeType ?? "provider";
  const tt = tgt?.data.nodeType ?? "provider";
  const inferred = inferEdgeTypeFromConnection({
    sourceNodeType: st,
    targetNodeType: tt,
    sourceHandle: rawFrom,
    targetHandle: rawTo
  });
  const et: EdgeType = inferred ?? edge.data?.edge_type ?? "data";
  return {
    id: edge.id,
    edge_type: et,
    from_node: edge.source,
    from_port: normalizeGraphPort({ port: rawFrom, nodeType: st, direction: "out", edgeType: et }),
    to_node: edge.target,
    to_port: normalizeGraphPort({ port: rawTo, nodeType: tt, direction: "in", edgeType: et })
  };
}

function toOperationNode(node: CanvasNode): OperationNode {
  // ADR-0159 §3.4 — no `ui` here, deliberately. Placement travels on the layout
  // channel; emitting it would put position back in the dirty check and make
  // every drag a spec save again.
  return {
    id: node.id,
    type: node.data.nodeType,
    label: node.data.label,
    description: node.data.description?.trim() || null,
    config: node.data.config
  };
}

function typeLabel(kind: OperationKind): string {
  if (kind === "visualize") return "Visualization";
  return "No output yet";
}

function OpPaletteGlyphSql({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function OpPaletteGlyphAi({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4v3M12 17v3M5.6 7.6l2.1 2.1M16.3 14.3l2.1 2.1M4 12h3M17 12h3M5.6 16.4l2.1-2.1M16.3 9.7l2.1-2.1"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="2.25" stroke="currentColor" strokeWidth="1.65" />
    </svg>
  );
}

function GlyphUndo({ className }: { className?: string }) {
  // lucide "undo-2" geometry — hook arrow curving back to the left.
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 14 4 9l5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5 5.5 5.5 0 0 1-5.5 5.5H11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GlyphRedo({ className }: { className?: string }) {
  // lucide "redo-2" geometry — mirror of undo, curving back to the right.
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="m15 14 5-5-5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5 5.5 5.5 0 0 0 9.5 20H13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Normalizes the two shapes stored in operation_node_outputs.transform_result:
 *   - Provider nodes store source output = QueryResultsPayload directly: { columns, rows, rowCount, durationMs }
 *   - Transform nodes store transform output = TransformResult: { kind: "table", table: { columns, rows, rowCount } }
 */
function extractQueryPayload(transformResult: unknown): QueryResultsPayload | null {
  if (!transformResult || typeof transformResult !== "object") return null;
  const r = transformResult as Record<string, unknown>;
  // TransformResult shape
  if (r.kind === "table" && r.table && typeof r.table === "object") {
    const t = r.table as Record<string, unknown>;
    if (Array.isArray(t.columns) && Array.isArray(t.rows)) {
      return { columns: t.columns as QueryColumnMeta[], rows: t.rows as unknown[][], rowCount: Number(t.rowCount ?? 0), durationMs: Number(t.durationMs ?? 0) };
    }
  }
  // Direct QueryResultsPayload shape
  if (Array.isArray(r.columns) && Array.isArray(r.rows)) {
    return { columns: r.columns as QueryColumnMeta[], rows: r.rows as unknown[][], rowCount: Number(r.rowCount ?? 0), durationMs: Number(r.durationMs ?? 0) };
  }
  return null;
}

export function OperationEditor() {
  const params = useParams<{ id: string }>();
  const operationId = params.id ?? "";
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const returnLabel = searchParams.get("returnLabel");
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** ADR-0205 Amendment 3: opened by link, but this person may not use it. */
  const [notAvailable, setNotAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [incompatibleVersion, setIncompatibleVersion] = useState<string | null>(null);

  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [providers, setProviders] = useState<DataProvider[]>([]);
  // ADR-0085 Phase 2: tenant Plex integrations — palette button appears iff ≥1 exists.
  const [plexIntegrations, setPlexIntegrations] = useState<Array<{ id: string; name: string }>>([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [operationMeta, setOperationMeta] = useState<NodeMetadata>({
    createdAt: null,
    updatedAt: null,
    ownerEmail: null
  });
  const [status, setStatus] = useState<OperationStatus>("draft");
  const [statusReason, setStatusReason] = useState<string | null>(null);
  const [tagsText, setTagsText] = useState("");
  const [operationWindows, setOperationWindows] = useState<WindowDef[]>([]);
  const [operationVariables, setOperationVariables] = useState<OperationVariableDef[]>([]);
  const [windowsModalOpen, setWindowsModalOpen] = useState(false);
  const [variablesModalOpen, setVariablesModalOpen] = useState(false);
  const [editingWindowIndex, setEditingWindowIndex] = useState<number | null>(null);
  const [editingVariableIndex, setEditingVariableIndex] = useState<number | null>(null);
  const [pendingWindowBinding, setPendingWindowBinding] = useState<{
    nodeId: string;
    paramName: string;
    field: "start_at" | "end_at" | "start_date" | "end_date";
  } | null>(null);
  const [pendingVariableBinding, setPendingVariableBinding] = useState<{
    nodeId: string;
    paramName: string;
  } | null>(null);
  const [windowDraft, setWindowDraft] = useState<WindowDef>({
    name: "window_1",
    type: "relative",
    start_offset: { value: -6, unit: "month" },
    end_offset: { value: 0, unit: "day" }
  });
  const [windowStartOffsetText, setWindowStartOffsetText] = useState("-6");
  const [windowEndOffsetText, setWindowEndOffsetText] = useState("0");
  const [variableDraft, setVariableDraft] = useState<OperationVariableDef>({
    name: "var_1",
    type: "string",
    value: "",
    description: ""
  });
  const [parameterModalOpen, setParameterModalOpen] = useState(false);
  const [editingParameterIndex, setEditingParameterIndex] = useState<number | null>(null);
  const [parameterDefaultText, setParameterDefaultText] = useState("");
  const [parameterDraft, setParameterDraft] = useState<ProviderParameter>({
    name: "param_1",
    type: "string",
    required: false,
    default: "",
    description: ""
  });
  const [bindingModalOpen, setBindingModalOpen] = useState(false);
  const [bindingModalMode, setBindingModalMode] = useState<"add" | "edit">("add");
  const [bindingModalOriginalParamName, setBindingModalOriginalParamName] = useState<string | null>(null);
  const [bindingModalParamName, setBindingModalParamName] = useState("");
  const [bindingModalParamType, setBindingModalParamType] = useState<ProviderParamType>("string");
  const [bindingModalHasDefault, setBindingModalHasDefault] = useState(false);
  const [bindingModalAllowScope, setBindingModalAllowScope] = useState(false);
  const [bindingModalSourceType, setBindingModalSourceType] = useState<BindingSource["type"]>("const");
  const [bindingConstText, setBindingConstText] = useState("");
  const [bindingWindowName, setBindingWindowName] = useState("");
  const [bindingWindowField, setBindingWindowField] = useState<"start_at" | "end_at" | "start_date" | "end_date">("start_at");
  const [bindingVariableName, setBindingVariableName] = useState("");
  const [bindingScopeColumn, setBindingScopeColumn] = useState("");
  const [kind, setKind] = useState<OperationKind>("unclassified");
  const [localProviders, setLocalProviders] = useState<Record<string, ProviderDefinitionDraft>>({});
  const [sharedProviderCache, setSharedProviderCache] = useState<Record<string, ProviderDefinitionDraft>>({});
  const [providerPreviewByKey, setProviderPreviewByKey] = useState<Record<string, ProviderPreviewState>>({});
  // ADR-0128 — Hub Execution Isolation. The async run API drives all node
  // execution. `runController` handles POST → poll runs/:runId → completion.
  const runController = useOperationRun();
  // Active run id per node (the node that is the run's target). Used by the
  // Stop button + run-detail surface. Cleared when the run settles.
  const [activeRunIdByNode, setActiveRunIdByNode] = useState<Record<string, string>>({});
  // Latest run detail (run + per-node rows) keyed by runId, for the run-detail
  // inspector surface. Updated on every poll tick + at completion.
  const [runDetailByRunId, setRunDetailByRunId] = useState<
    Record<string, { run: OperationRun; nodes: OperationRunNode[] }>
  >({});
  const [providerInspectorTab, setProviderInspectorTab] = useState<
    "metadata" | "scope" | "sql" | "parameters" | "bindings" | "schema" | "cache" | "history"
  >("metadata");
  const [plexInspectorTab, setPlexInspectorTab] = useState<
    "metadata" | "scope" | "inputs" | "parameters" | "bindings" | "schema" | "cache" | "history"
  >("metadata");
  // ADR-0129 — the model-node inspector spine is driven in controlled mode so an
  // Architect delegate can land the user on the node's Advisor (AI) slot.
  const [modelInspectorSlot, setModelInspectorSlot] = useState<InspectorSlot>("setup");
  const [historyDetailEntry, setHistoryDetailEntry] = useState<RunDetailEntry | null>(null);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  // ADR-0129 — the inspector is a fixed overlay flyout; its top aligns with the
  // measured top of the canvas area (editorAreaRef) so there's no magic offset.
  const editorAreaElRef = useRef<HTMLElement | null>(null);
  const [inspectorFlyoutTop, setInspectorFlyoutTop] = useState(0);
  const measureInspectorFlyoutTop = useCallback(() => {
    const el = editorAreaElRef.current;
    if (el) setInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  // Callback ref: measure as soon as the canvas area actually attaches. The
  // editor renders a loading state first, so an on-mount effect measured before
  // the section existed and left the offset stuck at 0 (flyout over the header).
  const editorAreaRef = useCallback((el: HTMLElement | null) => {
    editorAreaElRef.current = el;
    if (el) setInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  const [paletteOpen, setPaletteOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("atlas.opEditor.paletteOpen") !== "false"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("atlas.opEditor.paletteOpen", String(paletteOpen)); } catch {}
  }, [paletteOpen]);

  // ── Transform node inspector state ────────────────────────────────────────
  // ADR-0129 — controlled spine slot (editor-owned so a completed run can jump
  // to Data). 2.2c-async — run/result state is keyed per node so a run on node A
  // never paints node B's inspector; busy derives from the ADR-0128
  // `activeRunIdByNode` map plus a per-node "starting" flag for the pre-run flush.
  const [transformActiveSlot, setTransformActiveSlot] = useState<InspectorSlot>("setup");
  const [transformJson, setTransformJson] = useState("");
  const [transformJsonError, setTransformJsonError] = useState<string | null>(null);
  const [transformSql, setTransformSql] = useState("");
  // Per-node draft of the unsent transform-chat composer message. Keyed by node id
  // so switching nodes preserves each node's in-progress message instead of leaking
  // one node's draft into another.
  const [transformDrafts, setTransformDrafts] = useState<Record<string, string>>({});
  const [transformSubTab, setTransformSubTab] = useState<"data" | "schema">("data");
  const [transformPreviewByNode, setTransformPreviewByNode] = useState<Record<string, QueryResultsPayload | null>>({});
  const [transformStatsByNode, setTransformStatsByNode] = useState<Record<string, TransformLayerStats | null>>({});
  const [transformRunBusyByNode, setTransformRunBusyByNode] = useState<Record<string, boolean>>({});
  const [transformRunErrorByNode, setTransformRunErrorByNode] = useState<Record<string, string | null>>({});
  const [transformSessionId, setTransformSessionId] = useState<string | null>(null);
  const [transformMessages, setTransformMessages] = useState<TransformChatMessage[]>([]);
  const [transformSending, setTransformSending] = useState(false);
  const [transformInteractionMode, setTransformInteractionMode] = useState<"agent" | "ask">("agent");
  const transformChatScrollRef = useRef<HTMLDivElement>(null);
  // Mirror of transformSessionId for cancel-clicked-mid-flight: state may not
  // have propagated to a fresh handler closure yet when Cancel fires.
  const transformSessionIdRef = useRef<string | null>(null);
  // Keep the ref synced to state for ALL writers — not just ensureTransformSession.
  // Sessions loaded from the server (node reopened with existing chat history) set
  // the state but not the ref; without this, cancelTransformChat reads a null ref
  // and the Cancel button silently no-ops for any node that already has a session.
  useEffect(() => {
    transformSessionIdRef.current = transformSessionId;
  }, [transformSessionId]);

  // ── Visualization output node inspector state ─────────────────────────────
  // (ADR-0080: legacy `output.visualization` inspector state removed —
  // vizTab / vizJson(Error) / vizRun{Busy,Error} / viz{SessionId,Messages,Sending} /
  // vizInteractionMode / vizForceRefresh — all owned by OperationInspectorReactiveViz
  // / OperationInspectorTanTable / OperationInspectorKpiCard internally now.)
  const [aiChatCooldownUntil, setAiChatCooldownUntil] = useState(0);
  useEffect(() => {
    if (aiChatCooldownUntil <= Date.now()) return;
    const ms = aiChatCooldownUntil - Date.now();
    const id = window.setTimeout(() => setAiChatCooldownUntil(0), ms);
    return () => window.clearTimeout(id);
  }, [aiChatCooldownUntil]);
  const aiChatBlocked = aiChatCooldownUntil > Date.now();
  const [aiProviderExecutingIds, setAiProviderExecutingIds] = useState<ReadonlySet<string>>(new Set());
  function handleAiProviderRunStatus(nodeId: string, status: NodeRunStatus) {
    setAiProviderExecutingIds((prev) => {
      const next = new Set(prev);
      if (status === "running") next.add(nodeId);
      else next.delete(nodeId);
      return next;
    });
    setRunStatus([nodeId], status);
  }
  const [aiProviderAuthoringBusyIds, setAiProviderAuthoringBusyIds] = useState<ReadonlySet<string>>(new Set());
  const setAiProviderAuthoringBusy = useCallback((nodeId: string, busy: boolean) => {
    setAiProviderAuthoringBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(nodeId);
      else next.delete(nodeId);
      return next;
    });
  }, []);
  type TenantAiModelRow = {
    model_id: string;
    status: string;
    capabilities?: {
      temperature: boolean;
      max_output_tokens: boolean;
      response_format: boolean;
      web_search: boolean;
      tools: boolean;
    };
  };
  const [tenantAiModels, setTenantAiModels] = useState<TenantAiModelRow[]>([]);
  const [selectedAiModelId, setSelectedAiModelId] = useState("");
  const aiPrefSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ADR-0080: legacy viz-inspector React state (vizShowSpec/vizShowChat,
  // vizRenderModel cache, vizChatScrollRef) deleted with
  // OperationInspectorVisualization. ReactiveViz/TanTable/KPICard inspectors
  // manage their own equivalents internally.

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ models: TenantAiModelRow[]; hub_default: string; user_preference: string | null }>(
          "/api/tenant/ai/models"
        );
        setTenantAiModels(data.models);
        const pref = data.user_preference;
        const validPref = Boolean(pref && data.models.some((m) => m.model_id === pref));
        const pick = (validPref ? pref! : null) ?? data.hub_default ?? data.models[0]?.model_id ?? "";
        setSelectedAiModelId(pick);
      } catch {
        setTenantAiModels([]);
      }
    })();
  }, []);

  const scheduleSaveAiModelPreference = useCallback((modelId: string) => {
    if (aiPrefSaveTimerRef.current) clearTimeout(aiPrefSaveTimerRef.current);
    aiPrefSaveTimerRef.current = setTimeout(() => {
      void apiFetch("/api/tenant/account/preferences", {
        method: "PATCH",
        body: { preferred_ai_model_id: modelId }
      }).catch(() => {});
    }, 600);
  }, []);

  function onTenantAiModelChange(modelId: string) {
    setSelectedAiModelId(modelId);
    scheduleSaveAiModelPreference(modelId);
  }

  // ── Node output cache (from hub operation_node_outputs) ────────────────────
  const [nodeOutputs, setNodeOutputs] = useState<Record<string, NodeOutputRecord>>({});
  // ADR-0146: per-node schema-lock state, keyed by node id. `frozen` false means
  // locked but still waiting on a first successful run to capture the shape.
  const [nodeSchemaLocks, setNodeSchemaLocks] = useState<
    Record<string, { locked_at: string; frozen: boolean }>
  >({});
  // ADR-0216 §A7.13: step id → the Atlas Reports keeping it each run (their names).
  const [reportKeptSteps, setReportKeptSteps] = useState<Record<string, string[]>>({});
  const [schemaLockBusy, setSchemaLockBusy] = useState<Record<string, boolean>>({});
  // ADR-0147: in-flight flag for the Domain override → retype gesture.
  const [columnRetypeBusy, setColumnRetypeBusy] = useState<Record<string, boolean>>({});
  const [transformForceRefresh, setTransformForceRefresh] = useState(false);

  // Per-node cache data (lazy-loaded when Cache/Transform tabs are opened)
  // key = nodeId, value = extracted QueryResultsPayload (or null if no cache)
  const [nodeCache, setNodeCache] = useState<Record<string, QueryResultsPayload | null>>({});
  const [nodeCacheLoading, setNodeCacheLoading] = useState<Record<string, boolean>>({});
  const [nodeCacheRefreshBusy, setNodeCacheRefreshBusy] = useState(false);

  // ── Node run history (append-only log from operation_node_runs) ───────────
  const [nodeRunHistory, setNodeRunHistory] = useState<Record<string, NodeRunHistoryEntry[]>>({});
  const [nodeRunHistoryLoading, setNodeRunHistoryLoading] = useState<Record<string, boolean>>({});
  const [nodeExecutionEntries, setNodeExecutionEntries] = useState<Record<string, ExecutionLogEntry[]>>({});
  const [selectedNodeRun, setSelectedNodeRun] = useState<NodeRunHistoryEntry | null>(null);
  // ADR-0116 — tenant models, for resolving a model_id to its name in AI-config diffs (never a UUID).
  const [tenantModelsForNames, setTenantModelsForNames] = useState<Array<{ id: string; name: string }>>([]);
  const saveRef = useRef<(statusOverride?: OperationStatus, options?: SaveOptions) => Promise<boolean>>(async () => false);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  // ADR-0129 — set by an Architect delegate so the node-select effect lands on
  // the target model node's Advisor (AI) slot once selection settles.
  const openAdvisorOnSelectRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  const busyRef = useRef(false);
  const saveVersionRef = useRef(0);
  const reactFlowViewportRef = useRef<HTMLDivElement>(null);
  const reactFlowInstanceRef = useRef<ReactFlowInstance<CanvasNode, CanvasEdge> | null>(null);
  // ADR-0128 follow-up — model_train node ids whose training we've observed
  // active this session; used to stamp a terminal badge only on the active→done
  // transition (not on a cold mount of an already-finished training).
  const seenActiveTrainRef = useRef<Set<string>>(new Set());
  // Maps recommendation fingerprint → (stageIndex → canvasNodeId) for architect single-stage adds
  const stageNodeMapRef = useRef<Map<string, Map<number, string>>>(new Map());

  const [nodes, setNodes, onNodesChangeBase] = useNodesState<CanvasNode>([]);
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState<CanvasEdge>([]);

  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  // ADR-0153 Phase 5 — ONE path-driven resolution from findings to nodes,
  // consumed by the inspector shell's banner, the canvas badge sweep, and the
  // operation-level roll-up. No inspector receives findings props.
  // ── Loop rendering (derived, never stored) ────────────────────────────────
  //
  // Membership comes from `lib/loopTopology.ts`, which a parity gate holds to
  // the hub's owning implementation. Nothing here re-derives it.
  const loopRender = useMemo(() => {
    const graph = {
      nodes: nodes.map((n) => ({ id: n.id, type: n.data.nodeType, config: n.data.config })),
      edges: edges.map((e) => ({
        edge_type: e.data?.edge_type ?? "data",
        from_node: e.source,
        to_node: e.target,
      })),
    };
    const pairs = resolveLoopPairs(graph);
    const owner = nodeToLoopPair(graph);
    const labelById = new Map(nodes.map((n) => [n.id, n.data.label]));

    const accentByNodeId = new Map<string, string>();
    const nameByNodeId = new Map<string, string>();
    pairs.forEach((pair, i) => {
      const accent = loopPairAccent(i);
      const startLabel = labelById.get(pair.startId) ?? null;
      accentByNodeId.set(pair.startId, accent);
      accentByNodeId.set(pair.endId, accent);
      nameByNodeId.set(pair.startId, loopPairName(startLabel, "start"));
      nameByNodeId.set(pair.endId, loopPairName(startLabel, "end"));
    });
    for (const [nodeId, membership] of owner) {
      accentByNodeId.set(nodeId, loopPairAccent(membership.pairIndex));
    }
    return { pairs, owner, accentByNodeId, nameByNodeId };
  }, [nodes, edges]);

  /**
   * Nodes with loop membership stamped on — render-time only.
   *
   * REFERENTIAL STABILITY IS LOAD-BEARING HERE, not a nicety. Handing xyflow a
   * freshly-built node object on every render makes it re-measure, which
   * writes dimensions back through `onNodesChange`, which changes `nodes`,
   * which rebuilds the objects — an unbounded render loop that manifests as
   * "Maximum update depth exceeded" and, because the canvas never settles, as
   * NO EDGES DRAWN AT ALL. So a node keeps its previous derived object
   * whenever the source node and its three derived values are unchanged.
   */
  const derivedNodeCacheRef = useRef(
    new Map<string, { src: CanvasNode; accent: string | null; pairName: string | null; isBody: boolean; out: CanvasNode }>()
  );
  const renderNodes = useMemo(() => {
    const cache = derivedNodeCacheRef.current;
    const seen = new Set<string>();
    const out = nodes.map((n) => {
      seen.add(n.id);
      const accent = loopRender.accentByNodeId.get(n.id) ?? null;
      const pairName = loopRender.nameByNodeId.get(n.id) ?? null;
      const isBody = loopRender.owner.has(n.id);
      if (!accent && !pairName && !isBody) return n;
      const prev = cache.get(n.id);
      if (prev && prev.src === n && prev.accent === accent && prev.pairName === pairName && prev.isBody === isBody) {
        return prev.out;
      }
      const built: CanvasNode = {
        ...n,
        data: { ...n.data, loopPairAccent: accent, loopPairName: pairName, loopBodyMember: isBody },
      };
      cache.set(n.id, { src: n, accent, pairName, isBody, out: built });
      return built;
    });
    for (const id of [...cache.keys()]) if (!seen.has(id)) cache.delete(id);
    return out;
  }, [nodes, loopRender]);

  /**
   * The decorative dashed link between a loop's bookends.
   *
   * HARD CONSTRAINT: constructed HERE, at render time, and appended AFTER the
   * spec edges. It is not a graph edge. It must never reach `edges` state,
   * because that state is what the spec is built from — so it would otherwise
   * flow into the validator, the saved spec, lineage and the change grammar as
   * a connection the user never made and the hub would refuse.
   */
  const decorativeEdgeCacheRef = useRef(new Map<string, CanvasEdge>());
  const renderEdges = useMemo(() => {
    if (loopRender.pairs.length === 0) return edges;
    const decorative = loopRender.pairs.map((pair, i) => ({
      id: loopPairLinkId(pair),
      source: pair.startId,
      target: pair.endId,
      sourceHandle: "item_out",
      targetHandle: "result_in",
      className: "opLoopPairLink",
      selectable: false,
      focusable: false,
      deletable: false,
      style: { ["--op-loop-pair-accent" as string]: loopPairAccent(i) },
    })) as unknown as CanvasEdge[];
    // Same stability contract as the nodes above: reuse the previous object
    // for an unchanged pair so the canvas is not handed a new edge identity
    // every render.
    const cache = decorativeEdgeCacheRef.current;
    const stable = decorative.map((e) => {
      const prev = cache.get(e.id);
      if (prev && JSON.stringify(prev) === JSON.stringify(e)) return prev;
      cache.set(e.id, e);
      return e;
    });
    for (const id of [...cache.keys()]) if (!stable.some((e) => e.id === id)) cache.delete(id);
    return [...edges, ...stable];
  }, [edges, loopRender]);

  const groupedFindings = useMemo(
    () =>
      groupFindings(
        issues,
        nodes.map((n) => ({ id: n.id, nodeType: n.data.nodeType as string, label: n.data.label }))
      ),
    [issues, nodes]
  );
  const [graphSummary, setGraphSummary] = useState<GraphSummary | null>(null);
  const [validationRan, setValidationRan] = useState(false);

  const [savedSnapshot, setSavedSnapshot] = useState("");

  // ── ADR-0159: the version this tab is editing, and what happens when it moves ──
  //
  // The token lives in a ref as well as in `operationMeta` because every save
  // path is async: reading it out of render state can hand a save the value
  // from before the save that just landed, which the hub would then refuse as
  // stale — a conflict this tab caused itself.
  const operationTokenRef = useRef<string | null>(null);
  const [conflict, setConflict] = useState<WriteConflict | null>(null);
  const conflictRef = useRef<WriteConflict | null>(null);
  conflictRef.current = conflict;
  const conflictBannerRef = useRef<HTMLDivElement | null>(null);

  const rememberToken = useCallback((updatedAt: string | null | undefined) => {
    if (!updatedAt) return;
    operationTokenRef.current = updatedAt;
    setOperationMeta((prev) => ({ ...prev, updatedAt }));
  }, []);

  /**
   * ADR-0159 §6 — the escape hatch. Reloading is the exit from a conflict and
   * it costs whatever this tab has not saved; for the rare case where that is
   * substantial, the local version leaves on the clipboard. Cheap, honest, and
   * it adds no second place where a pipeline can be stored.
   */
  const copyLocalSpec = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(localSpecRef.current, null, 2));
      toast.push({ kind: "success", message: "Your version is on the clipboard." });
    } catch {
      toast.push({ kind: "error", message: "Couldn't reach the clipboard — your browser blocked it." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ADR-0159 §4.2 — the ONE way this editor writes a spec. Every spec-carrying
   * PATCH goes through here so the version token is attached in exactly one
   * place and a refusal is recognised in exactly one place. A site that built
   * its own PATCH would be a site that could still clobber.
   */
  const patchOperationSpec = useCallback(
    async <T,>(body: Record<string, unknown>): Promise<T> => {
      try {
        return await apiFetch<T>(`/api/tenant/operations/${encodeURIComponent(operationId)}`, {
          method: "PATCH",
          body: { ...body, expected_updated_at: operationTokenRef.current },
        });
      } catch (e) {
        const moved = readWriteConflict(e);
        if (moved) setConflict(moved);
        throw e;
      }
    },
    [operationId]
  );

  const defaultLocalConnectorId = useMemo(() => connectors[0]?.id ?? "", [connectors]);

  const nodeTypes = useMemo(() => ({ operationNode: OperationCanvasNode }), []);

  const selectedNode = useMemo(() => nodes.find((node) => node.selected), [nodes]);
  const selectedEdge = useMemo(() => edges.find((edge) => edge.selected), [edges]);

  const selectedNodeId = selectedNode?.id ?? null;
  // Latest selected node id in a ref so async run-completion callbacks can tell
  // whether the run's node is still the one on screen (2.2c-async choreography gate).
  const selectedNodeIdRef = useRef<string | null>(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;

  // ADR-0116 — models this operation's Model Train nodes have produced (trained),
  // for one-click binding of predict/evaluate nodes. Empty until a model is trained.
  const pipelineBindableModels = useMemo(() => {
    const ids = new Set<string>();
    for (const n of nodes) {
      if (n.data.nodeType !== "model_train") continue;
      const mid = (n.data.config?.spec_model_train as Record<string, unknown> | undefined)?.model_id;
      if (typeof mid === "string" && mid.trim()) ids.add(mid.trim());
    }
    return [...ids]
      .map((id) => ({ id, name: tenantModelsForNames.find((m) => m.id === id)?.name ?? null }))
      .filter((m): m is { id: string; name: string } => m.name !== null);
  }, [nodes, tenantModelsForNames]);
  // ADR-0128 follow-up — model_train nodes with a bound model, for the
  // editor-level training pulse below. Keyed on a stable (nodeId→modelId)
  // signature so the poll effect doesn't restart on unrelated canvas churn
  // (setRunStatus mutates runStatus, not this signature).
  const modelTrainNodes = useMemo(
    () =>
      nodes
        .filter((n) => n.data.nodeType === "model_train")
        .map((n) => ({
          nodeId: n.id,
          modelId: ((n.data.config?.spec_model_train as Record<string, unknown> | undefined)?.model_id ?? "") as string,
        }))
        .filter((x) => typeof x.modelId === "string" && x.modelId.trim().length > 0),
    [nodes]
  );
  const modelTrainNodesKey = useMemo(
    () => modelTrainNodes.map((x) => `${x.nodeId}:${x.modelId}`).sort().join("|"),
    [modelTrainNodes]
  );

  const transformPrompt = selectedNodeId ? (transformDrafts[selectedNodeId] ?? "") : "";
  const setTransformPrompt = useCallback<Dispatch<SetStateAction<string>>>((value) => {
    setTransformDrafts((prev) => {
      if (!selectedNodeId) return prev;
      const current = prev[selectedNodeId] ?? "";
      const next = typeof value === "function" ? (value as (p: string) => string)(current) : value;
      if (next === current) return prev;
      return { ...prev, [selectedNodeId]: next };
    });
  }, [selectedNodeId]);

  // ADR-0074: register feedback context so the popover's chip row reflects the
  // pipeline / selected node the user is looking at.
  useEffect(() => {
    if (!operationId) return;
    if (selectedNode) {
      const label = (selectedNode.data.label ?? "").trim() || NODE_LABELS[selectedNode.data.nodeType] || null;
      setFeedbackContext({ kind: "node", id: selectedNode.id, displayName: label });
      return () => clearFeedbackContext({ kind: "node", id: selectedNode.id });
    }
    setFeedbackContext({ kind: "pipeline", id: operationId, displayName: name || null });
    return () => clearFeedbackContext({ kind: "pipeline", id: operationId });
  }, [operationId, name, selectedNode]);

  const aiProviderContextInputs = useMemo(() => {
    if (!selectedNode || selectedNode.data.nodeType !== "ai_provider") {
      return { dataIn: false, contextIn: false };
    }
    return aiProviderContextInputStatus({ edges, aiNodeId: selectedNode.id });
  }, [selectedNode, edges]);

  const anyAiProviderPaletteBusy = useMemo(
    () =>
      aiProviderAuthoringBusyIds.size > 0 ||
      aiProviderExecutingIds.size > 0 ||
      nodes.some((n) => n.data.nodeType === "ai_provider" && n.data.runStatus === "running"),
    [aiProviderAuthoringBusyIds, aiProviderExecutingIds, nodes]
  );

  const selectedProviderRef = useMemo(() => {
    if (!selectedNode) return null;
    // ADR-0085 Phase 2: plex_api uses the same provider-ref shape as provider.
    if (selectedNode.data.nodeType !== "provider" && selectedNode.data.nodeType !== "plex_api") return null;
    const providerRef = selectedNode.data.config.provider;
    if (!providerRef || typeof providerRef !== "object" || Array.isArray(providerRef)) return null;
    if ((providerRef as Record<string, unknown>).type === "shared") {
      return { type: "shared" as const, provider_id: String((providerRef as Record<string, unknown>).provider_id ?? "") };
    }
    if ((providerRef as Record<string, unknown>).type === "local") {
      return { type: "local" as const, local_provider_id: String((providerRef as Record<string, unknown>).local_provider_id ?? "") };
    }
    return null;
  }, [selectedNode]);

  const selectedProviderDraft = useMemo(() => {
    if (!selectedProviderRef) return null;
    if (selectedProviderRef.type === "local") {
      return localProviders[selectedProviderRef.local_provider_id] ?? null;
    }
    return sharedProviderCache[selectedProviderRef.provider_id] ?? null;
  }, [selectedProviderRef, localProviders, sharedProviderCache]);

  const selectedProviderKey = useMemo(() => {
    if (!selectedProviderRef) return null;
    return selectedProviderRef.type === "local"
      ? `local:${selectedProviderRef.local_provider_id}`
      : `shared:${selectedProviderRef.provider_id}`;
  }, [selectedProviderRef]);

  const selectedProviderPreview = useMemo(() => {
    if (!selectedProviderKey) return null;
    return providerPreviewByKey[selectedProviderKey] ?? null;
  }, [selectedProviderKey, providerPreviewByKey]);

  /** Preview-map key for an arbitrary node (mirror of selectedProviderKey). */
  function providerKeyForNode(nodeId: string): string | null {
    const nd = nodes.find((n) => n.id === nodeId);
    const cfg = nd?.data?.config;
    const ref = cfg && typeof cfg === "object" && !Array.isArray(cfg)
      ? (cfg as Record<string, unknown>).provider
      : null;
    if (!ref || typeof ref !== "object" || Array.isArray(ref)) return null;
    const r = ref as Record<string, unknown>;
    if (r.type === "local" && r.local_provider_id) return `local:${String(r.local_provider_id)}`;
    if (r.type === "shared" && r.provider_id) return `shared:${String(r.provider_id)}`;
    return null;
  }

  /**
   * ADR-0128 — apply a run's per-node rows to the canvas badges + finished-at
   * tooltips. Nodes not in the run keep their existing badge (last-outcome).
   * Also evicts `nodeCache` entries for nodes that (re)landed output so the
   * next `loadNodeCache` re-fetches — kills the stale-cache class.
   *
   * The badge decision itself lives in `nodeBadgeForRun` (pure, unit tested) so
   * the canvas and run history cannot drift apart: the target pulses for the
   * whole run, a terminal run never leaves a live badge behind, and a persist
   * target skipped by design reports the LOAD's outcome rather than reading its
   * always-`skipped` chain row as an upstream failure. The run-detail / Trace
   * table still shows true per-node states.
   */
  function applyRunNodesToCanvas(run: OperationRun, nodes: readonly OperationRunNode[]) {
    setNodes((prev) => {
      const byId = new Map(nodes.map((n) => [n.node_id, n]));
      return prev.map((n) => {
        const rn = byId.get(n.id);
        if (!rn) return n;
        const badge: NodeBadgeStatus = nodeBadgeForRun(run, rn);
        return {
          ...n,
          data: {
            ...n.data,
            runStatus: badge,
            runFinishedAt: rn.finished_at ?? n.data.runFinishedAt ?? null,
            cacheProducedAt: rn.cache_produced_at ?? null,
          },
        };
      });
    });
    // Any node that just re-landed output has a stale memoized nodeCache entry.
    setNodeCache((prev) => {
      const next = { ...prev };
      for (const n of nodes) {
        if (n.status === "succeeded" || n.status === "failed") delete next[n.node_id];
      }
      return next;
    });
  }

  /** Record the latest run detail (run + nodes) for the run-detail surface. */
  function ingestRunDetail(run: OperationRun, nodes: OperationRunNode[]) {
    setRunDetailByRunId((prev) => ({ ...prev, [run.id]: { run, nodes } }));
    applyRunNodesToCanvas(run, nodes);
    const targets = [run.target_node_id, ...(run.additional_target_node_ids ?? [])];
    setActiveRunIdByNode((prev) => {
      const next = { ...prev };
      if (isActiveRunStatus(run.status)) {
        for (const t of targets) next[t] = run.id;
      } else {
        for (const t of targets) if (next[t] === run.id) delete next[t];
      }
      return next;
    });
  }

  // ADR-0128 — mount/open reconciliation. Fetch active runs once, reconcile
  // canvas badges, and hydrate run-detail state so a run started in another
  // tab (or before a remount) is reflected. Then, while any run is active,
  // poll the active-runs list every 1s until idle.
  useEffect(() => {
    if (!operationId) return;
    let disposed = false;
    let timer: number | null = null;
    // Bumped whenever something (the visibility kick below) starts a fresh
    // poll chain. A tick whose generation is stale does not reschedule, so two
    // chains can never run at once.
    let generation = 0;
    // Runs we have forced a `running` pulse for. A run that FINISHES between
    // ticks silently drops out of the ?active=1 list — without a final detail
    // fetch its terminal node snapshot is never ingested and the forced pulse
    // on its target sticks forever (the stuck-pulse bug for runs not driven by
    // this editor's runController, e.g. execute-persist).
    //
    // Defect, 2026-09-13: a node still pulsed `running` 49 minutes after its
    // run record said `failed`. An id used to be dropped from this set BEFORE
    // the final detail fetch, so a single failed fetch — the same connectivity
    // blip that killed the run's own request — retired the run from
    // reconciliation permanently and the pulse never cleared. Ids now leave
    // only once a terminal snapshot has actually been ingested.
    const pendingFinalRunIds = new Set<string>();

    /**
     * Fetch one run's detail and ingest it. Returns true once the run needs no
     * further chasing — it landed, or it is gone for good.
     */
    async function ingestRun(id: string): Promise<boolean> {
      try {
        const detail = await apiFetch<{ run: OperationRun; nodes: OperationRunNode[] }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/runs/${encodeURIComponent(id)}`
        );
        if (disposed) return false;
        ingestRunDetail(detail.run, detail.nodes);
        return true;
      } catch (e) {
        // A run deleted by retention is gone, not pending — stop chasing it.
        // Anything else is transient and gets retried on the next tick.
        return e instanceof ApiRequestError && e.status === 404;
      }
    }

    async function poll(myGeneration: number): Promise<void> {
      const reschedule = (ms: number) => {
        if (disposed || myGeneration !== generation) return;
        timer = window.setTimeout(() => void poll(myGeneration), ms);
      };

      let runs: OperationRun[] = [];
      try {
        const data = await apiFetch<{ runs: OperationRun[] }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/runs?active=1&limit=20`
        );
        runs = data.runs ?? [];
      } catch {
        reschedule(3000);
        return;
      }
      if (disposed) return;
      // Runs that left the active list finished — fetch each again so the
      // terminal run + node statuses land on the canvas (clears the forced
      // target pulse, applies success/error/skipped badges). Keep retrying
      // until one actually lands; a swallowed failure here is a stuck pulse.
      const activeIds = new Set(runs.map((r) => r.id));
      for (const id of [...pendingFinalRunIds].filter((id) => !activeIds.has(id))) {
        if (await ingestRun(id)) pendingFinalRunIds.delete(id);
        if (disposed) return;
      }
      // Pull full detail for each active run so per-node badges reconcile.
      for (const r of runs) {
        pendingFinalRunIds.add(r.id);
        await ingestRun(r.id);
        if (disposed) return;
      }
      // ADR-0130: reconcile continuously, not only while a run the editor
      // STARTED is active. A training (or a run started in another tab) is
      // created outside the editor's `runController`, so without a steady
      // heartbeat the canvas never learns its node states — only the fast
      // (1s) cadence while active, a slow (4s) heartbeat while idle so a
      // newly-started run's ancestors light up within a few seconds. A run
      // still owed a terminal snapshot keeps the fast cadence.
      reschedule(runs.length > 0 || pendingFinalRunIds.size > 0 ? 1000 : 4000);
    }

    // A backgrounded tab has its timers throttled (and, when the browser
    // freezes the page, stopped outright), so a run that ends while the user is
    // elsewhere can leave the canvas showing a pulse for as long as the tab
    // stays hidden. Reconcile the moment it comes back rather than waiting out
    // whatever cadence the browser allowed.
    const onVisible = () => {
      if (disposed || document.visibilityState !== "visible") return;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      generation += 1;
      void poll(generation);
    };
    document.addEventListener("visibilitychange", onVisible);

    void poll(generation);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (timer !== null) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId]);

  // ADR-0128 follow-up — editor-level model-training pulse. A training is NOT an
  // operation-run (deferred), so it never flows through the run poll above and
  // the model_train node otherwise never pulses. Poll each bound model's latest
  // training status directly and reflect an active training onto the canvas
  // badge — so it pulses like any other run AND keeps pulsing across node
  // navigation (the inspector-scoped poll can't). Terminal badges are stamped
  // only for trainings we observed active this session (a cold mount with an
  // already-finished training stays idle, matching the run-poll's active-only
  // reconciliation).
  useEffect(() => {
    if (!operationId || modelTrainNodes.length === 0) return;
    let disposed = false;
    let timer: number | null = null;
    const seenActive = seenActiveTrainRef.current;

    async function tick(): Promise<void> {
      let anyActive = false;
      for (const { nodeId, modelId } of modelTrainNodes) {
        try {
          const data = await apiFetch<{
            trainings?: Array<{ status: string; completed_at: string | null }>;
          }>(`/api/tenant/models/${encodeURIComponent(modelId)}`);
          if (disposed) return;
          const latest = data.trainings?.[0];
          if (!latest) continue;
          if (latest.status === "queued" || latest.status === "running") {
            anyActive = true;
            seenActive.add(nodeId);
            setRunStatus([nodeId], "running");
          } else if (seenActive.has(nodeId)) {
            // active → terminal transition we witnessed: stamp the outcome.
            seenActive.delete(nodeId);
            setNodes((prev) =>
              prev.map((n) =>
                n.id === nodeId
                  ? {
                      ...n,
                      data: {
                        ...n.data,
                        runStatus: latest.status === "success" ? "success" : "error",
                        runFinishedAt: latest.completed_at ?? n.data.runFinishedAt ?? null,
                      },
                    }
                  : n
              )
            );
          }
        } catch {
          /* best-effort per-model */
        }
      }
      if (disposed) return;
      // Fast cadence while a training is active; slow heartbeat otherwise to
      // catch a training kicked off from the node inspector.
      timer = window.setTimeout(() => void tick(), anyActive ? 2000 : 8000);
    }

    void tick();
    return () => {
      disposed = true;
      if (timer !== null) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId, modelTrainNodesKey]);

  /**
   * ADR-0128 — kick off (or attach to) an async run for a node and drive its
   * lifecycle through the shared hook. Badges/detail update from every poll
   * tick; `onSucceeded` runs the caller's cache-refresh choreography.
   */
  async function runNodeAsync(
    nodeId: string,
    opts: {
      forceRefresh?: boolean;
      onSucceeded?: (run: OperationRun, nodes: OperationRunNode[]) => void | Promise<void>;
      onFailed?: (error: string | null) => void;
      onCancelled?: () => void;
      onFailure?: (err: unknown) => void;
    }
  ): Promise<void> {
    await runController.startRun({
      operationId: operationId!,
      nodeId,
      forceRefresh: opts.forceRefresh,
      onProgress: (run, nodes) => ingestRunDetail(run, nodes),
      onComplete: async (run, nodes) => {
        ingestRunDetail(run, nodes);
        void loadNodeOutputs();
        for (const n of nodes) void loadNodeRunHistory(n.node_id);
        await opts.onSucceeded?.(run, nodes);
      },
      onError: (error) => {
        // onProgress already ingested the terminal run+nodes this tick.
        void loadNodeOutputs();
        void loadNodeRunHistory(nodeId);
        setRunStatus([nodeId], "error");
        opts.onFailed?.(error);
      },
      onCancelled: () => {
        setRunStatus([nodeId], "idle");
        opts.onCancelled?.();
      },
      onFailure: (err) => opts.onFailure?.(err),
    });
  }

  async function stopSelectedNodeExecution(): Promise<void> {
    if (!operationId || !selectedNode) return;
    const runId = activeRunIdByNode[selectedNode.id];
    if (!runId) return;
    try {
      await runController.cancelRun(operationId, runId);
      toast.push({ kind: "success", message: "Stop requested — cancelling the run." });
    } catch (e) {
      // 409 run_terminal — the run already finished; nothing to cancel.
      if (e instanceof ApiRequestError && e.status === 409 && e.code === "run_terminal") {
        return;
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }
  function nodeRunToDetail(run: NodeRunHistoryEntry): RunDetailEntry {
    const details = (run.details ?? {}) as Record<string, unknown>;
    const rawParams = details.resolved_params;
    const resolvedParams = rawParams && typeof rawParams === "object" && !Array.isArray(rawParams)
      ? (rawParams as Record<string, string | number | boolean | null>)
      : {};

    // ADR-0085 Phase 2: Plex captures the full request_body sent to the API.
    // Atlas does not wrap or transform — what's logged is exactly what got
    // POSTed. Pre-call failures (missing integration, etc.) log `null`.
    let apiCall: RunDetailEntry["api_call"] = null;
    if (run.node_type === "plex_api") {
      const datasourceId = typeof details.datasource_id === "number" ? details.datasource_id : null;
      apiCall = {
        kind: "plex_datasource_execute",
        summary: datasourceId !== null ? `Plex datasource ${datasourceId}` : "Plex datasource execute",
        body: details.request_body ?? null
      };
    }

    return {
      id: run.id,
      run_at: run.started_at,
      node_type: run.node_type,
      duration_ms: run.duration_ms,
      status: run.status,
      row_count: run.row_count,
      error: run.error,
      resolved_sql: typeof details.resolved_sql === "string" ? details.resolved_sql : "",
      resolved_params: resolvedParams,
      api_call: apiCall
    };
  }
  const inspectorTitle = useMemo(() => {
    if (selectedNode) {
      const nodeTypeLabel = NODE_LABELS[selectedNode.data.nodeType];
      const nodeName = selectedNode.data.label?.trim() || nodeTypeLabel;
      return `${nodeName} (${nodeTypeLabel})`;
    }
    if (selectedEdge) return "Edge";
    const operationName = name.trim();
    return operationName ? `${operationName} (Operation)` : "Operation";
  }, [selectedNode, selectedEdge, name]);

  // ADR-0129 — header node navigation (switch nodes while the flyout covers the
  // canvas). Labels never expose a UUID.
  const navNodes = useMemo<NodeNavItem[]>(
    () =>
      nodes.map((n) => ({
        id: n.id,
        label: n.data.label?.trim() || NODE_LABELS[n.data.nodeType],
        typeLabel: NODE_LABELS[n.data.nodeType],
      })),
    [nodes],
  );

  useEffect(() => {
    measureInspectorFlyoutTop();
    window.addEventListener("resize", measureInspectorFlyoutTop);
    window.addEventListener("scroll", measureInspectorFlyoutTop, true);
    // Observe the whole document so layout shifts ABOVE the canvas (banners,
    // toolbars, async content) that move the canvas down trigger a re-measure —
    // a section-only ResizeObserver wouldn't fire when only its position moves.
    const ro = new ResizeObserver(measureInspectorFlyoutTop);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", measureInspectorFlyoutTop);
      window.removeEventListener("scroll", measureInspectorFlyoutTop, true);
      ro.disconnect();
    };
  }, [measureInspectorFlyoutTop]);

  // ADR-0128 — run-node rows arrive in arbitrary order (single-transaction
  // inserts share one created_at). Order them the way the data flows: ancestors
  // first, target last (post-order DFS over data edges from the run's targets).
  // ADR-0128 — order run nodes the way data flows (ancestors first, target
  // last) so the Trace reads provider → transform → … in execution order.
  const orderRunNodesByFlow = useCallback(
    (targetIds: string[], runNodes: OperationRunNode[]): OperationRunNode[] => {
      const reverseAdj = new Map<string, string[]>();
      for (const edge of edges) {
        if (edge.data?.edge_type !== "data") continue;
        const list = reverseAdj.get(edge.target) ?? [];
        list.push(edge.source);
        reverseAdj.set(edge.target, list);
      }
      const order = new Map<string, number>();
      const seen = new Set<string>();
      let nextIndex = 0;
      const visit = (nid: string): void => {
        if (seen.has(nid)) return;
        seen.add(nid); // cycle guard
        for (const up of reverseAdj.get(nid) ?? []) visit(up);
        order.set(nid, nextIndex++);
      };
      for (const t of targetIds) visit(t);
      return [...runNodes].sort(
        (a, b) => (order.get(a.node_id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.node_id) ?? Number.MAX_SAFE_INTEGER)
      );
    },
    [edges]
  );

  // ADR-0128 — Trace modal: the full chain of a specific past run, opened from a
  // History row. Fetched fresh, so any run in the retention window works.
  const [traceModal, setTraceModal] = useState<{ run: OperationRun; nodes: OperationRunNode[] } | null>(null);
  const [traceModalLoading, setTraceModalLoading] = useState(false);
  // ADR-0130: when a Trace is opened from a node's History, scope it to that
  // node's upstream lineage (null = run-level, full chain).
  const [traceFocusNodeId, setTraceFocusNodeId] = useState<string | null>(null);
  const openTrace = useCallback(
    async (runId: string, focusNodeId?: string): Promise<void> => {
      if (!operationId) return;
      setTraceFocusNodeId(focusNodeId ?? null);
      setTraceModalLoading(true);
      try {
        const detail = await apiFetch<{ run: OperationRun; nodes: OperationRunNode[] }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/runs/${encodeURIComponent(runId)}`
        );
        setTraceModal(detail);
      } catch (e) {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      } finally {
        setTraceModalLoading(false);
      }
    },
    [operationId, toast]
  );

  function openNewWindowModal() {
    setPendingWindowBinding(null);
    setEditingWindowIndex(null);
    const nextWindow: WindowDef = {
      name: `window_${operationWindows.length + 1}`,
      type: "relative",
      start_offset: { value: -6, unit: "month" },
      end_offset: { value: 0, unit: "day" }
    };
    setWindowDraft(nextWindow);
    setWindowStartOffsetText(String(nextWindow.start_offset.value));
    setWindowEndOffsetText(String(nextWindow.end_offset.value));
    setWindowsModalOpen(true);
  }

  function openEditWindowModal(index: number) {
    const target = operationWindows[index];
    if (!target) return;
    setEditingWindowIndex(index);
    setWindowDraft(target);
    if (target.type === "relative") {
      setWindowStartOffsetText(String(target.start_offset.value));
      setWindowEndOffsetText(String(target.end_offset.value));
    }
    setWindowsModalOpen(true);
  }

  async function saveWindowModal() {
    const savedName = windowDraft.name;
    if (editingWindowIndex === null) {
      setOperationWindows((prev) => [...prev, windowDraft]);
    } else {
      setOperationWindows((prev) => {
        const next = [...prev];
        next[editingWindowIndex] = windowDraft;
        return next;
      });
    }
    if (pendingWindowBinding) {
      const target = pendingWindowBinding;
      setNodeData(target.nodeId, (data) => {
        const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
        nextBindings[target.paramName] = { type: "window", window: savedName, field: target.field };
        return { ...data, config: { ...data.config, bindings: nextBindings } };
      });
      setPendingWindowBinding(null);
    }
    const ok = await flushAndSave();
    if (ok) setWindowsModalOpen(false);
  }

  async function deleteWindowModal() {
    if (editingWindowIndex === null) return;
    const previousWindows = [...operationWindows];
    const previousPending = pendingWindowBinding;
    setOperationWindows((prev) => {
      const next = [...prev];
      next.splice(editingWindowIndex, 1);
      return next;
    });
    setPendingWindowBinding(null);
    const ok = await flushAndSave();
    if (ok) {
      setWindowsModalOpen(false);
      return;
    }
    setOperationWindows(previousWindows);
    setPendingWindowBinding(previousPending);
  }

  function openNewVariableModal() {
    setPendingVariableBinding(null);
    setEditingVariableIndex(null);
    setVariableDraft({
      name: `var_${operationVariables.length + 1}`,
      type: "string",
      value: "",
      description: ""
    });
    setVariablesModalOpen(true);
  }

  function openEditVariableModal(index: number) {
    const target = operationVariables[index];
    if (!target) return;
    setEditingVariableIndex(index);
    setVariableDraft(target);
    setVariablesModalOpen(true);
  }

  async function saveVariableModal() {
    const savedName = variableDraft.name.trim();
    if (!savedName) {
      toast.push({ kind: "error", message: "Variable name is required." });
      return;
    }
    if (!IDENTIFIER_RE.test(savedName)) {
      toast.push({ kind: "error", message: "Variable name must use letters, numbers, and underscores only (start with letter or underscore)." });
      return;
    }
    const duplicateVariableName = operationVariables.some(
      (entry, idx) => idx !== (editingVariableIndex ?? -1) && entry.name.toLowerCase() === savedName.toLowerCase()
    );
    if (duplicateVariableName) {
      toast.push({ kind: "error", message: "Variable name must be unique." });
      return;
    }
    const nextVariableDraft: OperationVariableDef = { ...variableDraft, name: savedName };
    if (editingVariableIndex === null) {
      setOperationVariables((prev) => [...prev, nextVariableDraft]);
    } else {
      setOperationVariables((prev) => {
        const next = [...prev];
        next[editingVariableIndex] = nextVariableDraft;
        return next;
      });
    }
    if (pendingVariableBinding) {
      const target = pendingVariableBinding;
      setNodeData(target.nodeId, (data) => {
        const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
        nextBindings[target.paramName] = { type: "variable", variable: savedName };
        return { ...data, config: { ...data.config, bindings: nextBindings } };
      });
      setPendingVariableBinding(null);
    }
    const ok = await flushAndSave();
    if (ok) setVariablesModalOpen(false);
  }

  async function deleteVariableModal() {
    if (editingVariableIndex === null) return;
    const previousVariables = [...operationVariables];
    const previousPending = pendingVariableBinding;
    setOperationVariables((prev) => {
      const next = [...prev];
      next.splice(editingVariableIndex, 1);
      return next;
    });
    setPendingVariableBinding(null);
    const ok = await flushAndSave();
    if (ok) {
      setVariablesModalOpen(false);
      return;
    }
    setOperationVariables(previousVariables);
    setPendingVariableBinding(previousPending);
  }

  async function deleteWindowFromList(index: number) {
    const target = operationWindows[index];
    if (!target) return;
    const ok = await confirm.confirm({
      title: "Delete window?",
      detail: `Delete "${target.name}" from this operation?`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    const previousWindows = [...operationWindows];
    setOperationWindows((prev) => prev.filter((_, idx) => idx !== index));
    const saved = await flushAndSave();
    if (!saved) setOperationWindows(previousWindows);
  }

  async function deleteVariableFromList(index: number) {
    const target = operationVariables[index];
    if (!target) return;
    const ok = await confirm.confirm({
      title: "Delete variable?",
      detail: `Delete "${target.name}" from this operation?`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    const previousVariables = [...operationVariables];
    setOperationVariables((prev) => prev.filter((_, idx) => idx !== index));
    const saved = await flushAndSave();
    if (!saved) setOperationVariables(previousVariables);
  }

  function closeWindowModal() {
    setPendingWindowBinding(null);
    setWindowsModalOpen(false);
  }

  function closeVariableModal() {
    setPendingVariableBinding(null);
    setVariablesModalOpen(false);
  }

  function closeParameterModal() {
    setParameterModalOpen(false);
  }

  const flushAndSave = useCallback(async (): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (!dirtyRef.current) return true;
    return saveRef.current(undefined, { silent: true });
  }, []);

  // ── Undo stack (local, in-memory) ─────────────────────────────────────────
  // Snapshot is taken before each structural mutation (add / remove / connect /
  // duplicate / quickcreate). Drag noise and metadata edits are intentionally
  // excluded. PDO-backed persist deletion clears the stack — the data is
  // hard-deleted server-side at confirm time and can't be restored by
  // replaying the client spec. Local provider drafts ride the snapshot so an
  // undone node removal gets its GC'd inline definition back (merge-restore
  // below — current drafts win, so SQL edits made after the snapshot survive).
  type GraphSnapshot = {
    nodes: CanvasNode[];
    edges: CanvasEdge[];
    localProviders: Record<string, ProviderDefinitionDraft>;
  };
  const historyRef = useRef<GraphSnapshot[]>([]);
  const futureRef = useRef<GraphSnapshot[]>([]);
  const [, setHistoryVersion] = useState(0);
  const bumpHistoryVersion = () => setHistoryVersion((v) => v + 1);
  const nodesRef = useRef<CanvasNode[]>(nodes);
  const edgesRef = useRef<CanvasEdge[]>(edges);
  const localProvidersRef = useRef(localProviders);
  nodesRef.current = nodes;
  edgesRef.current = edges;
  localProvidersRef.current = localProviders;
  const HISTORY_LIMIT = 20;

  const pushHistory = useCallback(() => {
    historyRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current, localProviders: localProvidersRef.current });
    if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
    if (futureRef.current.length > 0) futureRef.current = [];
    bumpHistoryVersion();
  }, []);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    futureRef.current = [];
    bumpHistoryVersion();
  }, []);

  // Merge-restore of provider drafts: re-add entries the removal GC collected
  // (a restored node needs its definition back) WITHOUT reverting drafts that
  // still exist — current entries win, so SQL edited after the snapshot stays.
  const restoreLocalProviders = useCallback((snapshot: Record<string, ProviderDefinitionDraft>) => {
    setLocalProviders((prev) => ({ ...snapshot, ...prev }));
  }, []);

  const undo = useCallback(() => {
    const snapshot = historyRef.current.pop();
    if (!snapshot) return;
    futureRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current, localProviders: localProvidersRef.current });
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    restoreLocalProviders(snapshot.localProviders);
    ++saveVersionRef.current;
    void flushAndSave();
    bumpHistoryVersion();
  }, [setNodes, setEdges, flushAndSave, restoreLocalProviders]);

  const redo = useCallback(() => {
    const snapshot = futureRef.current.pop();
    if (!snapshot) return;
    historyRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current, localProviders: localProvidersRef.current });
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    restoreLocalProviders(snapshot.localProviders);
    ++saveVersionRef.current;
    void flushAndSave();
    bumpHistoryVersion();
  }, [setNodes, setEdges, flushAndSave, restoreLocalProviders]);

  const canUndo = historyRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  function formatParameterDefaultText(param: ProviderParameter): string {
    if (param.default === undefined || param.default === null) return "";
    return String(param.default);
  }

  function parseParameterDefaultText(paramType: ProviderParamType, raw: string): ProviderParameter["default"] {
    const value = raw.trim();
    if (!value) return undefined;
    if (paramType === "int") {
      const parsed = Number.parseInt(value, 10);
      return Number.isNaN(parsed) ? value : parsed;
    }
    if (paramType === "decimal") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }
    if (paramType === "bool") {
      const normalized = value.toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
      return value;
    }
    return value;
  }

  function openNewParameterModal() {
    if (!selectedProviderDraft) return;
    setEditingParameterIndex(null);
    const nextParam: ProviderParameter = {
      name: `param_${selectedProviderDraft.parameters.length + 1}`,
      type: "string",
      required: false,
      default: "",
      description: ""
    };
    setParameterDraft(nextParam);
    setParameterDefaultText(formatParameterDefaultText(nextParam));
    setParameterModalOpen(true);
  }

  function openEditParameterModal(index: number) {
    if (!selectedProviderDraft) return;
    const target = selectedProviderDraft.parameters[index];
    if (!target) return;
    setEditingParameterIndex(index);
    setParameterDraft(target);
    setParameterDefaultText(formatParameterDefaultText(target));
    setParameterModalOpen(true);
  }

  async function saveParameterModal() {
    if (!selectedNode || !selectedProviderDraft) return;
    const normalizedParamName = parameterDraft.name.trim();
    if (!normalizedParamName) {
      toast.push({ kind: "error", message: "Parameter name is required." });
      return;
    }
    if (!IDENTIFIER_RE.test(normalizedParamName)) {
      toast.push({ kind: "error", message: "Parameter name must use letters, numbers, and underscores only (start with letter or underscore)." });
      return;
    }
    const duplicateParamName = selectedProviderDraft.parameters.some(
      (entry, idx) => idx !== (editingParameterIndex ?? -1) && entry.name.toLowerCase() === normalizedParamName.toLowerCase()
    );
    if (duplicateParamName) {
      toast.push({ kind: "error", message: "Parameter name must be unique." });
      return;
    }
    const previousParamName =
      editingParameterIndex === null ? null : selectedProviderDraft.parameters[editingParameterIndex]?.name ?? null;
    const nextParam: ProviderParameter = {
      ...parameterDraft,
      name: normalizedParamName,
      default: parseParameterDefaultText(parameterDraft.type, parameterDefaultText),
      description: parameterDraft.description ?? ""
    };
    await updateMaterialProviderDraft(selectedNode.id, (draft) => {
      const next = [...draft.parameters];
      if (editingParameterIndex === null) next.push(nextParam);
      else next[editingParameterIndex] = nextParam;
      return { ...draft, parameters: next };
    });
    if (previousParamName && previousParamName !== nextParam.name) {
      setNodeData(selectedNode.id, (data) => {
        const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
        const existing = nextBindings[previousParamName];
        delete nextBindings[previousParamName];
        if (existing !== undefined && nextBindings[nextParam.name] === undefined) {
          nextBindings[nextParam.name] = existing;
        }
        return { ...data, config: { ...data.config, bindings: nextBindings } };
      });
    }
    const ok = await flushAndSave();
    if (ok) setParameterModalOpen(false);
  }

  async function deleteParameterModal() {
    if (!selectedNode || editingParameterIndex === null || !selectedProviderDraft) return;
    const target = selectedProviderDraft.parameters[editingParameterIndex];
    if (!target) return;
    const previousParams = [...selectedProviderDraft.parameters];
    const previousBindings = { ...(((selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {})) };
    const ok = await confirm.confirm({
      title: "Delete parameter?",
      detail: `Delete "${target.name}" from this query?`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    await updateMaterialProviderDraft(selectedNode.id, (draft) => {
      const next = [...draft.parameters];
      next.splice(editingParameterIndex, 1);
      return { ...draft, parameters: next };
    });
    setNodeData(selectedNode.id, (data) => {
      const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
      delete nextBindings[target.name];
      return { ...data, config: { ...data.config, bindings: nextBindings } };
    });
    const saved = await flushAndSave();
    if (saved) {
      setParameterModalOpen(false);
      return;
    }
    await updateMaterialProviderDraft(selectedNode.id, (draft) => ({ ...draft, parameters: previousParams }));
    setNodeData(selectedNode.id, (data) => ({ ...data, config: { ...data.config, bindings: previousBindings } }));
  }

  async function deleteParameterFromList(index: number) {
    if (!selectedNode || !selectedProviderDraft) return;
    const target = selectedProviderDraft.parameters[index];
    if (!target) return;
    const previousParams = [...selectedProviderDraft.parameters];
    const previousBindings = { ...(((selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {})) };
    const ok = await confirm.confirm({
      title: "Delete parameter?",
      detail: `Delete "${target.name}" from this query?`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    await updateMaterialProviderDraft(selectedNode.id, (draft) => {
      const next = [...draft.parameters];
      next.splice(index, 1);
      return { ...draft, parameters: next };
    });
    setNodeData(selectedNode.id, (data) => {
      const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
      delete nextBindings[target.name];
      return { ...data, config: { ...data.config, bindings: nextBindings } };
    });
    const saved = await flushAndSave();
    if (saved) return;
    await updateMaterialProviderDraft(selectedNode.id, (draft) => ({ ...draft, parameters: previousParams }));
    setNodeData(selectedNode.id, (data) => ({ ...data, config: { ...data.config, bindings: previousBindings } }));
  }

  function openAddBindingModal() {
    if (!selectedNode || !selectedProviderDraft) return;
    const bindings = (selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {};
    const firstUnboundParam = selectedProviderDraft.parameters.find((param) => bindings[param.name] === undefined);
    const targetParam = firstUnboundParam ?? selectedProviderDraft.parameters[0];
    if (!targetParam) return;
    setBindingModalMode("add");
    setBindingModalOriginalParamName(null);
    setBindingModalParamName(targetParam.name);
    setBindingModalParamType(targetParam.type);
    setBindingModalHasDefault(targetParam.default !== undefined && targetParam.default !== null);
    setBindingModalAllowScope(
      (selectedNode.data.nodeType === "provider" || selectedNode.data.nodeType === "plex_api") &&
        edges.some((e) => e.target === selectedNode.id && e.data?.edge_type === "scope")
    );
    setBindingModalSourceType(targetParam.default !== undefined && targetParam.default !== null ? "default" : "const");
    setBindingConstText("");
    setBindingWindowName(operationWindows[0]?.name ?? "");
    setBindingWindowField("start_at");
    setBindingVariableName(operationVariables[0]?.name ?? "");
    setBindingScopeColumn("");
    setBindingModalOpen(true);
  }

  function openBindingModal(param: ProviderParameter) {
    if (!selectedNode) return;
    const bindings = (selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {};
    const current = bindings[param.name];
    const sourceType: BindingSource["type"] =
      current?.type ?? (param.default !== undefined && param.default !== null ? "default" : "const");
    setBindingModalMode("edit");
    setBindingModalOriginalParamName(param.name);
    setBindingModalParamName(param.name);
    setBindingModalParamType(param.type);
    setBindingModalHasDefault(param.default !== undefined && param.default !== null);
    setBindingModalAllowScope(
      (selectedNode.data.nodeType === "provider" || selectedNode.data.nodeType === "plex_api") &&
        edges.some((e) => e.target === selectedNode.id && e.data?.edge_type === "scope")
    );
    setBindingModalSourceType(sourceType);
    setBindingConstText(current?.type === "const" ? String(current.value ?? "") : "");
    setBindingWindowName(current?.type === "window" ? current.window : operationWindows[0]?.name ?? "");
    setBindingWindowField(current?.type === "window" ? current.field : "start_at");
    setBindingVariableName(current?.type === "variable" ? current.variable : operationVariables[0]?.name ?? "");
    setBindingScopeColumn(current?.type === "scope" ? current.column : "");
    setBindingModalOpen(true);
  }

  function closeBindingModal() {
    setBindingModalOpen(false);
  }

  function parseBindingConstValue(paramType: ProviderParamType, raw: string): string | number | boolean | null {
    let value: string | number | boolean | null = raw;
    if (paramType === "int") value = raw === "" ? null : Number.parseInt(raw, 10);
    else if (paramType === "decimal") value = raw === "" ? null : Number(raw);
    else if (paramType === "bool") value = raw.toLowerCase() === "true";
    return value;
  }

  async function saveBindingModal() {
    if (!selectedNode || !selectedProviderDraft || !bindingModalParamName) return;
    const selectedParamDef = selectedProviderDraft.parameters.find((param) => param.name === bindingModalParamName);
    if (!selectedParamDef) return;
    setNodeData(selectedNode.id, (data) => {
      const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
      if (bindingModalOriginalParamName && bindingModalOriginalParamName !== bindingModalParamName) {
        delete nextBindings[bindingModalOriginalParamName];
      }
      if (bindingModalSourceType === "default") nextBindings[bindingModalParamName] = { type: "default" };
      else if (bindingModalSourceType === "const") {
        nextBindings[bindingModalParamName] = {
          type: "const",
          value: parseBindingConstValue(selectedParamDef.type, bindingConstText)
        };
      } else if (bindingModalSourceType === "window") {
        nextBindings[bindingModalParamName] = { type: "window", window: bindingWindowName, field: bindingWindowField };
      } else if (bindingModalSourceType === "variable") {
        nextBindings[bindingModalParamName] = { type: "variable", variable: bindingVariableName };
      } else {
        nextBindings[bindingModalParamName] = { type: "scope", column: bindingScopeColumn };
      }
      return { ...data, config: { ...data.config, bindings: nextBindings } };
    });
    const ok = await flushAndSave();
    if (ok) setBindingModalOpen(false);
  }

  async function clearBinding(paramName: string) {
    if (!selectedNode) return;
    const previousBindings = { ...(((selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {})) };
    setNodeData(selectedNode.id, (data) => {
      const nextBindings = { ...((data.config.bindings as Record<string, BindingSource> | undefined) ?? {}) };
      delete nextBindings[paramName];
      return { ...data, config: { ...data.config, bindings: nextBindings } };
    });
    const ok = await flushAndSave();
    if (ok) return;
    setNodeData(selectedNode.id, (data) => ({ ...data, config: { ...data.config, bindings: previousBindings } }));
  }

  function bindingDescription(paramName: string, current: BindingSource): string {
    const targetLabel = "provider param";
    if (current.type === "variable") return `${current.variable || "(variable)"} (op) -> ${paramName} (${targetLabel})`;
    if (current.type === "window") return `${current.window || "(window)"} . ${current.field} (op window) -> ${paramName} (${targetLabel})`;
    if (current.type === "const") return `${String(current.value ?? "") || "(constant)"} (const) -> ${paramName} (${targetLabel})`;
    if (current.type === "scope") return `${current.column || "(scope column)"} (scope column) -> ${paramName} (${targetLabel})`;
    return `${paramName} default -> ${paramName} (${targetLabel})`;
  }

  function resolveWindowBindingValue(
    binding: Extract<BindingSource, { type: "window" }>
  ): string | null {
    const target = operationWindows.find((windowDef) => windowDef.name === binding.window);
    if (!target) return null;
    if (target.type === "absolute") {
      if (binding.field === "start_at") return target.start_at;
      if (binding.field === "end_at") return target.end_at;
      if (binding.field === "start_date") return toIsoDate(target.start_at);
      return toIsoDate(target.end_at);
    }
    const now = new Date();
    const start = addOffset(now, target.start_offset);
    const end = addOffset(now, target.end_offset);
    if (binding.field === "start_at") return start.toISOString();
    if (binding.field === "end_at") return end.toISOString();
    if (binding.field === "start_date") return toIsoDate(start.toISOString());
    return toIsoDate(end.toISOString());
  }

  function buildBoundSqlForNode(
    rawSql: string,
    params: ProviderParameter[],
    bindings: Record<string, BindingSource>
  ): { ok: true; sql: string; resolvedParams: Record<string, string | number | boolean | null> } | { ok: false; message: string } {
    const valuesByParam: Record<string, string | number | boolean | null> = {};
    for (const param of params) {
      const binding = bindings[param.name];
      let value: string | number | boolean | null | undefined = undefined;
      if (!binding || binding.type === "default") {
        value = param.default;
      } else if (binding.type === "const") {
        value = binding.value;
      } else if (binding.type === "variable") {
        value = operationVariables.find((entry) => entry.name === binding.variable)?.value;
      } else if (binding.type === "window") {
        value = resolveWindowBindingValue(binding);
      } else if (binding.type === "scope") {
        return {
          ok: false,
          message: `Cannot execute preview for :${param.name} with source "scope column". Use constant/variable/window for preview, or run with scope data at runtime.`
        };
      }

      if (value === undefined || value === null) {
        if (param.required) {
          return { ok: false, message: `Required parameter :${param.name} has no value.` };
        }
        valuesByParam[param.name] = null;
      } else {
        valuesByParam[param.name] = value;
      }
    }
    return { ok: true, sql: substituteColonParams(rawSql, valuesByParam), resolvedParams: valuesByParam };
  }

  const localSpec = useMemo<OperationSpecV1>(() => {
    const tags = normalizeTagsFromInput(tagsText);
    const nodeIds = new Set(nodes.map((n) => n.id));
    const graphEdges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
    return {
      ...defaultSpec(status, tags),
      status,
      tags,
      windows: operationWindows,
      variables: operationVariables,
      graph: {
        nodes: nodes.map(toOperationNode),
        edges: graphEdges.map((edge) => canvasEdgeToOperationEdge(edge, nodes))
      },
      local_providers: localProviders
    };
  }, [status, tagsText, operationWindows, operationVariables, nodes, edges, localProviders]);

  const localSpecRef = useRef(localSpec);
  localSpecRef.current = localSpec;
  saveRef.current = save;

  /** Share one in-flight cache fetch per node so concurrent callers (e.g. transform tab + provider) await the same result instead of getting null. */
  const nodeCacheLoadInflightRef = useRef<Record<string, Promise<QueryResultsPayload | null>>>({});

  // ── ADR-0159 §3.4: the layout channel ──────────────────────────────────────
  //
  // Dragging a step writes here — a full replace of the position map, debounced
  // like autosave but on its own timer and its own endpoint. It never touches
  // the spec, never moves the concurrency token, and is deliberately
  // last-write-wins: the worst a race costs is a step sitting where the other
  // person put it, which is fixed by dragging it back.
  const layoutSaveTimeoutRef = useRef<number | null>(null);
  const queueLayoutSave = useCallback((delayMs = 600) => {
    if (layoutSaveTimeoutRef.current !== null) {
      window.clearTimeout(layoutSaveTimeoutRef.current);
    }
    layoutSaveTimeoutRef.current = window.setTimeout(() => {
      layoutSaveTimeoutRef.current = null;
      const payload = layoutFromPositions(nodesRef.current);
      // Placement is not worth interrupting anyone over: a failure here leaves
      // the canvas exactly as the person arranged it for this session, and the
      // next drag tries again.
      void apiFetch(`/api/tenant/operations/${encodeURIComponent(operationId)}/layout`, {
        method: "PUT",
        body: { layout: payload },
      }).catch(() => {});
    }, delayMs);
  }, [operationId]);

  // Door-authored specs carry no layout channel (persist strips node.ui), so
  // every step hydrates at the origin. This ranks the graph left-to-right
  // along its edges and writes the result through the same debounced layout
  // channel a drag uses — it never touches the spec or the dirty state.
  const autoLayoutNodes = useCallback(() => {
    setNodes((current) => {
      if (current.length === 0) return current;
      const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
      g.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 48 });
      for (const n of current) {
        g.setNode(n.id, {
          width: n.measured?.width ?? 220,
          height: n.measured?.height ?? 110
        });
      }
      for (const e of edgesRef.current) {
        if (g.hasNode(e.source) && g.hasNode(e.target)) g.setEdge(e.source, e.target);
      }
      dagre.layout(g);
      return current.map((n) => {
        const placed = g.node(n.id);
        if (!placed) return n;
        // dagre reports centers; React Flow positions are top-left corners.
        const width = n.measured?.width ?? 220;
        const height = n.measured?.height ?? 110;
        return { ...n, position: { x: placed.x - width / 2, y: placed.y - height / 2 } };
      });
    });
    queueLayoutSave();
    window.requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView({ padding: 0.2, duration: 300 });
    });
  }, [setNodes, queueLayoutSave]);

  const applyGraphNodeChanges = useCallback(
    (changes: Parameters<typeof onNodesChangeBase>[0]) => {
      onNodesChangeBase(changes);

      const removedIds = new Set(
        changes.filter((c): c is { type: "remove"; id: string } => c.type === "remove").map((c) => c.id)
      );
      if (removedIds.size > 0) {
        setEdges((current) =>
          current.filter((e) => !removedIds.has(e.source) && !removedIds.has(e.target))
        );
        // Mirror of the hub applier's remove_node GC (FIXES.md 2026-08-06):
        // collect the removed nodes' inline provider drafts once no surviving
        // node references them. Refcounted, and read generically off
        // config.provider — no node-type list. Undo restores collected drafts
        // via the history snapshot's merge-restore.
        const localRefOf = (n: CanvasNode): string => {
          const cfg = n.data.config as Record<string, unknown> | undefined;
          const provider = cfg?.provider as { type?: string; local_provider_id?: string } | undefined;
          return provider?.type === "local" && provider.local_provider_id ? provider.local_provider_id : "";
        };
        // nodesRef still holds the pre-removal graph here (refs update on render).
        const stillReferenced = new Set(
          nodesRef.current.filter((n) => !removedIds.has(n.id)).map(localRefOf).filter(Boolean)
        );
        const collectable = nodesRef.current
          .filter((n) => removedIds.has(n.id))
          .map(localRefOf)
          .filter((id) => id !== "" && !stillReferenced.has(id));
        if (collectable.length > 0) {
          setLocalProviders((prev) => {
            const next = { ...prev };
            for (const id of collectable) delete next[id];
            return next;
          });
        }
      }

      const hasRemove = changes.some((c) => c.type === "remove");
      if (hasRemove) {
        ++saveVersionRef.current;
      }

      // ADR-0159 §3.4 — a finished drag writes PLACEMENT, not the spec. This
      // line is the one that used to send the whole document on every drag,
      // which is how a stale tab could revert a pipeline by moving a box.
      const dragEnded = changes.some((c) => c.type === "position" && c.dragging === false);
      if (dragEnded) {
        queueLayoutSave();
      }

      const shouldSave = hasRemove || changes.some((c) => c.type === "select");

      if (shouldSave) {
        void flushAndSave();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onNodesChangeBase, setEdges, flushAndSave, queueLayoutSave]
  );

  const onNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChangeBase>[0]) => {
      const removes = changes.filter((c): c is { type: "remove"; id: string } => c.type === "remove");
      if (removes.length === 0) {
        applyGraphNodeChanges(changes);
        return;
      }

      const removedIds = new Set(removes.map((r) => r.id));
      const fwdAdj = new Map<string, string[]>();
      for (const edge of edges) {
        const list = fwdAdj.get(edge.source) ?? [];
        list.push(edge.target);
        fwdAdj.set(edge.source, list);
      }
      const downstream = new Set<string>();
      const walk = (nid: string) => {
        for (const child of fwdAdj.get(nid) ?? []) {
          if (downstream.has(child) || removedIds.has(child)) continue;
          downstream.add(child);
          walk(child);
        }
      };
      for (const id of removedIds) walk(id);
      const downstreamCount = downstream.size;
      const downstreamSuffix =
        downstreamCount > 0
          ? ` ${downstreamCount} downstream node${downstreamCount === 1 ? "" : "s"} will lose this as an input and may need to be reconfigured.`
          : "";

      // Migration 160 (2026-06-10) — persist removal is no longer eagerly
      // destructive at PATCH. Two distinct paths:
      //   1. Persist node with an initialized PDO → explicit data-loss
      //      action. Typed-confirm per persist; on confirm, hard-delete the
      //      PDO via DELETE /tenant/pdos/:id BEFORE the spec save; the
      //      hub's PATCH orphan pass then no-ops on the missing row.
      //   2. Persist node with no PDO yet, or non-persist nodes → cheap
      //      confirm. Orphan-on-removal preserves any incidental data
      //      across undo/redo; undo restoring the same React Flow id
      //      auto-rebinds.
      // ADR-0106 — a Model Train node with a trained model is the same kind of
      // explicit data-loss action as a PDO-backed persist: typed-confirm per
      // node, then hard-DELETE the model (DB rows + on-disk artifacts) via
      // DELETE /tenant/models/:id BEFORE the spec save. A node with no model yet
      // takes the cheap-confirm path.
      const persistRemovesWithPdo: Array<{ id: string; label: string; pdoId: string }> = [];
      const modelRemovesWithModel: Array<{ id: string; label: string; modelId: string }> = [];
      let hasNonPersistOrEmptyRemove = false;
      for (const r of removes) {
        const node = nodes.find((n) => n.id === r.id);
        if (!node) continue;
        if (node.data.nodeType === "persist") {
          const sp = (node.data.config as Record<string, unknown> | undefined)?.spec_persist as
            | { pdo_id?: string | null }
            | undefined;
          const pdoId = sp?.pdo_id?.trim();
          if (pdoId) {
            persistRemovesWithPdo.push({
              id: r.id,
              label: node.data.label?.trim() || node.data.nodeType,
              pdoId,
            });
          } else {
            hasNonPersistOrEmptyRemove = true;
          }
          continue;
        }
        if (node.data.nodeType === "model_train") {
          const sp = (node.data.config as Record<string, unknown> | undefined)?.spec_model_train as
            | { model_id?: string | null }
            | undefined;
          const modelId = sp?.model_id?.trim();
          if (modelId) {
            modelRemovesWithModel.push({
              id: r.id,
              label: node.data.label?.trim() || node.data.nodeType,
              modelId,
            });
          } else {
            hasNonPersistOrEmptyRemove = true;
          }
          continue;
        }
        hasNonPersistOrEmptyRemove = true;
      }

      void (async () => {
        // Each PDO-backed persist gets its own typed-confirm. Cancel
        // anywhere → abort the whole batch (no partial canvas mutation, no
        // partial DELETE).
        for (const persist of persistRemovesWithPdo) {
          const ok = await confirm.confirm({
            title: "Permanently delete data store?",
            detail: `Removing the persist node "${persist.label}" will permanently delete its data store and every version of materialized data.${downstreamSuffix} This cannot be undone.`,
            confirmLabel: "Delete permanently",
            destructive: true,
            requireTypedConfirmation: { value: persist.label, label: "Type the node name to confirm:" },
          });
          if (!ok) return;
          try {
            await apiFetch(`/api/tenant/pdos/${encodeURIComponent(persist.pdoId)}`, {
              method: "DELETE",
            });
          } catch (e) {
            toast.push({
              kind: "error",
              message: `Couldn't delete data store for "${persist.label}": ${e instanceof Error ? e.message : String(e)}`,
            });
            return; // abort — don't proceed with canvas mutation
          }
        }

        // Each model-backed Model Train node gets its own typed-confirm + DELETE.
        for (const model of modelRemovesWithModel) {
          const ok = await confirm.confirm({
            title: "Permanently delete model?",
            detail: `Removing the Model Train node "${model.label}" will permanently delete its model, every trained version, and the trained artifacts.${downstreamSuffix} This cannot be undone.`,
            confirmLabel: "Delete permanently",
            destructive: true,
            requireTypedConfirmation: { value: model.label, label: "Type the node name to confirm:" },
          });
          if (!ok) return;
          try {
            await apiFetch(`/api/tenant/models/${encodeURIComponent(model.modelId)}`, {
              method: "DELETE",
            });
          } catch (e) {
            toast.push({
              kind: "error",
              message: `Couldn't delete model for "${model.label}": ${e instanceof Error ? e.message : String(e)}`,
            });
            return; // abort — don't proceed with canvas mutation
          }
        }

        const cheapCount = removes.length - persistRemovesWithPdo.length - modelRemovesWithModel.length;
        if (cheapCount > 0) {
          const ok = await confirm.confirm({
            title: cheapCount === 1 ? "Remove node?" : `Remove ${cheapCount} nodes?`,
            detail: hasNonPersistOrEmptyRemove
              ? `This will remove the node and any connected edges.${downstreamSuffix} This cannot be undone.`
              : `This will unbind the persist node. The data store has no versions yet, so nothing is lost.${downstreamSuffix}`,
            confirmLabel: "Remove",
          });
          if (!ok) return;
        }

        if (persistRemovesWithPdo.length > 0 || modelRemovesWithModel.length > 0) {
          // PDO-backed persists / model-backed train nodes were just hard-DELETEd
          // above. Restoring the canvas id would re-bind to an empty store or a
          // gone model, which would be a more confusing "undo" than no undo at
          // all — wipe history to be honest about what's recoverable.
          clearHistory();
        } else {
          pushHistory();
        }
        applyGraphNodeChanges(changes);
      })();
    },
    [nodes, edges, confirm, applyGraphNodeChanges, toast, pushHistory, clearHistory]
  );

  const onEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChangeBase>[0]) => {
      const removals = changes.filter((c): c is { type: "remove"; id: string } => c.type === "remove");
      const nonRemovals = changes.filter((c) => c.type !== "remove");

      if (removals.length === 0) {
        onEdgesChangeBase(changes);
        return;
      }

      // Build forward adjacency from current edges (before the change is applied)
      const fwdAdj = new Map<string, string[]>();
      for (const edge of edges) {
        const list = fwdAdj.get(edge.source) ?? [];
        list.push(edge.target);
        fwdAdj.set(edge.source, list);
      }
      const getDownstream = (startId: string): string[] => {
        const visited = new Set<string>();
        const walk = (nid: string) => {
          if (visited.has(nid)) return;
          visited.add(nid);
          for (const child of fwdAdj.get(nid) ?? []) walk(child);
        };
        walk(startId);
        return [...visited];
      };

      // Collect the target of each removed edge plus all its descendants
      const toEvict = new Set<string>();
      for (const c of removals) {
        const removedEdge = edges.find((e) => e.id === c.id);
        if (!removedEdge) continue;
        for (const nid of getDownstream(removedEdge.target)) toEvict.add(nid);
      }

      const applyRemovals = () => {
        if (toEvict.size > 0) {
          setNodeCache((prev) => {
            const next = { ...prev };
            for (const nid of toEvict) delete next[nid];
            return next;
          });
          for (const nid of toEvict) {
            delete nodeCacheLoadInflightRef.current[nid];
          }
        }
        ++saveVersionRef.current;
        void flushAndSave();
        onEdgesChangeBase(changes);
      };

      const n = removals.length;
      const affected = toEvict.size;
      const affectedSuffix =
        affected > 0
          ? ` ${affected} downstream node${affected === 1 ? "" : "s"} will lose ${affected === 1 ? "its" : "their"} input and may need to be reconfigured.`
          : "";

      void (async () => {
        const ok = await confirm.confirm({
          title: n === 1 ? "Remove connection?" : `Remove ${n} connections?`,
          detail: `This will disconnect the link${n === 1 ? "" : "s"}.${affectedSuffix} This cannot be undone.`,
          confirmLabel: "Remove",
        });
        if (!ok) {
          if (nonRemovals.length > 0) onEdgesChangeBase(nonRemovals);
          return;
        }
        pushHistory();
        applyRemovals();
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEdgesChangeBase, edges, confirm, pushHistory]
  );

  const dirty =
    savedSnapshot !==
    JSON.stringify({
      name: name.trim(),
      description: description.trim() || null,
      status,
      tags: normalizeTagsFromInput(tagsText),
      spec: localSpec
    });

  dirtyRef.current = dirty;
  busyRef.current = busy;

  const queueAutoSave = useCallback((delayMs = 350) => {
    // ADR-0159 §6 — autosave PAUSES on a conflict. Left running it would
    // re-send the same stale write every 350 ms forever, each one refused,
    // and the reader would be told about it repeatedly while nothing changed.
    if (conflictRef.current) return;
    if (autoSaveTimeoutRef.current !== null) {
      window.clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = window.setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      if (!dirtyRef.current || busyRef.current || conflictRef.current) return;
      void saveRef.current(undefined, { silent: true });
    }, delayMs);
  }, []);


  /** Cancel any pending debounced autosave and save synchronously if the
   *  operation is dirty. Used before actions that need the latest config in
   *  the DB (e.g., dry-run evaluate). Resolves once the save completes. */
  const flushAutoSave = useCallback(async () => {
    if (autoSaveTimeoutRef.current !== null) {
      window.clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    if (!dirtyRef.current || busyRef.current || conflictRef.current) return;
    await saveRef.current(undefined, { silent: true });
  }, []);

  const onConnect = useCallback(
    (connection: Connection) => {
      const sourceNode = nodes.find((node) => node.id === connection.source);
      const targetNode = nodes.find((node) => node.id === connection.target);
      if (!sourceNode || !targetNode) return;
      const edgeType = inferEdgeTypeFromConnection({
        sourceNodeType: sourceNode.data.nodeType,
        targetNodeType: targetNode.data.nodeType,
        sourceHandle: connection.sourceHandle ?? "",
        targetHandle: connection.targetHandle ?? ""
      });
      if (!edgeType) {
        toast.push({ kind: "error", message: "Those ports cannot be connected." });
        return;
      }

      // Edges to remove-and-replace once the user confirms (single-input nodes only).
      let replaceableDataInput: Edge[] | null = null;

      if (edgeType === "data" && connection.target) {
        const targetType = targetNode.data.nodeType;
        const newTargetPort = stripPortHandle(connection.targetHandle);
        const existingDataInputs = edges.filter(
          (e) => e.target === connection.target && e.data?.edge_type === "data"
        );

        if (targetType === "transform") {
          // multi tabular_in — allowed
        } else if (targetType === "ai_provider") {
          const usedPorts = new Set(existingDataInputs.map((e) => stripPortHandle(e.targetHandle)));
          if (usedPorts.has(newTargetPort)) {
            toast.push({
              kind: "error",
              message: "This AI Provider input port already has a connection."
            });
            return;
          }
          if (existingDataInputs.length >= 2) {
            toast.push({
              kind: "error",
              message: "AI Provider allows at most one data_in and one context_in. Remove a connection first."
            });
            return;
          }
        } else if (targetType === "persist") {
          // ADR-0102: persist's input topology is policy-driven — snapshot
          // policy has one port (tabular_in_incremental); keyed policies have
          // two (tabular_in_initial + tabular_in_incremental). Enforce per-
          // port at-most-one, not a single-fan-in cap. Edges on disallowed
          // ports under the current policy still pass through here (the
          // canvas's policy-driven handle filtering keeps them undraggable
          // in the first place; the hub validator catches any that slip
          // through e.g. via a manual spec edit).
          const usedPorts = new Set(existingDataInputs.map((e) => stripPortHandle(e.targetHandle)));
          if (usedPorts.has(newTargetPort)) {
            toast.push({
              kind: "error",
              message: "This Persist input port already has a connection. Remove it before wiring a new one."
            });
            return;
          }
        } else if (existingDataInputs.length > 0) {
          // Single-input node with an existing connection: defer to the
          // replace-confirm flow below instead of rejecting outright.
          replaceableDataInput = existingDataInputs;
        }
      }

      if (edgeType === "scope" && connection.target) {
        const existingScope = edges.filter(
          (e) => e.target === connection.target && e.data?.edge_type === "scope"
        );
        if (existingScope.length > 0) {
          toast.push({ kind: "error", message: "This node already has a scope input." });
          return;
        }
      }

      const sourcePort = stripPortHandle(connection.sourceHandle);
      const targetPort = stripPortHandle(connection.targetHandle);

      const commitEdge = (removeEdgeIds?: Set<string>) => {
        pushHistory();
        setEdges((current) => {
          const base = removeEdgeIds ? current.filter((e) => !removeEdgeIds.has(e.id)) : current;
          return addEdge(
            {
              ...connection,
              id: crypto.randomUUID(),
              sourceHandle: sourcePort,
              targetHandle: targetPort,
              markerEnd: { type: MarkerType.ArrowClosed },
              data: { edge_type: edgeType },
              animated: edgeType === "scope",
              style:
                edgeType === "scope"
                  ? { stroke: "var(--op-port-scope)", strokeDasharray: "4 4" }
                  : { stroke: "var(--op-port-tabular)" }
            },
            base
          );
        });
        void flushAndSave();
      };

      // Single-input data node that already has a connection: offer to replace
      // it rather than rejecting the drop. (Per-port multi-input nodes —
      // transform / ai_provider / persist — are handled above and never reach here.)
      if (replaceableDataInput) {
        const targetLabel =
          targetNode.data.label?.trim() || NODE_LABELS[targetNode.data.nodeType] || "This";
        void (async () => {
          const ok = await confirm.confirm({
            title: "Replace existing input?",
            detail: `"${targetLabel}" accepts only one data input. Replace its current connection with this one?`,
            confirmLabel: "Replace"
          });
          if (!ok) return;
          commitEdge(new Set(replaceableDataInput.map((e) => e.id)));
        })();
        return;
      }

      commitEdge();
    },
    [nodes, edges, setEdges, toast, confirm, pushHistory]
  );

  function setNodeData(nodeId: string, updater: (data: CanvasNodeData) => CanvasNodeData) {
    setNodes((current) =>
      current.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: updater(node.data)
            }
          : node
      )
    );
    queueAutoSave();
  }

  // ── ADR-0116: the model-node config-change seam (the only in-session writer) ──
  // Both AI reasoners (advisor propose, architect reach) route their deltas through
  // here. Computes the next config, validates it against the resolved upstream schema
  // + the model_train rules, requires a confirm for destructive deltas, then writes
  // via setNodeData (autosaves). Never writes an invalid delta; the hub's save-time
  // validateOperation is the authoritative backstop.

  const readModelTrainConfig = (data: CanvasNodeData): ModelTrainConfig => {
    const sp = (data.config?.spec_model_train as Record<string, unknown> | undefined) ?? {};
    return ((sp.config as ModelTrainConfig | undefined) ?? {}) as ModelTrainConfig;
  };
  // model_predict → spec_model_predict; model_evaluate → spec_model_evaluate (ADR-0116 all-nodes).
  const bindingSpecKey = (kind: ModelNodeKind): string => (kind === "model_predict" ? "spec_model_predict" : "spec_model_evaluate");
  const readBindingSpec = (data: CanvasNodeData, kind: ModelNodeKind): ModelBindingConfig => {
    const sp = (data.config?.[bindingSpecKey(kind)] as ModelBindingConfig | undefined) ?? {};
    return { ...sp };
  };

  /** ADR-0116 → operations-seam migration: the editor no longer applies model
   *  deltas itself. Flush pending edits so the persisted row matches the canvas,
   *  POST the delta through the hub change seam (the same applier the MCP door
   *  uses — validation, destructive classification, and the revision ledger all
   *  live there), then patch the returned node config in place — never a
   *  whole-graph rehydrate (preserves the editor's delete-race discipline).
   *  The existing confirm dialog answers the hub's `confirmation_required` by
   *  re-calling with the change token. */
  const applyModelConfigChange = async (
    nodeId: string,
    change: ModelConfigChange
  ): Promise<ApplyModelConfigResult> => {
    const node = nodes.find((n) => n.id === nodeId);
    const kind = node?.data.nodeType as ModelNodeKind | undefined;
    if (!node || (kind !== "model_train" && kind !== "model_predict" && kind !== "model_evaluate")) {
      return { ok: false, reason: "That change targets a model node that isn't on this canvas." };
    }

    await flushAutoSave();

    const before: ModelTrainConfig | ModelBindingConfig =
      kind === "model_train" ? readModelTrainConfig(node.data) : readBindingSpec(node.data, kind);
    const changes = [{ kind: "update_node_config", node_id: nodeId, config: change }];
    type ChangeApplyResponse = { operation: Operation; summary?: string[]; note?: string };
    const post = (changeToken?: string) =>
      apiFetch<ChangeApplyResponse>(`/api/tenant/operations/${encodeURIComponent(operationId)}/change-apply`, {
        method: "POST",
        body: changeToken ? { changes, change_token: changeToken } : { changes },
      });

    let applied: ChangeApplyResponse;
    try {
      applied = await post();
    } catch (e) {
      const confirmable =
        e instanceof ApiRequestError && e.code === "confirmation_required" && typeof e.data.change_token === "string";
      if (!confirmable) {
        return { ok: false, reason: e instanceof Error ? e.message : String(e) };
      }
      const err = e as ApiRequestError;
      const summary = Array.isArray(err.data.summary) ? (err.data.summary as string[]).join(" ") : "";
      const ok = await confirm.confirm({
        title: "Apply this change to the model?",
        detail: `${summary || err.message} You'll still need to press Train to retrain the model.`,
        confirmLabel: "Apply change",
        destructive: true,
      });
      if (!ok) return { ok: false, reason: "Cancelled.", cancelled: true };
      try {
        applied = await post(err.data.change_token as string);
      } catch (e2) {
        return { ok: false, reason: e2 instanceof Error ? e2.message : String(e2) };
      }
    }

    // Patch the applied node config in place from the hub's response.
    const savedSpec = applied.operation.spec as { graph?: { nodes?: Array<Record<string, unknown>> } } | null;
    const savedNode = (savedSpec?.graph?.nodes ?? []).find((n) => String(n.id ?? "") === nodeId);
    const savedConfig =
      savedNode && savedNode.config && typeof savedNode.config === "object" && !Array.isArray(savedNode.config)
        ? (savedNode.config as Record<string, unknown>)
        : null;
    if (savedConfig) {
      setNodeData(nodeId, (data) => ({ ...data, config: savedConfig }));
    }
    // Sync the savedSnapshot (the addNode pattern) so the patched node doesn't
    // read as fresh dirt and echo a second save of what the hub just persisted.
    setSavedSnapshot(
      JSON.stringify({
        name: applied.operation.name,
        description: applied.operation.description ?? null,
        status: applied.operation.status,
        tags: applied.operation.tags ?? [],
        spec: parseSpecV1(applied.operation.spec, applied.operation.status, applied.operation.tags ?? []),
      })
    );

    // The diff is preview rendering, computed from before vs the hub's applied
    // state (nothing renders the binding's model name from this diff today).
    if (kind === "model_train") {
      const after = ((savedConfig?.spec_model_train as { config?: ModelTrainConfig } | undefined)?.config ?? {}) as ModelTrainConfig;
      return { ok: true, diff: computeConfigDiff(before as ModelTrainConfig, after) };
    }
    const after = (savedConfig?.[bindingSpecKey(kind)] as ModelBindingConfig | undefined) ?? {};
    return { ok: true, diff: computeBindingDiff(before as ModelBindingConfig, after, kind, () => null) };
  };

  const selectNodeById = (nodeId: string) => {
    setNodes((current) => current.map((n) => (n.selected === (n.id === nodeId) ? n : { ...n, selected: n.id === nodeId })));
  };

  // ADR-0116 §4.3 — architect delegation: hand the directed instruction to the node's
  // OWN advisor (it runs server-side), then bring that node's Advisor tab into view so
  // the panel shows the conversation + any proposed change (reuses the P5 surface).
  const handleArchitectDelegate = (nodeId: string, instruction: string) => {
    void (async () => {
      try {
        const base = `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/advisor/sessions`;
        const created = await apiFetch<{ session: { id: string } }>(base, { method: "POST" });
        await apiFetch(`${base}/${encodeURIComponent(created.session.id)}/messages`, { method: "POST", body: { message: instruction } });
      } catch {
        toast.push({ kind: "error", message: "Couldn't hand this to the model's advisor." });
        return;
      }
      openAdvisorOnSelectRef.current = nodeId;
      selectNodeById(nodeId);
    })();
  };

  // ADR-0216 §A7.13: the steps an Atlas Report keeps say so on the canvas.
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => {
        const keptBy = reportKeptSteps[n.id] ?? null;
        if (JSON.stringify(n.data.keptBy ?? null) === JSON.stringify(keptBy)) return n;
        return { ...n, data: { ...n.data, keptBy } };
      })
    );
  }, [reportKeptSteps]);

  // ── Node output helpers ─────────────────────────────────────────────────────

  // ADR-0146: mirror lock state onto the canvas glyph. This cannot ride along
  // with the node-output sync — a node can be locked before it has ever run, so
  // it may have no output row at all.
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => {
        const lock = nodeSchemaLocks[n.id];
        const title = lock ? schemaLockTitle(lock) : null;
        if ((n.data.schemaLockTitle ?? null) === title) return n;
        return { ...n, data: { ...n.data, schemaLockTitle: title } };
      })
    );
  }, [nodeSchemaLocks]);

  // ADR-0153 Phase 5 — mirror findings onto the canvas badge. Same shape as the
  // schema-lock sweep above and for the same reason: findings can exist before
  // a node has ever run, so this cannot ride the node-output sync (whose
  // early-return skips nodes with no output row). Identity-preserving so an
  // unchanged map causes no render churn.
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => {
        const summary = groupedFindings.byNode.get(n.id) ?? null;
        const severity = summary?.severity ?? null;
        const title = summary ? findingsBadgeTitle(summary) : null;
        if ((n.data.findingsSeverity ?? null) === severity && (n.data.findingsTitle ?? null) === title) return n;
        return { ...n, data: { ...n.data, findingsSeverity: severity, findingsTitle: title } };
      })
    );
  }, [groupedFindings]);

  /**
   * ADR-0146: freeze / unfreeze one node's output shape. An immediate server
   * action, not a spec edit — no autosave involvement, and it deliberately does
   * NOT freeze the node's configuration (editing a locked node stays allowed;
   * the frozen shape flags the consequence on the next run).
   */
  async function toggleNodeSchemaLock(nodeId: string, locked: boolean) {
    setSchemaLockBusy((prev) => ({ ...prev, [nodeId]: true }));
    try {
      const result = await apiFetch<{
        node_schema_lock: { node_id: string; locked: boolean; locked_at: string | null; frozen: boolean };
        updated_at?: string;
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/schema-lock`,
        { method: "POST", body: { locked } }
      );
      // The gesture bumped the ADR-0159 token server-side; adopt the fresh one
      // or the next spec save carries a stale token and 409s against our own write.
      rememberToken(result.updated_at);
      const lock = result.node_schema_lock;
      setNodeSchemaLocks((prev) => {
        const next = { ...prev };
        if (lock.locked && lock.locked_at) next[nodeId] = { locked_at: lock.locked_at, frozen: lock.frozen };
        else delete next[nodeId];
        return next;
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSchemaLockBusy((prev) => ({ ...prev, [nodeId]: false }));
    }
  }

  async function loadNodeOutputs() {
    if (!operationId) return;
    try {
      const data = await apiFetch<{ outputs: Array<{ node_id: string; node_type: string; last_row_count: number | null; last_duration_ms: number | null; last_run_at: string | null; last_error: string | null; validation_status?: string; validation_error?: string | null; output_schema?: Array<{ name: string; type: string }> | null }> }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/node-outputs`
      );
      const map: Record<string, NodeOutputRecord> = {};
      for (const o of data.outputs) {
        map[o.node_id] = {
          nodeId: o.node_id,
          nodeType: o.node_type,
          rowCount: o.last_row_count,
          durationMs: o.last_duration_ms,
          runAt: o.last_run_at,
          error: o.last_error,
          validationStatus: (o.validation_status as "valid" | "needs_validation") ?? "valid",
          validationError: o.validation_error ?? null,
          outputSchema: o.output_schema ?? null
        };
      }
      setNodeOutputs(map);
      setNodes((prev) =>
        prev.map((n) => {
          const rec = map[n.id];
          if (!rec) return n;
          const rs: NodeRunStatus = rec.error ? "error" : rec.runAt ? "success" : "idle";
          const needsValidation = rec.validationStatus === "needs_validation";
          return {
            ...n,
            data: {
              ...n.data,
              runStatus: rs,
              needsValidation
            }
          };
        })
      );
    } catch {
      // non-critical; ignore
    }
  }

  async function loadNodeRunHistory(nodeId: string) {
    if (!operationId) return;
    setNodeRunHistoryLoading((prev) => ({ ...prev, [nodeId]: true }));
    try {
      const nd = nodes.find((n) => n.id === nodeId);
      const ndType = nd?.data?.nodeType;
      const ndConfig = nd?.data?.config;
      const ndProviderRef = ndConfig && typeof ndConfig === "object" && !Array.isArray(ndConfig)
        ? (ndConfig as Record<string, unknown>).provider : null;
      const isShared = ndProviderRef && typeof ndProviderRef === "object" && !Array.isArray(ndProviderRef)
        && (ndProviderRef as Record<string, unknown>).type === "shared"
        && !!(ndProviderRef as Record<string, unknown>).provider_id
        && ndType === "provider";

      if (isShared) {
        // Shared providers run ACROSS operations — their history is the
        // provider's own cross-operation refresh log, not an operation run.
        // No per-run chain Trace (that surfaces in the consuming node's trace).
        const res = await apiFetch<{ runs: ExecutionLogEntry[] }>(
          `/api/tenant/operations/${operationId}/nodes/${nodeId}/provider-runs?limit=50`
        );
        const entries = res.runs;
        setNodeExecutionEntries((prev) => ({ ...prev, [nodeId]: entries }));
        const runs: NodeRunHistoryEntry[] = entries.map((entry) => {
          const details = (entry.details ?? {}) as Record<string, unknown>;
          return {
            id: entry.id,
            node_id: entry.node_id ?? nodeId,
            node_type: entry.source_type,
            status: entry.status,
            started_at: entry.started_at,
            finished_at: entry.finished_at,
            duration_ms: entry.duration_ms,
            row_count: entry.row_count,
            error: entry.error,
            from_cache: Boolean(details.from_cache),
            details: entry.details ?? null,
            connector_id: entry.connector_id,
            runId: null
          };
        });
        setNodeRunHistory((prev) => ({ ...prev, [nodeId]: runs }));
      } else {
        // ADR-0128: operation-run-based history. Each row is a run that
        // TOUCHED this node — targeted it (its own Execute / Refresh-cache)
        // or made it do real work as a participant (how local providers run) —
        // carrying the node's own outcome; the row opens that run's full-chain
        // Trace. The backend excludes runs where this node merely participated
        // as a downstream cache-hit.
        const res = await apiFetch<{
          runs: Array<{
            run_id: string;
            run_status: string;
            run_context: string;
            run_created_at: string;
            run_finished_at: string | null;
            run_error: string | null;
            node_status: string;
            node_row_count: number | null;
            node_duration_ms: number | null;
            node_finished_at: string | null;
            node_error: string | null;
            run_failed_node_id: string | null;
          }>;
        }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/operation-runs?limit=50`);
        // Bugfix 2026-10-29: the row reports THIS step's outcome. Its error is
        // the step's own; a run that failed at a sibling leaves a labelled
        // note ("Run failed at <step>") instead of lending the row its error.
        // Step labels come from the canvas — never a node id on screen.
        const stepLabelById = new Map(
          nodes.map((n) => [n.id, n.data.label?.trim() || NODE_LABELS[n.data.nodeType] || ""])
        );
        const runs: NodeRunHistoryEntry[] = res.runs.map((r) => {
          const outcome = historyRowOutcome({
            nodeStatus: r.node_status,
            runStatus: r.run_status,
            nodeError: r.node_error,
            runError: r.run_error,
            failedStep: r.run_failed_node_id ? { label: stepLabelById.get(r.run_failed_node_id) ?? null } : null,
          });
          return {
            id: r.run_id,
            node_id: nodeId,
            node_type: ndType ?? "",
            status: r.node_status,
            run_status: r.run_status,
            run_context: r.run_context,
            started_at: r.run_created_at,
            finished_at: r.node_finished_at ?? r.run_finished_at ?? r.run_created_at,
            duration_ms: r.node_duration_ms,
            row_count: r.node_row_count,
            error: outcome.error,
            run_note: outcome.runNote,
            from_cache: r.node_status === "from_cache",
            runId: r.run_id
          };
        });
        setNodeRunHistory((prev) => ({ ...prev, [nodeId]: runs }));
      }
    } catch {
      // non-critical
    } finally {
      setNodeRunHistoryLoading((prev) => ({ ...prev, [nodeId]: false }));
    }
  }

  /** Returns the immediate upstream canvas nodes (direct data-edge parents) of a given node. */
  function getDirectParentNodes(targetNodeId: string): typeof nodes {
    const parentIds = new Set(
      edges
        .filter((e) => e.data?.edge_type === "data" && e.target === targetNodeId)
        .map((e) => e.source)
    );
    return nodes.filter((n) => parentIds.has(n.id));
  }

  /** Mark a set of node IDs with the given run status on the canvas. */
  function setRunStatus(nodeIds: string[], status: NodeRunStatus) {
    setNodes((prev) =>
      prev.map((n) => (nodeIds.includes(n.id) ? { ...n, data: { ...n.data, runStatus: status } } : n))
    );
  }

  /** Walk data edges backward from a node to collect all ancestor IDs (inclusive of the start node). */
  function getLineageNodeIds(startNodeId: string): string[] {
    const reverseAdj = new Map<string, string[]>();
    for (const edge of edges) {
      if (edge.data?.edge_type !== "data") continue;
      const list = reverseAdj.get(edge.target) ?? [];
      list.push(edge.source);
      reverseAdj.set(edge.target, list);
    }
    const visited = new Set<string>();
    const walk = (nid: string) => {
      if (visited.has(nid)) return;
      visited.add(nid);
      for (const up of reverseAdj.get(nid) ?? []) walk(up);
    };
    walk(startNodeId);
    return [...visited];
  }

  async function loadNodeCache(nodeId: string, force = false): Promise<QueryResultsPayload | null> {
    // Return already-loaded cache (including null = "confirmed empty"), unless forced
    if (!force && Object.prototype.hasOwnProperty.call(nodeCache, nodeId)) return nodeCache[nodeId];
    if (!force) {
      const inflight = nodeCacheLoadInflightRef.current[nodeId];
      if (inflight) return inflight;
    }

    const promise = (async (): Promise<QueryResultsPayload | null> => {
      setNodeCacheLoading((prev) => ({ ...prev, [nodeId]: true }));
      try {
        const data = await apiFetch<{ transformResult: unknown; lastRunAt: string | null; lastRowCount: number | null; lastDurationMs: number | null; lastError: string | null }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache`
        );
        const payload = extractQueryPayload(data.transformResult);
        setNodeCache((prev) => ({ ...prev, [nodeId]: payload }));
        return payload;
      } catch (e) {
        // 404 = no cache yet; other errors = surface in UI
        if (!(e instanceof ApiRequestError && e.status === 404)) {
          console.warn("[OperationEditor] loadNodeCache error:", e);
        }
        setNodeCache((prev) => ({ ...prev, [nodeId]: null }));
        return null;
      } finally {
        setNodeCacheLoading((prev) => ({ ...prev, [nodeId]: false }));
      }
    })();

    if (!force) {
      nodeCacheLoadInflightRef.current[nodeId] = promise;
      void promise.finally(() => {
        if (nodeCacheLoadInflightRef.current[nodeId] === promise) {
          delete nodeCacheLoadInflightRef.current[nodeId];
        }
      });
    }

    return promise;
  }

  /** Row count of a run's target node (rows come from the polled node row, not the receipt). */
  function targetRowCount(nodeId: string, nodes: readonly OperationRunNode[]): number {
    return nodes.find((n) => n.node_id === nodeId)?.row_count ?? 0;
  }

  /** Run a provider node (async) to refresh its cache, then reload cache display. */
  async function refreshProviderCache(nodeId: string) {
    setNodeCacheRefreshBusy(true);

    // Auto-save so the run reads the latest spec from DB
    const saved = await save();
    if (!saved) { setNodeCacheRefreshBusy(false); return; }

    setRunStatus([nodeId], "running");
    try {
      await runNodeAsync(nodeId, {
        forceRefresh: true,
        onSucceeded: async (_run, nodes) => {
          // Force-reload cache (bypasses stale-closure guard)
          const freshPayload = await loadNodeCache(nodeId, true);
          if (freshPayload && freshPayload.columns.length > 0 && selectedProviderRef?.type === "local") {
            const lid = selectedProviderRef.local_provider_id;
            const current = localProviders[lid];
            if (current) {
              const updatedLocalProviders = { ...localProviders, [lid]: { ...current, schema_contract: { columns: freshPayload.columns } } };
              setLocalProviders(updatedLocalProviders);
              await save(undefined, { silent: true, localProvidersOverride: updatedLocalProviders });
            }
          }
          toast.push({ kind: "success", message: `Provider refreshed — ${targetRowCount(nodeId, nodes).toLocaleString()} rows` });
        },
        onFailed: (error) => {
          toast.push({ kind: "error", message: error ?? "Run failed." });
        },
        onFailure: (err) => {
          setRunStatus([nodeId], "error");
          toast.push({ kind: "error", message: err instanceof Error ? err.message : String(err) });
        },
      });
    } finally {
      setNodeCacheRefreshBusy(false);
    }
  }

  // ── Transform functions ─────────────────────────────────────────────────────

  function parseTransformJson(json: string): { value: TransformSpec | null; error: string | null } {
    const trimmed = json.trim();
    if (trimmed === "") return { value: null, error: null };
    try { return { value: JSON.parse(trimmed) as TransformSpec, error: null }; }
    catch { return { value: null, error: "Invalid transform JSON" }; }
  }

  async function ensureTransformSession(nodeId: string): Promise<string> {
    if (transformSessionId) return transformSessionId;
    const data = await apiFetch<{ session: { id: string } }>(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/transform/sessions`,
      { method: "POST", body: {} }
    );
    setTransformSessionId(data.session.id);
    transformSessionIdRef.current = data.session.id;
    return data.session.id;
  }

  // The server's catch persists a "Canceled by you" assistant message and the
  // original send POST resolves 200 with it — no client-side abort needed.
  async function cancelTransformChat() {
    const sid = transformSessionIdRef.current;
    if (!sid) return;
    try {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    } catch (e) {
      const nid = selectedNodeIdRef.current;
      if (nid) setTransformRunErrorByNode((p) => ({ ...p, [nid]: e instanceof Error ? e.message : String(e) }));
    }
  }

  async function sendTransformPrompt(nodeId: string, message: string, onRestorePrompt: (msg: string) => void) {
    setTransformSending(true);
    setTransformRunErrorByNode((p) => ({ ...p, [nodeId]: null }));
    setTransformMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: "user", text: message }]);
    try {
      const sid = await ensureTransformSession(nodeId);
      const currentSpec = parseTransformJson(transformJson).value ?? { specVersion: "transform.v1", output: "table", ops: [] };
      const data = await apiFetch<{
        assistantMessage: { id: string; displayText: string };
        transformSpec?: TransformSpec;
        meta?: { persistableTransformSpec?: TransformSpec };
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/transform/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          body: {
            message,
            currentTransformSpec: currentSpec,
            interactionMode: transformInteractionMode,
            ...(selectedAiModelId ? { model: selectedAiModelId } : {})
          }
        }
      );
      setTransformMessages((prev) => [...prev, { id: data.assistantMessage.id, role: "assistant", text: data.assistantMessage.displayText }]);
      const newSpec = data.meta?.persistableTransformSpec ?? data.transformSpec ?? null;
      if (newSpec) {
        const newJson = JSON.stringify(newSpec, null, 2);
        setTransformJson(newJson);
        setTransformJsonError(null);
        if (newSpec.specVersion === "transform.v2") setTransformSql(newSpec.sql);
        else setTransformSql("");
        // Persist spec_transform into the node config so it saves with the operation
        setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: newSpec, mode: "passthrough" } }));
        queueAutoSave(250);
        // Auto-run the transform preview with the fresh spec
        void runTransformPreview(nodeId, newSpec);
      }
    } catch (e) {
      if (isAiUsageLimitError(e)) {
        toast.push({ kind: "error", message: e.message });
        setAiChatCooldownUntil(Date.now() + AI_USAGE_LIMIT_COOLDOWN_MS);
        onRestorePrompt(message);
        setTransformMessages((prev) =>
          prev.length > 0 && prev[prev.length - 1]?.id.startsWith("local-") ? prev.slice(0, -1) : prev
        );
      } else {
        setTransformRunErrorByNode((p) => ({ ...p, [nodeId]: e instanceof Error ? e.message : String(e) }));
      }
    } finally {
      setTransformSending(false);
    }
  }

  function resetTransformChat() {
    setTransformSessionId(null);
    transformSessionIdRef.current = null;
    setTransformMessages([]);
    const nid = selectedNodeIdRef.current;
    if (nid) setTransformRunErrorByNode((p) => ({ ...p, [nid]: null }));
  }

  async function runTransformPreview(nodeId: string, specOverride?: TransformSpec, forceRefreshOverride?: boolean) {
    const useForce = forceRefreshOverride ?? transformForceRefresh;
    setTransformRunBusyByNode((p) => ({ ...p, [nodeId]: true }));
    setTransformRunErrorByNode((p) => ({ ...p, [nodeId]: null }));

    if (specOverride) {
      setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: specOverride, mode: "passthrough" } }));
    }
    const saved = await flushAndSave();
    if (!saved) { setTransformRunBusyByNode((p) => ({ ...p, [nodeId]: false })); return; }

    const affectedIds = useForce ? getLineageNodeIds(nodeId) : [nodeId];
    setRunStatus(affectedIds, "running");

    try {
      await runNodeAsync(nodeId, {
        forceRefresh: useForce,
        onSucceeded: async (_run, nodes) => {
          const target = nodes.find((n) => n.node_id === nodeId) ?? null;
          // Render from the landed cache preview (transforms land results per
          // ADR-0120 — this also makes the Data tab survive reloads). Stats
          // (runAt / duration / rows) come from the polled target node row.
          const resultPayload = await loadNodeCache(nodeId, true);
          if (resultPayload) {
            setTransformPreviewByNode((p) => ({ ...p, [nodeId]: resultPayload }));
            setTransformStatsByNode((p) => ({
              ...p,
              [nodeId]: {
                runAt: target?.finished_at ?? null,
                durationMs: target?.duration_ms ?? null,
                rowCount: target?.row_count ?? resultPayload.rowCount,
              },
            }));
            // Jump the spine to Data (and flip its sub-tab) only when the run's
            // node is still on screen; otherwise write silently into keyed state
            // so a background run never moves another node's inspector (2.2c-async).
            if (selectedNodeIdRef.current === nodeId) {
              setTransformSubTab("data");
              setTransformActiveSlot("data");
            }
          } else {
            setTransformStatsByNode((p) => ({
              ...p,
              [nodeId]: {
                runAt: target?.finished_at ?? null,
                durationMs: target?.duration_ms ?? null,
                rowCount: target?.row_count ?? 0,
              },
            }));
          }
          if (specOverride) {
            setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: specOverride, mode: "passthrough" } }));
          }
        },
        onFailed: (error) => {
          setTransformRunErrorByNode((p) => ({ ...p, [nodeId]: error ?? "Run failed." }));
          setRunStatus(affectedIds, "idle");
          setRunStatus([nodeId], "error");
        },
        onFailure: (err) => {
          setTransformRunErrorByNode((p) => ({ ...p, [nodeId]: err instanceof Error ? err.message : String(err) }));
          setRunStatus(affectedIds, "idle");
          setRunStatus([nodeId], "error");
        },
      });
    } finally {
      setTransformRunBusyByNode((p) => ({ ...p, [nodeId]: false }));
    }
  }

  function handleTransformJsonChange(nodeId: string, value: string) {
    setTransformJson(value);
    const { value: parsed, error } = parseTransformJson(value);
    setTransformJsonError(error);
    if (parsed) {
      setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: parsed, mode: "passthrough" } }));
      if (parsed.specVersion === "transform.v2") setTransformSql(parsed.sql);
      else setTransformSql("");
    }
  }

  function handleTransformSqlChange(nodeId: string, sqlValue: string) {
    setTransformSql(sqlValue);
    if (!sqlValue.trim()) {
      clearTransformSpec(nodeId);
      setTransformJson("");
      return;
    }
    const currentSpec = parseTransformJson(transformJson).value;
    const newSpec: TransformSpecV2 = {
      specVersion: "transform.v2",
      output: (currentSpec?.output ?? "table") as "table" | "series",
      sql: sqlValue,
      ...(currentSpec?.series ? { series: currentSpec.series } : {})
    };
    const newJson = JSON.stringify(newSpec, null, 2);
    setTransformJson(newJson);
    setTransformJsonError(null);
    setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: newSpec, mode: "passthrough" } }));
    queueAutoSave(350);
  }

  function clearTransformSpec(nodeId: string) {
    setTransformJson("");
    setTransformSql("");
    setTransformJsonError(null);
    setTransformPreviewByNode((p) => ({ ...p, [nodeId]: null }));
    setTransformStatsByNode((p) => ({ ...p, [nodeId]: null }));
    setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_transform: null, mode: "passthrough" } }));
  }

  // ADR-0080: legacy `output.visualization` JSON-spec parser + AI-chat +
  // run + JSON-edit flow (parseVizJson / ensureVizSession / sendVizPrompt /
  // resetVizChat / runVisualizePreview / handleVizJsonChange / clearVizSpec)
  // removed. OperationInspectorReactiveViz / TanTable / KPI Card each manage
  // their own equivalents internally now.

  async function ensureSharedProviderLoaded(providerId: string): Promise<ProviderDefinitionDraft | null> {
    if (!providerId) return null;
    if (sharedProviderCache[providerId]) return sharedProviderCache[providerId];
    try {
      const result = await apiFetch<{ data_provider: DataProvider }>(`/api/tenant/data-providers/${encodeURIComponent(providerId)}`);
      const provider = result.data_provider;
      const draft: ProviderDefinitionDraft = {
        name: provider.name ?? "",
        description: provider.description ?? "",
        connector_id: provider.connector_id ?? "",
        source_spec: provider.source_spec ?? { specVersion: "source.v1", kind: "sql", sql: "" },
        parameters: provider.parameters ?? [],
        schema_contract: provider.last_source_result?.columns ? { columns: provider.last_source_result.columns } : null,
        source_sql_locked: Boolean(provider.source_sql_locked),
        schema_locked: Boolean(provider.schema_locked)
      };
      setSharedProviderCache((prev) => ({ ...prev, [providerId]: draft }));
      return draft;
    } catch {
      return null;
    }
  }

  async function detachSharedProviderForNode(nodeId: string, sharedProviderId: string): Promise<string | null> {
    const shared = await ensureSharedProviderLoaded(sharedProviderId);
    const localProviderId = crypto.randomUUID();
    const baseDraft = shared ?? createDefaultLocalProviderDraft(defaultLocalConnectorId, "Detached provider");
    setLocalProviders((prev) => ({
      ...prev,
      [localProviderId]: {
        ...baseDraft
      }
    }));
    setNodeData(nodeId, (data) => ({
      ...data,
      config: {
        ...data.config,
        provider: { type: "local", local_provider_id: localProviderId }
      }
    }));
    return localProviderId;
  }

  async function updateMaterialProviderDraft(nodeId: string, updater: (draft: ProviderDefinitionDraft) => ProviderDefinitionDraft) {
    const node = nodes.find((entry) => entry.id === nodeId);
    if (!node) return;
    const providerRef = node.data.config.provider;
    if (!providerRef || typeof providerRef !== "object" || Array.isArray(providerRef)) return;
    const rec = providerRef as Record<string, unknown>;

    if (rec.type === "shared") {
      const providerId = String(rec.provider_id ?? "");
      const sharedDraft = await ensureSharedProviderLoaded(providerId);
      if (!sharedDraft) return;
      const before = materialDefinitionSnapshot(sharedDraft);
      const after = materialDefinitionSnapshot(updater({ ...sharedDraft }));
      if (before === after) return;
      const shouldDetach = await confirm.confirm({
        title: "Detach shared provider?",
        detail: "Editing SQL, parameters, connector, or query fields detaches this node from the shared provider and creates a local copy.",
        confirmLabel: "Detach and edit"
      });
      if (!shouldDetach) return;
      const newLocalId = await detachSharedProviderForNode(nodeId, providerId);
      if (!newLocalId) return;
      setLocalProviders((prev) => {
        const current = prev[newLocalId] ?? createDefaultLocalProviderDraft(defaultLocalConnectorId, "Local provider");
        return { ...prev, [newLocalId]: updater(current) };
      });
      return;
    }

    if (rec.type === "local") {
      const localId = String(rec.local_provider_id ?? "");
      if (!localId) return;
      setLocalProviders((prev) => {
        const current = prev[localId] ?? createDefaultLocalProviderDraft(defaultLocalConnectorId, "Local provider");
        return { ...prev, [localId]: updater(current) };
      });
    }
  }

  /** ADR-0147: per-column Domain override on an in-op node (transform / local
   *  provider / plex). Persists the config change by PATCHing the spec
   *  DIRECTLY (addNode's inline pattern — the dirtyRef indirection races when
   *  a save must be guaranteed within the same gesture), then calls the
   *  retype endpoint, which reads the PERSISTED spec and re-types the node's
   *  cached output in place (no source re-pull). Diagnostics are ephemeral. */
  async function changeNodeColumnDomain(
    nodeId: string,
    columnName: string,
    domain: ColumnDomain | null
  ) {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const currentRaw = (node.data.config as Record<string, unknown>).column_type_overrides;
    const priorMap =
      currentRaw && typeof currentRaw === "object" && !Array.isArray(currentRaw)
        ? { ...(currentRaw as Record<string, string>) }
        : {};
    const current = nextOverrideMap(priorMap, columnName, domain);

    // Build + PATCH the operation spec with a given override map (addNode's
    // inline pattern — the dirtyRef indirection races when a save must be
    // guaranteed within this gesture). Used for the forward write and the
    // revert on a validation rejection.
    const patchWithOverrides = async (overrideMap: Record<string, string>) => {
      const nextNodes = nodes.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, config: { ...n.data.config, column_type_overrides: overrideMap } } }
          : n
      );
      const nodeIdsForEdgeFilter = new Set(nextNodes.map((n) => n.id));
      const validEdges = edges.filter(
        (e) => nodeIdsForEdgeFilter.has(e.source) && nodeIdsForEdgeFilter.has(e.target)
      );
      const nextTags = normalizeTagsFromInput(tagsText);
      const inlineSpec: OperationSpecV1 = {
        ...defaultSpec(status, nextTags),
        status,
        tags: nextTags,
        windows: operationWindows,
        variables: operationVariables,
        graph: {
          nodes: nextNodes.map(toOperationNode),
          edges: validEdges.map((edge) => canvasEdgeToOperationEdge(edge, nextNodes)),
        },
        local_providers: localProviders,
      };
      const patched = await patchOperationSpec<{ operation: Operation }>({
        name: name.trim(),
        description: description.trim() || null,
        status,
        tags: nextTags,
        spec: inlineSpec,
      });
      // ADR-0159 — the response carries the next token. Before this, the retype
      // and add-node paths synced only `savedSnapshot`, so the tab kept editing
      // against a version it had itself superseded.
      rememberToken(patched.operation.updated_at);
    };

    // React state for immediate UI feedback.
    setNodeData(nodeId, (data) => ({
      ...data,
      config: { ...data.config, column_type_overrides: current },
    }));
    setColumnRetypeBusy((prev) => ({ ...prev, [nodeId]: true }));
    try {
      await patchWithOverrides(current);

      const res = await apiFetch<{
        retyped: boolean;
        output_schema: Array<{ name: string; type: string }> | null;
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/retype`,
        { method: "POST" }
      );

      const schemaByName = new Map((res.output_schema ?? []).map((c) => [c.name, c.type]));
      if (res.retyped && schemaByName.size > 0) {
        // Re-render the schema surfaces from the corrected types (no re-pull).
        setTransformPreviewByNode((prev) => {
          const p = prev[nodeId];
          if (!p) return prev;
          return {
            ...prev,
            [nodeId]: {
              ...p,
              columns: p.columns.map((c) =>
                schemaByName.has(c.name) ? { ...c, type: schemaByName.get(c.name)! } : c
              ),
            },
          };
        });
        setNodeOutputs((prev) => {
          const rec = prev[nodeId];
          if (!rec) return prev;
          return { ...prev, [nodeId]: { ...rec, outputSchema: res.output_schema ?? rec.outputSchema } };
        });
        const pk = providerKeyForNode(nodeId);
        if (pk?.startsWith("local:")) {
          const localId = pk.slice("local:".length);
          setLocalProviders((prev) => {
            const draft = prev[localId];
            if (!draft?.schema_contract) return prev;
            return {
              ...prev,
              [localId]: {
                ...draft,
                schema_contract: {
                  columns: draft.schema_contract.columns.map((c) =>
                    schemaByName.has(c.name) ? { ...c, type: schemaByName.get(c.name)! } : c
                  ),
                },
              },
            };
          });
        }
      }
    } catch (e) {
      // ADR-0147 validate-before-convert: revert the node config to its prior
      // override map (both React state and the persisted spec), then explain.
      setNodeData(nodeId, (data) => ({
        ...data,
        config: { ...data.config, column_type_overrides: priorMap },
      }));
      try {
        await patchWithOverrides(priorMap);
      } catch {
        /* best-effort revert-persist; the local state is already reverted */
      }
      if (e instanceof ApiRequestError && e.code === "type_validation_failed" && domain) {
        const failures = (e.data?.failures ?? {}) as TypeValidationFailures;
        toast.push({ kind: "error", message: typeValidationMessage(failures, domain) });
      } else {
        toast.push({
          kind: "error",
          message: e instanceof Error ? e.message : "Could not save the column type change.",
        });
      }
    } finally {
      setColumnRetypeBusy((prev) => ({ ...prev, [nodeId]: false }));
    }
  }

  async function promoteSelectedLocalProvider() {
    if (!selectedNode || !selectedProviderRef || selectedProviderRef.type !== "local") return;
    const local = localProviders[selectedProviderRef.local_provider_id];
    if (!local) return;
    // ADR-0085 Phase 2: promote only applies to SQL providers in Phase 7.
    // Plex local→shared promotion can land later via the Data Provider editor.
    const sqlSource = "sql" in local.source_spec ? local.source_spec : null;
    if (!sqlSource || !local.name.trim() || !sqlSource.sql.trim() || !local.connector_id) {
      toast.push({ kind: "error", message: "Name, connector, and SQL are required before promoting." });
      return;
    }
    try {
      const result = await apiFetch<{ data_provider: { id: string; name: string } }>("/api/tenant/data-providers", {
        method: "POST",
        body: {
          name: local.name.trim(),
          description: local.description?.trim() || null,
          connector_id: local.connector_id,
          source_spec: local.source_spec,
          parameters: local.parameters
        }
      });
      const sharedId = result.data_provider.id;
      setNodeData(selectedNode.id, (data) => ({
        ...data,
        config: {
          ...data.config,
          provider: { type: "shared", provider_id: sharedId }
        }
      }));
      setSharedProviderCache((prev) => ({ ...prev, [sharedId]: { ...local, source_sql_locked: false } }));
      toast.push({ kind: "success", message: "Promoted to shared provider." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function executeSelectedProviderSql() {
    if (!selectedProviderDraft || !selectedProviderKey || !selectedNode) return;
    const nodeId = selectedNode.id;
    const providerKey = selectedProviderKey;
    const sqlSource = "sql" in selectedProviderDraft.source_spec ? selectedProviderDraft.source_spec : null;
    const rawSql = sqlSource?.sql?.trim() ?? "";
    if (!rawSql) {
      toast.push({ kind: "error", message: "SQL is required to execute." });
      return;
    }

    // Save spec first so execute-preview reads the latest SQL and bindings
    const saved = await flushAndSave();
    if (!saved) return;

    setRunStatus([nodeId], "running");
    setProviderPreviewByKey((prev) => ({
      ...prev,
      [providerKey]: { loading: true, error: null, result: prev[providerKey]?.result ?? null }
    }));
    await runNodeAsync(nodeId, {
      forceRefresh: true,
      onSucceeded: async (_run, nodes) => {
        // Force-reload cache (bypasses stale-closure guard) — this capped
        // preview is what renders in the grid.
        const freshPayload = await loadNodeCache(nodeId, true);
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: null, result: freshPayload }
        }));
        if (freshPayload && freshPayload.columns.length > 0 && selectedProviderRef?.type === "local") {
          const lid = selectedProviderRef.local_provider_id;
          const currentDraft = localProviders[lid];
          if (currentDraft) {
            const updatedLocalProviders = { ...localProviders, [lid]: { ...currentDraft, schema_contract: { columns: freshPayload.columns } } };
            setLocalProviders(updatedLocalProviders);
            await save(undefined, { silent: true, localProvidersOverride: updatedLocalProviders });
          }
        }
        toast.push({ kind: "success", message: `Execute complete — ${targetRowCount(nodeId, nodes).toLocaleString()} rows` });
      },
      onFailed: (error) => {
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: error ?? "Run failed.", result: prev[providerKey]?.result ?? null }
        }));
      },
      onCancelled: () => {
        // Run stopped (Stop button / another session) — not a failure.
        toast.push({ kind: "success", message: "Run stopped." });
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: null, result: prev[providerKey]?.result ?? null }
        }));
      },
      onFailure: (err) => {
        const message = err instanceof Error ? err.message : String(err);
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: message, result: prev[providerKey]?.result ?? null }
        }));
        void loadNodeRunHistory(nodeId);
        setRunStatus([nodeId], "error");
      },
    });
  }

  /**
   * ADR-0085 Phase 2: Plex API equivalent of executeSelectedProviderSql.
   * Saves the spec, then triggers execute-preview against the node so the
   * server-side dispatcher (`execChainPlexApi`) resolves bindings, substitutes
   * `{:param_name}` tokens in the inputs_template, calls Plex, and writes the
   * cache row. Updates the local provider's schema_contract on success so the
   * Schema tab populates.
   */
  async function executeSelectedPlexRequest() {
    if (!selectedNode) {
      toast.push({ kind: "error", message: "Select a node first." });
      return;
    }
    if (!selectedProviderDraft || !selectedProviderKey) {
      toast.push({ kind: "error", message: "Plex node is not fully configured — switch the Provider source to Local and pick an integration." });
      return;
    }
    const nodeId = selectedNode.id;
    const providerKey = selectedProviderKey;
    const plexSrc =
      "datasource_id" in selectedProviderDraft.source_spec
        ? (selectedProviderDraft.source_spec as { datasource_id?: number })
        : null;
    if (!selectedProviderDraft.integration_id) {
      toast.push({ kind: "error", message: "Select a Plex integration first." });
      return;
    }
    if (!plexSrc || !plexSrc.datasource_id || plexSrc.datasource_id <= 0) {
      toast.push({ kind: "error", message: "Datasource ID must be a positive integer." });
      return;
    }

    // flushAndSave runs save() with `silent: true`, which suppresses the
    // validation-failed toast. If save fails, surface the validation issues
    // explicitly so Run never silently aborts.
    const saved = await flushAndSave();
    if (!saved) {
      const errIssues = issues.filter((i) => i.level === "error");
      const first = errIssues[0];
      const detail = first ? `${first.path}: ${first.message}` : "spec validation failed";
      toast.push({
        kind: "error",
        message: `Save blocked — fix validation issues before running (${detail}${errIssues.length > 1 ? `, +${errIssues.length - 1} more` : ""}).`
      });
      return;
    }

    setRunStatus([nodeId], "running");
    setProviderPreviewByKey((prev) => ({
      ...prev,
      [providerKey]: { loading: true, error: null, result: prev[providerKey]?.result ?? null }
    }));
    await runNodeAsync(nodeId, {
      forceRefresh: true,
      onSucceeded: async (_run, nodes) => {
        // The grid renders the /cache preview.
        const freshPayload = await loadNodeCache(nodeId, true);
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: null, result: freshPayload }
        }));
        if (freshPayload && freshPayload.columns.length > 0 && selectedProviderRef?.type === "local") {
          const lid = selectedProviderRef.local_provider_id;
          const currentDraft = localProviders[lid];
          if (currentDraft) {
            const updatedLocalProviders = { ...localProviders, [lid]: { ...currentDraft, schema_contract: { columns: freshPayload.columns } } };
            setLocalProviders(updatedLocalProviders);
            await save(undefined, { silent: true, localProvidersOverride: updatedLocalProviders });
          }
        }
        toast.push({ kind: "success", message: `Run complete — ${targetRowCount(nodeId, nodes).toLocaleString()} rows` });
      },
      onFailed: (error) => {
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: error ?? "Run failed.", result: prev[providerKey]?.result ?? null }
        }));
      },
      onCancelled: () => {
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: null, result: prev[providerKey]?.result ?? null }
        }));
      },
      onFailure: (err) => {
        const message = err instanceof Error ? err.message : String(err);
        setProviderPreviewByKey((prev) => ({
          ...prev,
          [providerKey]: { loading: false, error: message, result: prev[providerKey]?.result ?? null }
        }));
        void loadNodeRunHistory(nodeId);
        setRunStatus([nodeId], "error");
      },
    });
  }

  /** Place a new node near the center of the visible canvas, offset if that spot overlaps existing nodes. */
  function computeNewNodePosition(existing: CanvasNode[]): { x: number; y: number } {
    const idx = existing.length;
    const fallback = { x: 80 + (idx % 4) * 220, y: 80 + Math.floor(idx / 4) * 140 };
    const wrap = reactFlowViewportRef.current;
    const rf = reactFlowInstanceRef.current;
    if (!wrap || !rf) return fallback;
    const rect = wrap.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 40) return fallback;

    const centerFlow = rf.screenToFlowPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    });

    const NODE_W = 200;
    const NODE_H = 100;
    const MARGIN = 16;
    const overlaps = (x: number, y: number) =>
      existing.some((n) => {
        const nx = n.position.x;
        const ny = n.position.y;
        return (
          x + NODE_W + MARGIN > nx - MARGIN &&
          x - MARGIN < nx + NODE_W + MARGIN &&
          y + NODE_H + MARGIN > ny - MARGIN &&
          y - MARGIN < ny + NODE_H + MARGIN
        );
      });

    const baseX = centerFlow.x - NODE_W / 2;
    const baseY = centerFlow.y - NODE_H / 2;
    const STEP_X = 48;
    const STEP_Y = 44;

    // Search for the free slot closest to the viewport center. Candidates are
    // generated over a grid around center, then visited in order of true
    // distance from center — so the nearest open space wins regardless of
    // direction. (A row-by-row sweep would exhaust an entire row horizontally
    // before stepping down, drifting new nodes far to the right/left of a
    // left-to-right pipeline instead of dropping them just below center.)
    const SPAN = 24;
    const candidates: { x: number; y: number; dist: number }[] = [];
    for (let dy = -SPAN; dy <= SPAN; dy++) {
      for (let dx = -SPAN; dx <= SPAN; dx++) {
        const ox = dx * STEP_X;
        const oy = dy * STEP_Y;
        candidates.push({ x: baseX + ox, y: baseY + oy, dist: ox * ox + oy * oy });
      }
    }
    candidates.sort((a, b) => a.dist - b.dist);
    for (const c of candidates) {
      if (!overlaps(c.x, c.y)) return { x: Math.round(c.x), y: Math.round(c.y) };
    }
    return { x: Math.round(baseX + idx * STEP_X), y: Math.round(baseY + idx * STEP_Y) };
  }

  async function addNode(type: NodeType) {
    // ADR-0092 fix: the prior code (setNodes + void flushAndSave) and even
    // the flushSync + saveRef version both went through the localSpecRef /
    // dirtyRef indirection. That indirection is updated DURING a React
    // render, which races with whatever async scheduling fires the actual
    // POST — so the autosave routinely either bailed (dirtyRef stale=false)
    // or sent a stale spec missing the new node. Result: drop a persist
    // node, navigate away, the node was gone on reload.
    //
    // Skip the indirection entirely. Build the new node, push to React
    // state for visual feedback, build the operation spec INLINE from the
    // closure-captured state plus the new node, and POST it directly.
    const idx = nodes.length;
    let config = defaultNodeConfig(type);
    let providerSeed: { id: string; draft: ReturnType<typeof createDefaultLocalProviderDraft> } | null = null;
    if (type === "provider") {
      const localProviderId = crypto.randomUUID();
      providerSeed = {
        id: localProviderId,
        draft: createDefaultLocalProviderDraft(defaultLocalConnectorId, `${NODE_LABELS[type]} ${idx + 1}`),
      };
      config = { ...config, provider: { type: "local", local_provider_id: localProviderId } };
    } else if (type === "plex_api") {
      // ADR-0085 Phase 2: seed a Plex local-provider draft. Auto-select the
      // sole integration when there's just one; otherwise leave blank for the
      // user to pick in the inspector.
      const localProviderId = crypto.randomUUID();
      const autoIntegrationId = plexIntegrations.length === 1 ? plexIntegrations[0]!.id : null;
      providerSeed = {
        id: localProviderId,
        draft: createDefaultPlexLocalProviderDraft(autoIntegrationId, `${NODE_LABELS[type]} ${idx + 1}`),
      };
      config = { ...config, provider: { type: "local", local_provider_id: localProviderId } };
    }
    const newNode: CanvasNode = {
      id: crypto.randomUUID(),
      type: "operationNode",
      position: computeNewNodePosition(nodes),
      data: {
        nodeType: type,
        label: NODE_LABELS[type],
        config,
      },
    };

    // A loop is authored as a PAIR — that is the unit of thought, and a start
    // step on its own is a validation error the user would hit immediately. So
    // adding a Loop Start places its Loop End too, already paired, offset to
    // the right so the body has somewhere to go between them.
    const extraNodes: CanvasNode[] = [];
    if (type === "loop_start") {
      extraNodes.push({
        id: crypto.randomUUID(),
        type: "operationNode",
        position: { x: newNode.position.x + 460, y: newNode.position.y },
        data: {
          nodeType: "loop_end",
          label: NODE_LABELS.loop_end,
          config: { ...defaultNodeConfig("loop_end"), loop_start_node_id: newNode.id },
        },
      });
    }

    pushHistory();
    // 1. React state for visual feedback.
    setNodes((current) => [...current, newNode, ...extraNodes]);
    if (providerSeed) {
      setLocalProviders((prev) => ({ ...prev, [providerSeed!.id]: providerSeed!.draft }));
    }

    // 2. Build the spec inline from closure state + the new node, and POST
    //    directly. Bypass save()/dirty/localSpecRef so the new node is
    //    GUARANTEED to be in the POST body.
    const nextNodes = [...nodes, newNode, ...extraNodes];
    const nodeIdsForEdgeFilter = new Set(nextNodes.map((n) => n.id));
    const validEdges = edges.filter((e) => nodeIdsForEdgeFilter.has(e.source) && nodeIdsForEdgeFilter.has(e.target));
    const nextTags = normalizeTagsFromInput(tagsText);
    const nextLocalProviders = providerSeed ? { ...localProviders, [providerSeed.id]: providerSeed.draft } : localProviders;
    const inlineSpec: OperationSpecV1 = {
      ...defaultSpec(status, nextTags),
      status,
      tags: nextTags,
      windows: operationWindows,
      variables: operationVariables,
      graph: {
        nodes: nextNodes.map(toOperationNode),
        edges: validEdges.map((edge) => canvasEdgeToOperationEdge(edge, nextNodes)),
      },
      local_providers: nextLocalProviders,
    };

    async function postPatch(targetStatus: OperationStatus): Promise<Operation> {
      const result = await patchOperationSpec<{ operation: Operation }>({
        name: name.trim(),
        description: description.trim() || null,
        status: targetStatus,
        tags: nextTags,
        spec: { ...inlineSpec, status: targetStatus, tags: nextTags },
      });
      rememberToken(result.operation.updated_at);
      return result.operation;
    }

    let saved: Operation;
    try {
      saved = await postPatch(status);
    } catch (e) {
      // If the op is published and the new node introduces validation
      // errors, the server rejects with `validation_failed`. Retry as
      // 'invalid' — the spec is mid-edit and not in a publishable state by
      // definition. The user can re-publish after finishing the configuration.
      if (e instanceof ApiRequestError && e.code === "validation_failed" && status === "published") {
        try {
          saved = await postPatch("invalid");
          setStatus("invalid");
          toast.push({
            kind: "info",
            message: "Operation marked as invalid while you finish configuring this node — re-publish when ready.",
          });
        } catch (e2) {
          toast.push({
            kind: "error",
            message: `Couldn't save the new ${NODE_LABELS[type] ?? type} node: ${e2 instanceof Error ? e2.message : String(e2)}`,
          });
          return;
        }
      } else {
        toast.push({
          kind: "error",
          message: `Couldn't save the new ${NODE_LABELS[type] ?? type} node: ${e instanceof Error ? e.message : String(e)}`,
        });
        return;
      }
    }

    // Sync the savedSnapshot so a subsequent dirty-check sees this as the
    // current saved state.
    setSavedSnapshot(
      JSON.stringify({
        name: saved.name,
        description: saved.description ?? null,
        status: saved.status,
        tags: saved.tags ?? [],
        spec: parseSpecV1(saved.spec, saved.status, saved.tags ?? []),
      }),
    );
  }

  /** Pipeline shape key (node types + sorted connections). Ignores labels so refinements still match the stage map after Apply. */
  function recFingerprint(rec: PipelineRecommendation): string {
    const types = rec.stages.map((s) => s.nodeType);
    const connSorted = [...rec.connections].sort((a, b) => {
      const dFrom = a.from.stageIndex - b.from.stageIndex;
      if (dFrom !== 0) return dFrom;
      const dTo = a.to.stageIndex - b.to.stageIndex;
      if (dTo !== 0) return dTo;
      const dFp = a.from.port.localeCompare(b.from.port);
      if (dFp !== 0) return dFp;
      return a.to.port.localeCompare(b.to.port);
    });
    const connKey = connSorted.map(
      (c) => `${c.from.stageIndex}:${c.from.port}->${c.to.stageIndex}:${c.to.port}`
    );
    return JSON.stringify({ t: types, c: connKey });
  }

  /** Resolve canvas node id for a stage index: stage map first, else unique label+nodeType on canvas. */
  function resolveStageNodeId(
    indexMap: Map<number, string>,
    rec: PipelineRecommendation,
    stageIndex: number,
    canvasNodes: CanvasNode[],
    currentNodeIds: Set<string>
  ): string | undefined {
    const mapped = indexMap.get(stageIndex);
    if (mapped && currentNodeIds.has(mapped)) return mapped;

    const st = rec.stages[stageIndex];
    if (!st) return undefined;
    const matches = canvasNodes.filter(
      (n) =>
        n.data.label === st.label &&
        n.data.nodeType === (st.nodeType as NodeType) &&
        currentNodeIds.has(n.id)
    );
    if (matches.length === 1) return matches[0]!.id;
    return undefined;
  }

  /** When the architect wires specific AI output ports, align node config so Execute rules match wiring. */
  function aiProviderOutputModeFlagsFromConnections(
    rec: PipelineRecommendation,
    stageIndex: number
  ): { enable_structured: boolean; enable_unstructured: boolean } | null {
    const hasTabularOut = rec.connections.some(
      (c) => c.from.stageIndex === stageIndex && c.from.port === "tabular_out"
    );
    const hasContentOut = rec.connections.some(
      (c) => c.from.stageIndex === stageIndex && c.from.port === "content_out"
    );
    if (!hasTabularOut && !hasContentOut) return null;
    return {
      enable_structured: hasTabularOut,
      enable_unstructured: hasContentOut
    };
  }

  function buildStageNode(stage: PipelineRecommendation["stages"][0], id: string, position: { x: number; y: number }): CanvasNode {
    const nodeType = stage.nodeType as NodeType;
    const base = defaultNodeConfig(nodeType);
    let config: Record<string, unknown> = { ...base };
    if (nodeType === "ai_provider") {
      if (typeof stage.webSearchEnabled === "boolean") {
        const sc = (config["search_config"] as Record<string, unknown>) ?? {};
        config = { ...config, search_config: { ...sc, web_search_enabled: stage.webSearchEnabled } };
      }
      if (typeof stage.maxOutputTokens === "number" && stage.maxOutputTokens > 0) {
        const mt = (config["model_tuning"] as Record<string, unknown>) ?? {};
        config = { ...config, model_tuning: { ...mt, max_output_tokens: stage.maxOutputTokens } };
      }
    }
    // ADR-0116 §4.4 — overlay the architect's provisional model config onto the
    // default spec_model_train.config (retaining model_id + intent fields + provisional).
    if (nodeType === "model_train" && stage.nodeConfig) {
      const baseMT = (config["spec_model_train"] as Record<string, unknown>) ?? {};
      const baseCfg = (baseMT["config"] as Record<string, unknown>) ?? {};
      config = {
        ...config,
        spec_model_train: { ...baseMT, config: { ...baseCfg, ...stage.nodeConfig } },
      };
    }
    // ADR-0116 — predict/evaluate can't be bound at plan time (the model doesn't exist
    // until the train node is trained). Mark architect-created ones provisional so the
    // inspector surfaces a "bind the pipeline's model" review instead of a blank node.
    if (nodeType === "model_predict" || nodeType === "model_evaluate") {
      const key = nodeType === "model_predict" ? "spec_model_predict" : "spec_model_evaluate";
      const base = (config[key] as Record<string, unknown>) ?? {};
      config = { ...config, [key]: { ...base, provisional: true } };
    }
    return {
      id,
      type: "operationNode" as const,
      position,
      data: { nodeType, label: stage.label, description: stage.purpose ?? null, config }
    };
  }

  function buildStageEdge(
    fromNodeId: string,
    toNodeId: string,
    fromPort: string,
    toPort: string
  ): CanvasEdge {
    const et: EdgeType = fromPort.startsWith("scope") ? "scope" : "data";
    return {
      id: crypto.randomUUID(),
      source: fromNodeId,
      target: toNodeId,
      sourceHandle: fromPort,
      targetHandle: toPort,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { edge_type: et },
      animated: et === "scope",
      style: et === "scope"
        ? { stroke: "var(--op-port-scope)", strokeDasharray: "4 4" }
        : { stroke: "var(--op-port-tabular)" }
    };
  }

  function applyRecommendation(rec: PipelineRecommendation) {
    const NODE_W = 220;
    const NODE_H = 110;
    const COL_GAP = 80;
    const ROW_GAP = 40;

    const currentNodes = nodes; // captured from state at call time
    const currentEdges = edges;
    const currentNodeIds = new Set(currentNodes.map((n) => n.id));

    // Resolve which stages reuse existing canvas nodes vs need a fresh one.
    // A stage reuses an existing node when the architect set `existingNodeId`
    // to a real id present on the canvas. Bogus/stale ids are ignored.
    const idMap: string[] = rec.stages.map((s) =>
      s.existingNodeId && currentNodeIds.has(s.existingNodeId)
        ? s.existingNodeId
        : crypto.randomUUID()
    );
    const isExisting: boolean[] = rec.stages.map(
      (s) => !!(s.existingNodeId && currentNodeIds.has(s.existingNodeId))
    );

    // Compute topological depth (column) only over NEW stages — existing nodes
    // already have positions on the canvas.
    const depth: number[] = rec.stages.map(() => 0);
    let dirty = true;
    while (dirty) {
      dirty = false;
      for (const c of rec.connections) {
        if (isExisting[c.to.stageIndex]) continue;
        const next = depth[c.from.stageIndex]! + 1;
        if (next > depth[c.to.stageIndex]!) {
          depth[c.to.stageIndex] = next;
          dirty = true;
        }
      }
    }

    // Group only new stages into columns for layout
    const byDepth = new Map<number, number[]>();
    depth.forEach((d, i) => {
      if (isExisting[i]) return;
      const col = byDepth.get(d) ?? [];
      col.push(i);
      byDepth.set(d, col);
    });

    // Determine origin — right of existing nodes, or canvas center if empty
    const rf = reactFlowInstanceRef.current;
    const wrap = reactFlowViewportRef.current;
    let originX = 80;
    let originY = 80;
    if (currentNodes.length > 0) {
      originX = Math.max(...currentNodes.map((n) => n.position.x + NODE_W)) + COL_GAP;
      originY = currentNodes.reduce((s, n) => s + n.position.y, 0) / currentNodes.length;
    } else if (rf && wrap) {
      const rect = wrap.getBoundingClientRect();
      const center = rf.screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      const maxDepth = depth.length > 0 ? Math.max(...depth) : 0;
      originX = center.x - ((maxDepth * (NODE_W + COL_GAP)) / 2);
      originY = center.y;
    }

    // Assign canvas positions for new stages only
    const positions: Array<{ x: number; y: number } | null> = new Array(rec.stages.length).fill(null);
    byDepth.forEach((stageIdxs, d) => {
      const x = originX + d * (NODE_W + COL_GAP);
      const totalH = stageIdxs.length * NODE_H + (stageIdxs.length - 1) * ROW_GAP;
      stageIdxs.forEach((si, row) => {
        positions[si] = { x: Math.round(x), y: Math.round(originY - totalH / 2 + row * (NODE_H + ROW_GAP)) };
      });
    });

    const newNodes: CanvasNode[] = [];
    rec.stages.forEach((stage, i) => {
      if (isExisting[i]) return;
      let node = buildStageNode(stage, idMap[i]!, positions[i]!);
      if (stage.nodeType === "ai_provider") {
        const om = aiProviderOutputModeFlagsFromConnections(rec, i);
        if (om) {
          node = {
            ...node,
            data: { ...node.data, config: { ...node.data.config, ...om } }
          };
        }
      }
      newNodes.push(node);
    });

    // Build edges, skipping any that already exist on the canvas
    // (same source/target nodes AND same source/target ports).
    const edgeKey = (s: string, sp: string, t: string, tp: string) => `${s}|${sp}->${t}|${tp}`;
    const existingEdgeKeys = new Set(
      currentEdges.map((e) => edgeKey(e.source, e.sourceHandle ?? "", e.target, e.targetHandle ?? ""))
    );
    const newEdges: CanvasEdge[] = [];
    for (const c of rec.connections) {
      const sourceId = idMap[c.from.stageIndex]!;
      const targetId = idMap[c.to.stageIndex]!;
      const k = edgeKey(sourceId, c.from.port, targetId, c.to.port);
      if (existingEdgeKeys.has(k)) continue;
      existingEdgeKeys.add(k);
      newEdges.push(buildStageEdge(sourceId, targetId, c.from.port, c.to.port));
    }

    // Record stageIndex → nodeId so single-stage adds can wire connections later
    const fp = recFingerprint(rec);
    const indexMap = stageNodeMapRef.current.get(fp) ?? new Map<number, string>();
    idMap.forEach((nodeId, i) => indexMap.set(i, nodeId));
    stageNodeMapRef.current.set(fp, indexMap);

    pushHistory();
    setNodes((current) => [...current, ...newNodes]);
    setEdges((current) => [...current, ...newEdges]);
    void flushAndSave();
  }

  function applySingleStage(rec: PipelineRecommendation, stageIndex: number) {
    const stage = rec.stages[stageIndex];
    if (!stage) return;

    const currentNodes = nodes;
    const currentEdges = edges;
    const currentNodeIds = new Set(currentNodes.map((n) => n.id));

    // If the architect tagged this stage as an existing canvas node, there's
    // nothing to add — the node is already on the canvas.
    if (stage.existingNodeId && currentNodeIds.has(stage.existingNodeId)) return;

    const newId = crypto.randomUUID();

    // Place in the free space closest to the viewport center, same as a manual
    // palette add.
    let newNode = buildStageNode(stage, newId, computeNewNodePosition(currentNodes));
    if (stage.nodeType === "ai_provider") {
      const om = aiProviderOutputModeFlagsFromConnections(rec, stageIndex);
      if (om) {
        newNode = {
          ...newNode,
          data: { ...newNode.data, config: { ...newNode.data.config, ...om } }
        };
      }
    }

    // Resolve connections: existingNodeId first (architect-declared reuse),
    // then stage map (topology-keyed), then unique label+nodeType on canvas.
    const fp = recFingerprint(rec);
    const indexMap = stageNodeMapRef.current.get(fp) ?? new Map<number, string>();
    const resolveOther = (otherIndex: number): string | undefined => {
      const other = rec.stages[otherIndex];
      if (other?.existingNodeId && currentNodeIds.has(other.existingNodeId)) {
        return other.existingNodeId;
      }
      return resolveStageNodeId(indexMap, rec, otherIndex, currentNodes, currentNodeIds);
    };

    const edgeKey = (s: string, sp: string, t: string, tp: string) => `${s}|${sp}->${t}|${tp}`;
    const existingEdgeKeys = new Set(
      currentEdges.map((e) => edgeKey(e.source, e.sourceHandle ?? "", e.target, e.targetHandle ?? ""))
    );

    const newEdges: CanvasEdge[] = [];
    for (const c of rec.connections) {
      let sourceId: string | undefined;
      let targetId: string | undefined;
      if (c.from.stageIndex === stageIndex) {
        sourceId = newId;
        targetId = resolveOther(c.to.stageIndex);
      } else if (c.to.stageIndex === stageIndex) {
        sourceId = resolveOther(c.from.stageIndex);
        targetId = newId;
      } else {
        continue;
      }
      if (!sourceId || !targetId) continue;
      const k = edgeKey(sourceId, c.from.port, targetId, c.to.port);
      if (existingEdgeKeys.has(k)) continue;
      existingEdgeKeys.add(k);
      newEdges.push(buildStageEdge(sourceId, targetId, c.from.port, c.to.port));
    }

    // Update the map so subsequent single-stage adds connect to this new node
    indexMap.set(stageIndex, newId);
    stageNodeMapRef.current.set(fp, indexMap);

    pushHistory();
    setNodes((current) => [...current, newNode]);
    setEdges((current) => [...current, ...newEdges]);
    void flushAndSave();
  }

  function removeSelected() {
    if (selectedNode) {
      onNodesChange([{ type: "remove" as const, id: selectedNode.id }]);
      return;
    }
    if (selectedEdge) {
      onEdgesChange([{ type: "remove" as const, id: selectedEdge.id }]);
    }
  }

  function duplicateSelected() {
    if (!selectedNode) return;
    const sourceNode = selectedNode;
    const sourceId = sourceNode.id;
    const newId = crypto.randomUUID();

    const newConfig = JSON.parse(JSON.stringify(sourceNode.data.config ?? {})) as Record<string, unknown>;

    if (sourceNode.data.nodeType === "provider") {
      const cfg = newConfig as ProviderNodeConfig;
      if (cfg.provider?.type === "local" && cfg.provider.local_provider_id) {
        const sourceLocalId = cfg.provider.local_provider_id;
        const newLocalId = crypto.randomUUID();
        setLocalProviders((prev) => {
          const sourceDraft = prev[sourceLocalId];
          if (!sourceDraft) return prev;
          return { ...prev, [newLocalId]: JSON.parse(JSON.stringify(sourceDraft)) as ProviderDefinitionDraft };
        });
        cfg.provider = { type: "local", local_provider_id: newLocalId };
      }
    }

    if (sourceNode.data.nodeType === "persist") {
      const cfg = newConfig as { spec_persist?: { pdo_id: string | null } };
      if (cfg.spec_persist) cfg.spec_persist.pdo_id = null;
    }

    // ADR-0102: duplicate of a file_provider gets a fresh upload slot —
    // current_upload_id and column_mappings are pointer state, not config.
    if (sourceNode.data.nodeType === "file_provider") {
      const cfg = newConfig as { spec_file_provider?: Record<string, unknown> };
      if (cfg.spec_file_provider) {
        delete cfg.spec_file_provider.current_upload_id;
        cfg.spec_file_provider.column_mappings = {};
      }
    }

    const baseLabel = (sourceNode.data.label ?? "").trim() || NODE_LABELS[sourceNode.data.nodeType];
    const newLabel = `${baseLabel} (copy)`;

    const newPosition = {
      x: (sourceNode.position?.x ?? 0) + 40,
      y: (sourceNode.position?.y ?? 0) + 40
    };

    const newNode: CanvasNode = {
      id: newId,
      type: "operationNode",
      position: newPosition,
      data: {
        nodeType: sourceNode.data.nodeType,
        label: newLabel,
        description: sourceNode.data.description,
        config: newConfig
      },
      selected: true
    };

    const clonedIncomingEdges: CanvasEdge[] = edges
      .filter((e) => e.target === sourceId)
      .map((e) => ({ ...e, id: crypto.randomUUID(), target: newId, selected: false }));

    pushHistory();
    setNodes((current) => {
      const cleared = current.map((n) => (n.selected ? { ...n, selected: false } : n));
      return [...cleared, newNode];
    });
    if (clonedIncomingEdges.length > 0) {
      setEdges((current) => [...current, ...clonedIncomingEdges]);
    }
    ++saveVersionRef.current;
    void flushAndSave();
  }

  async function load() {
    setLoading(true);
    setError(null);
    setNotAvailable(false);
    try {
      const [operationData, connectorsData, providersData, integrationsData] = await Promise.all([
        apiFetch<{ operation: Operation; report_kept_steps?: Record<string, string[]> }>(`/api/tenant/operations/${encodeURIComponent(operationId)}`),
        apiFetch<{ connectors: Connector[] }>("/api/tenant/connectors"),
        apiFetch<{ data_providers: DataProvider[] }>("/api/tenant/data-providers"),
        // ADR-0085 Phase 2: ignore failure (e.g. non-admin); palette just hides Plex button.
        apiFetch<{ integrations: Array<{ id: string; name: string; kind: string }> }>("/api/tenant/admin/integrations?kind=plex_api").catch(
          () => ({ integrations: [] as Array<{ id: string; name: string; kind: string }> })
        )
      ]);
      const operation = operationData.operation;
      setConnectors(connectorsData.connectors);
      setProviders(providersData.data_providers);
      setPlexIntegrations(integrationsData.integrations.map((i) => ({ id: i.id, name: i.name })));
      setName(operation.name);
      setDescription(operation.description ?? "");
      setOperationMeta({
        createdAt: operation.created_at ?? null,
        updatedAt: operation.updated_at ?? null,
        ownerEmail: operation.owner_email ?? null
      });
      // ADR-0159 — the base every save from this tab will be measured against,
      // and a clean slate: whatever conflict this load resolves is over.
      operationTokenRef.current = operation.updated_at ?? null;
      setConflict(null);
      setStatus(operation.status);
      setStatusReason(operation.status_reason ?? null);
      setTagsText((operation.tags ?? []).join(", "));
      setKind(operation.kind);
      setNodeSchemaLocks(operation.node_schema_locks ?? {});
      setReportKeptSteps(operationData.report_kept_steps ?? {});

      const specRec = operation.spec && typeof operation.spec === "object" && !Array.isArray(operation.spec)
        ? (operation.spec as Record<string, unknown>)
        : null;
      if (!specRec || specRec.version !== "operation.v1") {
        setIncompatibleVersion(typeof specRec?.version === "string" ? specRec.version : "unknown");
        setNodes([]);
        setEdges([]);
        setLocalProviders({});
        setOperationWindows([]);
        setOperationVariables([]);
      } else {
        setIncompatibleVersion(null);
        const spec = parseSpecV1(operation.spec, operation.status, operation.tags ?? []);
        const targetNodeId = searchParams.get("node");
        // Deep-link to a specific node (e.g. from a Model's Source link). If the
        // node is gone (e.g. its model was deleted but the model row still
        // points here), say so instead of silently landing on the operation.
        const targetNodeExists = targetNodeId ? spec.graph.nodes.some((n) => n.id === targetNodeId) : true;
        setNodes(
          spec.graph.nodes.map((n) => ({
            ...toCanvasNode(n, operation.layout?.nodes),
            selected: targetNodeId ? n.id === targetNodeId : false
          }))
        );
        const nodeTypesById = new Map(spec.graph.nodes.map((n) => [n.id, n.type]));
        setEdges(
          spec.graph.edges.map((e) =>
            toCanvasEdge(e, nodeTypesById.get(e.from_node) ?? "", nodeTypesById.get(e.to_node) ?? "")
          )
        );
        setLocalProviders(
          normalizeLocalProvidersRecord(
            (spec.local_providers as Record<string, unknown>) ?? {},
            connectorsData.connectors[0]?.id ?? ""
          )
        );
        setOperationWindows(spec.windows ?? []);
        setOperationVariables(spec.variables ?? []);
        if (targetNodeId && !targetNodeExists) {
          toast.push({
            kind: "info",
            message: "That link points to a node that's no longer in this operation — it may have been deleted.",
          });
        }
      }

      // ADR-0153 rule 1 — findings ingest on LOAD, not behind a Validate
      // click: a door-authored draft must open showing what is wrong. The
      // fetch is silent (no toasts) and best-effort; a failure leaves the
      // previous findings rather than fabricating a clean state.
      void validateOperation({ silent: true });
      setGraphSummary(null);
      const snapshotSpec = specRec && specRec.version === "operation.v1" ? parseSpecV1(operation.spec, operation.status, operation.tags ?? []) : defaultSpec(operation.status, operation.tags ?? []);
      setSavedSnapshot(
        JSON.stringify({
          name: operation.name,
          description: operation.description ?? null,
          status: operation.status,
          tags: operation.tags ?? [],
          spec: snapshotSpec
        })
      );
    } catch (e) {
      if (isNotAvailable(e)) setNotAvailable(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!operationId) return;
    // ADR-0133 Amendment 12: the hub owns the canonical starting config for each
    // step type. Fetch it before `load()`, because rendering a stored node whose
    // config is missing falls back to that shape (`toCanvasNode`) — and a
    // door-authored bare step is exactly such a node. One small static request;
    // if it fails the editor still opens and new steps start empty, which the
    // validator reports like any other incomplete step.
    void (async () => {
      await loadNodeCreationShapes();
      await load();
    })();
    void loadNodeOutputs();
    // ADR-0116 — model name lookup for AI-config binding diffs (best-effort).
    void (async () => {
      try {
        const data = await apiFetch<{ models: Array<{ id: string; name: string }> }>("/api/tenant/models");
        setTenantModelsForNames(data.models ?? []);
      } catch {
        /* non-fatal — diffs fall back to "the selected model" */
      }
    })();
  }, [operationId]);

  // ADR-0159 §6 — the staleness check that turns most conflicts into nothing.
  //
  // Coming back to a tab is when it is most likely to be out of date and least
  // likely to have unsaved work. If the pipeline moved while this tab sat in
  // the background: a CLEAN tab just re-reads, silently, and the reader never
  // learns there was a race. A DIRTY tab gets the banner immediately — before
  // more work is invested on a base that is already gone — instead of at the
  // next save, which is how all three production clobbers played out.
  useEffect(() => {
    if (!operationId) return;
    async function checkForRemoteChange() {
      if (document.visibilityState === "hidden") return;
      if (busyRef.current || conflictRef.current || !operationTokenRef.current) return;
      try {
        const data = await apiFetch<{ operation: Operation }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}`
        );
        const remote = data.operation.updated_at;
        if (!remote || remote === operationTokenRef.current) return;
        if (dirtyRef.current) {
          setConflict({
            updatedAt: remote,
            actorLabel: data.operation.last_write_actor_label || "Someone",
            sourceDisplay: data.operation.last_write_source_display || "another surface",
            at: remote,
          });
        } else {
          await load();
        }
      } catch {
        // A failed check is not worth reporting: the save path still refuses a
        // stale write, so this is an early warning, never the guarantee.
      }
    }
    function onFocus() {
      void checkForRemoteChange();
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId]);

  useEffect(() => {
    function flushBeforeExit() {
      if (!dirtyRef.current || busyRef.current || conflictRef.current) return;
      void saveRef.current(undefined, { silent: true });
    }
    window.addEventListener("pagehide", flushBeforeExit);
    window.addEventListener("beforeunload", flushBeforeExit);
    return () => {
      window.removeEventListener("pagehide", flushBeforeExit);
      window.removeEventListener("beforeunload", flushBeforeExit);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current !== null) {
        window.clearTimeout(autoSaveTimeoutRef.current);
      }
      if (!dirtyRef.current || busyRef.current) return;
      void saveRef.current(undefined, { silent: true });
    };
  }, []);

  useEffect(() => {
    if (!selectedProviderRef || selectedProviderRef.type !== "shared") return;
    void ensureSharedProviderLoaded(selectedProviderRef.provider_id);
  }, [selectedProviderRef]);

  useEffect(() => {
    setProviderInspectorTab("metadata");
    setPlexInspectorTab("metadata");
    const t = selectedNode?.data.nodeType;

    // Model nodes: land on the Advisor (AI) slot when this selection came from an
    // Architect delegate; otherwise open on Setup like the other spine nodes.
    if (t === "model_train" || t === "model_predict" || t === "model_evaluate") {
      if (openAdvisorOnSelectRef.current && openAdvisorOnSelectRef.current === selectedNode?.id) {
        setModelInspectorSlot("ai");
        openAdvisorOnSelectRef.current = null;
      } else {
        setModelInspectorSlot("setup");
      }
    }

    // Sync the editor content to the newly-selected transform node. Per-node run
    // state (preview / stats / error) is preserved in its keyed map — NOT cleared
    // here — so switching nodes never blanks another node's result (2.2c-async).
    if (t === "transform") {
      const spec = (selectedNode?.data.config.spec_transform ?? null) as TransformSpec | null;
      setTransformJson(spec !== null ? JSON.stringify(spec, null, 2) : "");
      setTransformSql(spec?.specVersion === "transform.v2" ? spec.sql : "");
      setTransformJsonError(null);
      setTransformSubTab("data");
      setTransformActiveSlot("setup");
      // Reload existing AI transform chat session
      const nid = selectedNode!.id;
      setTransformSessionId(null);
      setTransformMessages([]);
      (async () => {
        try {
          const data = await apiFetch<{ session: { id: string }; messages: Array<{ id: string; role: "user" | "assistant"; content: string }> }>(
            `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nid)}/ai/transform/sessions/latest`
          );
          setTransformSessionId(data.session.id);
          setTransformMessages(data.messages.map((m) => ({ id: m.id, role: m.role, text: m.content })));
        } catch {
          // no existing session — start fresh
        }
      })();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id]);

  // Auto-load run history whenever a node with a history tab is selected —
  // the History tab no longer has a manual Refresh affordance. Reloads after
  // a Run happen via explicit loadNodeRunHistory() calls in the Run handlers.
  useEffect(() => {
    if (!selectedNode) return;
    const nt = selectedNode.data.nodeType;
    if (nt === "provider" || nt === "plex_api" || nt === "transform") {
      void loadNodeRunHistory(selectedNode.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id]);

  useEffect(() => {
    const el = transformChatScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [transformMessages]);

  // (ADR-0080: vizChatScrollRef effect deleted with OperationInspectorVisualization.)

  // Lazy-load provider cache when Cache tab is opened
  useEffect(() => {
    const nid = selectedNode?.id;
    if (!nid) return;
    const t = selectedNode?.data.nodeType;
    if (t === "provider" && providerInspectorTab === "cache") {
      void loadNodeCache(nid);
    }
    if (t === "plex_api" && plexInspectorTab === "cache") {
      void loadNodeCache(nid);
    }
    if (t === "persistent_provider") {
      void loadNodeCache(nid);
    }
    // ADR-0172: same posture as persistent_provider — load on selection (the
    // inspector's Data tab renders whatever is here; missed in the initial
    // build, which showed run stats with a phantom "no cached data").
    if (t === "platform_provider") {
      void loadNodeCache(nid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerInspectorTab, plexInspectorTab, selectedNode?.id]);

  // Lazy-load a transform node's cached preview on selection (per-node keyed) so
  // the Data slot shows the last result without a manual Run.
  useEffect(() => {
    const nid = selectedNode?.id;
    if (!nid) return;
    if (selectedNode?.data.nodeType === "transform" && transformPreviewByNode[nid] == null) {
      void (async () => {
        const payload = await loadNodeCache(nid);
        if (payload) {
          setTransformPreviewByNode((p) => ({ ...p, [nid]: payload }));
          const rec = nodeOutputs[nid];
          setTransformStatsByNode((p) => ({ ...p, [nid]: { runAt: rec?.runAt ?? null, durationMs: rec?.durationMs ?? null, rowCount: payload.rowCount } }));
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id]);

  // (ADR-0080: legacy viz render-model lazy-load deleted — tantable/kpi_card
  // inspectors manage their own render-model state internally via the cache
  // endpoint; the parent-level pre-fetch was only ever read by the deleted
  // OperationInspectorVisualization.)


  // 2026-08-22 phantom-banner fix — spec saves are single-flight. The ADR-0159
  // token is learned from a save's RESPONSE (rememberToken below), so a second
  // save started mid-flight carried a stale token, the hub refused it, and the
  // conflict banner named this tab's own write from milliseconds earlier. Any
  // two of the many save triggers (350ms autosave, select-change flush, remove
  // flush, undo/redo flush, blur handlers) inside one request's latency raced.
  // A caller arriving mid-flight now waits; a silent caller whose work the
  // predecessor already wrote short-circuits instead of re-saving.
  const saveFlightRef = useRef(createSingleFlight<boolean>());
  function save(statusOverride?: OperationStatus, options?: SaveOptions): Promise<boolean> {
    return saveFlightRef.current.run(() => doSave(statusOverride, options), {
      skipAfterWait: () =>
        statusOverride === undefined && options?.silent && !dirtyRef.current ? true : null,
    });
  }

  async function doSave(statusOverride?: OperationStatus, options?: SaveOptions): Promise<boolean> {
    const selectedNodeIdBeforeSave = selectedNode?.id ?? null;
    const selectedEdgeIdBeforeSave = selectedEdge?.id ?? null;
    const providerInspectorTabBeforeSave = providerInspectorTab;
    if (!name.trim()) {
      if (!options?.silent) toast.push({ kind: "error", message: "Name is required" });
      return false;
    }
    if (incompatibleVersion) {
      toast.push({ kind: "error", message: "This operation is not operation.v1. Delete it and create a new operation." });
      return false;
    }

    const effectiveStatus = statusOverride ?? status;
    const thisSaveVersion = ++saveVersionRef.current;
    setBusy(true);
    try {
      const nextTags = normalizeTagsFromInput(tagsText);
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        status: effectiveStatus,
        tags: nextTags,
        spec: { ...localSpecRef.current, ...(options?.localProvidersOverride ? { local_providers: options.localProvidersOverride } : {}), status: effectiveStatus, tags: nextTags }
      };
      const result = await patchOperationSpec<{ operation: Operation; issues?: ValidationIssue[] }>(payload);
      // ADR-0153 rule 1 — the server validates every save; the findings ride
      // the 200 and are ingested here, so the channel is current after every
      // save with no manual gesture.
      if (result.issues !== undefined) {
        setIssues(result.issues);
        setValidationRan(true);
      }

      const nextOperation = result.operation;
      setName(nextOperation.name);
      setDescription(nextOperation.description ?? "");
      setStatus(nextOperation.status);
      setStatusReason(nextOperation.status_reason ?? null);
      setTagsText((nextOperation.tags ?? []).join(", "));
      setKind(nextOperation.kind);
      rememberToken(nextOperation.updated_at);

      // Rehydrate the graph from the server response only for explicit (non-silent)
      // saves.  Silent/auto-saves treat local state as the source of truth — letting
      // a server echo overwrite setNodes/setEdges is what caused the delete-race bug.
      if (thisSaveVersion === saveVersionRef.current &&
          nextOperation.spec && typeof nextOperation.spec === "object" && !Array.isArray(nextOperation.spec)) {
        const rec = nextOperation.spec as Record<string, unknown>;

        if (!options?.silent && rec.version === "operation.v1") {
          const spec = parseSpecV1(nextOperation.spec, nextOperation.status, nextOperation.tags ?? []);
          setNodes((prev) => {
            const prevById = new Map(prev.map((n) => [n.id, n]));
            return spec.graph.nodes.map((opNode) => {
              // Layout is not part of a save's response, and `existing.position`
              // below wins for every node already on screen; a node that is new
              // to this tab lands at the origin exactly as it did before.
              const node = toCanvasNode(opNode);
              const existing = prevById.get(node.id);
              const merged = existing
                ? {
                    ...node,
                    position: existing.position,
                    data: {
                      ...node.data,
                      runStatus: existing.data.runStatus,
                      needsValidation: existing.data.needsValidation,
                      findingsSeverity: existing.data.findingsSeverity,
                      findingsTitle: existing.data.findingsTitle
                    }
                  }
                : node;
              if (selectedNodeIdBeforeSave && merged.id === selectedNodeIdBeforeSave) {
                return { ...merged, selected: true };
              }
              return merged;
            });
          });
          const nodeTypesById = new Map(spec.graph.nodes.map((n) => [n.id, n.type]));
          setEdges(
            spec.graph.edges.map((opEdge) => {
              const edge = toCanvasEdge(
                opEdge,
                nodeTypesById.get(opEdge.from_node) ?? "",
                nodeTypesById.get(opEdge.to_node) ?? ""
              );
              if (selectedEdgeIdBeforeSave && edge.id === selectedEdgeIdBeforeSave) {
                return { ...edge, selected: true };
              }
              return edge;
            })
          );
          if (selectedNodeIdBeforeSave) {
            setProviderInspectorTab(providerInspectorTabBeforeSave);
          }
        }
        setSavedSnapshot(
          JSON.stringify({
            name: nextOperation.name,
            description: nextOperation.description ?? null,
            status: nextOperation.status,
            tags: nextOperation.tags ?? [],
            spec: parseSpecV1(nextOperation.spec, nextOperation.status, nextOperation.tags ?? [])
          })
        );
      }
      if (!options?.silent) {
        toast.push({ kind: "success", message: "Saved" });
      }
      return true;
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "validation_failed") {
        const payloadIssues = Array.isArray((e.data as { issues?: unknown }).issues) ? ((e.data as { issues: ValidationIssue[] }).issues) : [];
        setIssues(payloadIssues);
        if (!options?.silent) {
          toast.push({ kind: "error", message: "Save blocked by validation (publish requires clean validation)." });
        }
      } else {
      if (!options?.silent) {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function validateOperation(options?: { silent?: boolean }) {
    if (incompatibleVersion) return;
    if (!options?.silent) setBusy(true);
    try {
      const result = await apiFetch<{
        ok: true;
        issues: ValidationIssue[];
        derived_kind?: OperationKind;
        graph_summary?: GraphSummary;
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/validate`, {
        method: "POST",
        body: { spec: localSpecRef.current }
      });
      setIssues(result.issues ?? []);
      setGraphSummary(result.graph_summary ?? null);
      if (result.derived_kind) setKind(result.derived_kind);
      setValidationRan(true);
      if (!options?.silent) toast.push({ kind: "success", message: "Validation passed" });
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "validation_failed") {
        const payloadIssues = Array.isArray((e.data as { issues?: unknown }).issues) ? ((e.data as { issues: ValidationIssue[] }).issues) : [];
        setIssues(payloadIssues);
        setGraphSummary(null);
        setValidationRan(true);
        if (!options?.silent) toast.push({ kind: "error", message: "Validation failed" });
      } else {
        if (!options?.silent) toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      if (!options?.silent) setBusy(false);
    }
  }

  async function deleteOperation() {
    const ok = await confirm.confirm({
      title: "Delete operation?",
      detail:
        "This permanently deletes this operation and every persistent data store created from its Persist nodes (all versions and materialized data). This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await apiFetch(`/api/tenant/operations/${encodeURIComponent(operationId)}`, { method: "DELETE" });
    nav("/app/build/operations");
  }

  if (loading) return <LoadingState />;
  if (notAvailable) {
    return (
      <PageLayout title="Operation">
        <NotAvailableState thing="operation" />
      </PageLayout>
    );
  }
  if (error) return <ErrorState detail={error} />;

  if (incompatibleVersion) {
    return (
      <PageLayout
        title="Operations"
        description={name || undefined}
        actions={
          <button className="btn btnDanger" type="button" onClick={() => void deleteOperation()}>
            Delete operation
          </button>
        }
      >
        <div className="card">
          <h3 className="reportSectionTitle">Unsupported operation spec</h3>
          <p className="muted">This operation uses <code>{incompatibleVersion}</code>. Graph authoring requires <code>operation.v1</code>.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <button className="breadcrumbLink" type="button" onClick={() => nav("/app/build/operations")}>Operations</button>
          <span className="breadcrumbSep">/</span>
          <span>{name || "Operation"}</span>
        </span>
      }
      description={typeLabel(kind)}
      actions={
        <>
          {dirty && <button className="btn btnPrimary" type="button" onClick={() => { if (dirtyRef.current) void save(); }} disabled={busy}>Save</button>}
        </>
      }
    >
      <MLNodeRunProvider onRunDetail={(d) => ingestRunDetail(d.run, d.nodes)}>
      {returnTo && (
        <div className="returnToPageBanner">
          <button
            type="button"
            className="btn btnLink returnToPageLink"
            onClick={() => nav(returnTo)}
          >
            ← Back to {returnLabel || "Page Builder"}
          </button>
          <span className="returnToPageHint">Navigating away from this operation will clear this link.</span>
        </div>
      )}

      {status === "validate" && (
        <div style={{
          padding: "10px 16px", marginBottom: 8, borderRadius: 8,
          background: "var(--warning-50, #fffbeb)", border: "1px solid var(--warning-300, #fcd34d)",
          color: "var(--warning-800, #92400e)", fontSize: 13, display: "flex", alignItems: "center", gap: 8
        }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <strong>Validation Required</strong>
            {statusReason && <span> — {statusReason}</span>}
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Execution is blocked until all flagged nodes are reviewed and validated.</div>
          </div>
          {(() => {
            const firstFlagged = nodes.find((n) => nodeOutputs[n.id]?.validationStatus === "needs_validation");
            if (firstFlagged) {
              return (
                <button
                  type="button"
                  className="btn btnSm"
                  style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  onClick={() => setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === firstFlagged.id })))}
                >
                  Select flagged node
                </button>
              );
            }
            // No individually-flagged nodes found — operation is stuck in validate status
            // (downstream nodes may never have run). Offer operation-level clear.
            return (
              <button
                type="button"
                className="btn btnSm"
                style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                onClick={async () => {
                  try {
                    const result = await apiFetch<{ operation: Operation }>(
                      `/api/tenant/operations/${encodeURIComponent(operationId)}/clear-all-validation`,
                      { method: "POST" }
                    );
                    if (result.operation) {
                      setStatus(result.operation.status);
                      setStatusReason(result.operation.status_reason ?? null);
                    }
                    void loadNodeOutputs();
                    toast.push({ kind: "success", message: "Validation cleared" });
                  } catch (e) {
                    toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
                  }
                }}
              >
                Clear validation
              </button>
            );
          })()}
        </div>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "stretch", minHeight: 0 }}>
        <aside
          className="card"
          style={{
            width: paletteOpen ? 200 : 44,
            flexShrink: 0,
            padding: paletteOpen ? undefined : 8,
            alignContent: "start",
            transition: "width 0.15s ease",
          }}
        >
          {paletteOpen ? (
            <>
              <div className="reportSectionHeader" style={{ marginBottom: 12 }}>
                <h3 className="reportSectionTitle" style={{ flex: 1, margin: 0 }}>Palette</h3>
                <button
                  className="btn btnSm"
                  type="button"
                  title="Collapse palette"
                  aria-label="Collapse palette"
                  onClick={() => setPaletteOpen(false)}
                >
                  ◀
                </button>
              </div>
              <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
                <button
                  className="btn btnSm btnDanger"
                  type="button"
                  onClick={removeSelected}
                  disabled={!selectedNode && !selectedEdge}
                  title="Remove selected node or connection"
                  style={{ flex: 1, minWidth: 0, justifyContent: "flex-start" }}
                >
                  Remove
                </button>
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={undo}
                  disabled={!canUndo}
                  title="Undo last change"
                  aria-label="Undo last change"
                  style={{ flexShrink: 0, padding: "0 8px", display: "inline-flex", alignItems: "center" }}
                >
                  <GlyphUndo />
                </button>
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={redo}
                  disabled={!canRedo}
                  title="Redo"
                  aria-label="Redo"
                  style={{ flexShrink: 0, padding: "0 8px", display: "inline-flex", alignItems: "center" }}
                >
                  <GlyphRedo />
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Sources</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn btnSm opPaletteBtnProvider" type="button" onClick={() => addNode("provider")} style={{ width: "100%", justifyContent: "flex-start" }}>
                      <span className="opPaletteBtnInner">
                        <OpPaletteGlyphSql className="opPaletteBtnGlyph" />
                        <span>SQL Provider</span>
                      </span>
                    </button>
                    <button
                      className={`btn btnSm opPaletteBtnAiProvider${anyAiProviderPaletteBusy ? " opPaletteBtnAiBusy" : ""}`}
                      type="button"
                      onClick={() => addNode("ai_provider")}
                      aria-busy={anyAiProviderPaletteBusy}
                      title={anyAiProviderPaletteBusy ? "AI work in progress" : undefined}
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      <span className="opPaletteBtnInner">
                        <OpPaletteGlyphAi className="opPaletteBtnGlyph" />
                        <span>AI Provider</span>
                      </span>
                    </button>
                    <button className="btn btnSm opPaletteBtnPersistentProvider" type="button" onClick={() => addNode("persistent_provider")} style={{ width: "100%", justifyContent: "flex-start" }}>Persistent Provider</button>
                    {plexIntegrations.length > 0 ? (
                      <button
                        className="btn btnSm opPaletteBtnProvider"
                        type="button"
                        onClick={() => addNode("plex_api")}
                        title="ADR-0085 — fetch from Plex Cloud Data Source API"
                        style={{ width: "100%", justifyContent: "flex-start" }}
                      >
                        Plex API
                      </button>
                    ) : null}
                    {/* ADR-0102 — file_provider: tabular data from an uploaded file. */}
                    <button
                      className="btn btnSm opPaletteBtnProvider"
                      type="button"
                      onClick={() => addNode("file_provider")}
                      title="ADR-0102 — produces tabular data from a user-uploaded file (xlsx, csv, tsv, json, jsonl, parquet)"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      File Provider
                    </button>
                    {/* ADR-0172 — platform_provider: Atlas state (action items, studies, watch events, subscription runs) as tabular rows. */}
                    <button
                      className="btn btnSm opPaletteBtnProvider"
                      type="button"
                      onClick={() => addNode("platform_provider")}
                      title="Serves Atlas's own platform data — action items, studies, watch events, subscription deliveries — as rows for tiles and transforms"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      Atlas Provider
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Transform</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn btnSm opPaletteBtnTransform" type="button" onClick={() => addNode("transform")} style={{ width: "100%", justifyContent: "flex-start" }}>
                      Transform
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Visualize</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      className="btn btnSm opPaletteBtnViz"
                      type="button"
                      onClick={() => addNode("reactive_visualization")}
                      title="Parameterized DuckDB SQL + ECharts chart that re-executes on slicer interaction"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      {/* ADR-0056 §8.1 — lightning badge differentiates from the plain viz output */}
                      <span aria-hidden>⚡</span> Reactive Viz
                    </button>
                    <button
                      className="btn btnSm opPaletteBtnViz"
                      type="button"
                      onClick={() => addNode("tantable")}
                      title="ADR-0070 — Rich table visualization with sorting, filtering, and drill-down"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      TanTable
                    </button>
                    <button
                      className="btn btnSm opPaletteBtnViz"
                      type="button"
                      onClick={() => addNode("kpi_card")}
                      title="ADR-0070 — Key performance indicator metric cards with formatting"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      KPI Card
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Store</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn btnSm opPaletteBtnPersist" type="button" onClick={() => addNode("persist")} style={{ width: "100%", justifyContent: "flex-start" }}>Persist</button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>ML</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="btn btnSm opPaletteBtnModel" type="button" onClick={() => addNode("model_train")} title="ADR-0106 — trains a predictive model from upstream tabular data (regression / classification)" style={{ width: "100%", justifyContent: "flex-start" }}>Model Train</button>
                    <button className="btn btnSm opPaletteBtnModel" type="button" onClick={() => addNode("model_predict")} title="Scores upstream rows with a trained model version (in-hub)" style={{ width: "100%", justifyContent: "flex-start" }}>Model Predict</button>
                    <button className="btn btnSm opPaletteBtnModel" type="button" onClick={() => addNode("model_evaluate")} title="Evaluates a model version against a labelled dataset" style={{ width: "100%", justifyContent: "flex-start" }}>Model Evaluate</button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Control</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      className="btn btnSm opPaletteBtnControl"
                      type="button"
                      onClick={() => addNode("loop_start")}
                      title="Runs the steps between the bookends once per item in a list. Adding this places its Loop End too — a loop is authored as a pair."
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      Loop Start
                    </button>
                    <button
                      className="btn btnSm opPaletteBtnControl"
                      type="button"
                      onClick={() => addNode("loop_end")}
                      title="Closes a loop and is the only way a value leaves one. Emits the combined results plus a run log with a row per item."
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      Loop End
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase" }}>Monitor</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      className="btn btnSm opPaletteBtnWatch"
                      type="button"
                      onClick={() => addNode("watch")}
                      title="ADR-0042 — evaluates an upstream tabular result against a trigger and dispatches configured responses (action items / email / webhook)"
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      Watch
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <button
              type="button"
              title="Expand palette"
              aria-label="Expand palette"
              onClick={() => setPaletteOpen(true)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "8px 0",
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              <span aria-hidden>▶</span>
              <span
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Palette
              </span>
            </button>
          )}
        </aside>
        <section ref={editorAreaRef} className="card" style={{ flex: 1, minWidth: 0, alignContent: "start" }}>
          {conflict && (
            <WriteConflictBanner
              noun="pipeline"
              actorLabel={conflict.actorLabel}
              sourceDisplay={conflict.sourceDisplay}
              at={conflict.at || conflict.updatedAt}
              busy={busy}
              onReload={() => void load()}
              onCopy={() => void copyLocalSpec()}
            />
          )}
          <div
            ref={reactFlowViewportRef}
            style={{ height: 560, border: "1px solid var(--line)", borderRadius: 8 }}
            onKeyDown={(e) => {
              const isDelete = e.key === "Delete" || e.key === "Backspace";
              const isDuplicate = (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "d";
              const isUndo = (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "z";
              const isRedo = (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === "z";
              if (!isDelete && !isDuplicate && !isUndo && !isRedo) return;
              // Don't intercept keystrokes typed into any input, textarea, select, or CodeMirror editor
              const target = e.target as HTMLElement;
              if (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.tagName === "SELECT" ||
                target.isContentEditable ||
                target.closest(".cm-editor") !== null
              ) return;
              if (isUndo) {
                e.preventDefault();
                undo();
                return;
              }
              if (isRedo) {
                e.preventDefault();
                redo();
                return;
              }
              if (isDuplicate) {
                if (!selectedNode) return;
                e.preventDefault();
                duplicateSelected();
                return;
              }
              e.preventDefault();
              const selectedNodeIds = nodes.filter((n) => n.selected).map((n) => n.id);
              if (selectedNodeIds.length > 0) {
                onNodesChange(selectedNodeIds.map((id) => ({ type: "remove" as const, id })));
                return;
              }
              const selectedEdgeIds = edges.filter((edge) => edge.selected).map((edge) => edge.id);
              if (selectedEdgeIds.length > 0) {
                onEdgesChange(selectedEdgeIds.map((id) => ({ type: "remove" as const, id })));
              }
            }}
          >
            <OperationNodeOutputsProvider value={nodeOutputs}>
            <ReactFlow
              nodes={renderNodes}
              edges={renderEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
              }}
              fitView
              nodeTypes={nodeTypes}
              connectionRadius={50}
              deleteKeyCode={null}
              defaultEdgeOptions={{
                style: { stroke: "var(--op-port-tabular)", strokeWidth: 1.5 },
                markerEnd: { type: MarkerType.ArrowClosed }
              }}
              isValidConnection={(connection) => {
                const src = nodes.find((n) => n.id === connection.source);
                const tgt = nodes.find((n) => n.id === connection.target);
                if (!src || !tgt) return false;
                return isPortsConnectionValid({
                  sourceNodeType: src.data.nodeType,
                  targetNodeType: tgt.data.nodeType,
                  sourceHandle: connection.sourceHandle ?? "",
                  targetHandle: connection.targetHandle ?? ""
                });
              }}
              onEdgeClick={(_event, edge) => {
                setEdges((current) => current.map((e) => ({ ...e, selected: e.id === edge.id })));
                setNodes((current) => current.map((n) => ({ ...n, selected: false })));
              }}
            >
              {nodes.length > 0 ? (
                <Panel position="top-left">
                  <button
                    className="btn btnSm"
                    type="button"
                    onClick={autoLayoutNodes}
                    title="Arrange steps left to right by data flow"
                  >
                    Auto layout
                  </button>
                </Panel>
              ) : null}
              {selectedNode && activeRunIdByNode[selectedNode.id] ? (
                <Panel position="top-right">
                  <button
                    className="btn btnSm"
                    type="button"
                    onClick={() => void stopSelectedNodeExecution()}
                  >
                    Stop run
                  </button>
                </Panel>
              ) : null}
              <MiniMap />
              <Controls />
              <Background />
            </ReactFlow>
            </OperationNodeOutputsProvider>
          </div>
        </section>
        <InspectorFlyout
          surfaceId="operationEditor"
          panelId="inspector"
          open={!inspectorCollapsed}
          onOpenChange={(o) => setInspectorCollapsed(!o)}
          stripLabel="Inspector"
          topOffset={inspectorFlyoutTop}
          widthConfig={{ min: 360, max: (vw) => Math.min(vw - 80, 1100), default: 480 }}
          headerNav={
            selectedNode ? (
              <NodeNavDropdown nodes={navNodes} selectedId={selectedNode.id} onSelect={selectNodeById} />
            ) : (
              inspectorTitle
            )
          }
          headerActions={
            selectedNode ? (
              <button
                type="button"
                className="btn btnSm"
                title="Duplicate node (⌘/Ctrl+D)"
                onClick={duplicateSelected}
              >
                Duplicate
              </button>
            ) : undefined
          }
        >
          <div
            className={
              // The operation-level inspector (nothing selected) and the migrated
              // node types use the frame-filling spine body; the untabbed edge
              // inspector keeps the plain scroll container.
              (!selectedNode && !selectedEdge) ||
              (selectedNode &&
                [
                  "provider",
                  "plex_api",
                  "persistent_provider",
                  "platform_provider",
                  "model_train",
                  "model_predict",
                  "model_evaluate",
                  "tantable",
                  "kpi_card",
                  "reactive_visualization",
                  "ai_provider",
                  "watch",
                  "file_provider",
                  "persist",
                  "transform"
                ].includes(selectedNode.data.nodeType))
                ? "inspectorSpineBody"
                : "operationNodeInspectorBody"
            }
          >
            <>
          {!selectedNode && !selectedEdge ? (
            <OperationInspectorOperationLevel
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              tagsText={tagsText}
              setTagsText={setTagsText}
              status={status}
              setStatus={setStatus}
              save={save}
              issues={issues}
              resolvedFindings={groupedFindings.all.map((f) => ({
                treatment: f.treatment,
                stepLabel: f.stepLabel,
                nodeId: f.nodeId,
                field: f.field,
                message: f.message,
                pendingNote: f.pendingNote,
              }))}
              onSelectNode={(nodeId) => setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === nodeId })))}
              graphSummary={graphSummary}
              validationRan={validationRan}
              onValidate={() => void validateOperation()}
              operationWindows={operationWindows}
              operationVariables={operationVariables}
              openNewWindowModal={openNewWindowModal}
              openEditWindowModal={openEditWindowModal}
              deleteWindowFromList={deleteWindowFromList}
              openNewVariableModal={openNewVariableModal}
              openEditVariableModal={openEditVariableModal}
              deleteVariableFromList={deleteVariableFromList}
              operationId={operationId}
              aiChatBlocked={aiChatBlocked}
              onAiChatRateLimited={() => setAiChatCooldownUntil(Date.now() + AI_USAGE_LIMIT_COOLDOWN_MS)}
              onApplyPipeline={applyRecommendation}
              onApplyStage={applySingleStage}
              onApplyNodeChange={applyModelConfigChange}
              onDelegate={handleArchitectDelegate}
              nodeLabelById={(id) => nodes.find((n) => n.id === id)?.data.label ?? null}
              resolveModelName={(id) => tenantModelsForNames.find((m) => m.id === id)?.name ?? null}
              tenantAiModels={tenantAiModels}
              accountDefaultAiModelId={selectedAiModelId}
            />
          ) : null}

          {selectedNode ? (
            <div className="reportSection operationNodeInspectorSection">
              {/* ADR-0153 Phase 5 — the findings banner. Rendered by the SHELL
                  for every node type (zero per-inspector wiring; the ADR-0146
                  precedent): the selected node's validator findings, worst
                  treatment first, each in business language. Declared
                  unfinished-by-design findings render neutral with their
                  finish-in-Atlas note instead of red. */}
              {(() => {
                const summary = groupedFindings.byNode.get(selectedNode.id);
                if (!summary) return null;
                const real = summary.findings.filter((f) => f.treatment !== "pending");
                const pending = summary.findings.filter((f) => f.treatment === "pending");
                return (
                  <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                    {real.length > 0 && (
                      <div
                        className="card"
                        style={
                          summary.severity === "error"
                            ? { borderColor: "var(--danger-border)", background: "var(--danger-bg)" }
                            : { borderColor: "var(--warning-border, #fde68a)", background: "var(--warning-bg, #fffbeb)" }
                        }
                      >
                        <div style={{ fontWeight: 700, marginBottom: 4, color: summary.severity === "error" ? "var(--danger-text)" : "var(--warning, #92400e)" }}>
                          {real.filter((f) => f.treatment === "error").length > 0
                            ? "This step has problems to fix"
                            : "This step has warnings"}
                        </div>
                        {real.map((f, i) => (
                          <div key={i} className="help" style={{ marginTop: 2 }}>
                            {humanizeField(f.field) ? <strong>{humanizeField(f.field)}: </strong> : null}
                            {f.message}
                          </div>
                        ))}
                      </div>
                    )}
                    {pending.length > 0 && (
                      <div className="card">
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>Unfinished — nothing is wrong</div>
                        <div className="help">{pending[0]!.pendingNote}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {nodeOutputs[selectedNode.id]?.validationStatus === "needs_validation" && (
                <div style={{
                  padding: "8px 12px", marginBottom: 8, borderRadius: 6,
                  background: "var(--warning-50, #fffbeb)", border: "1px solid var(--warning-300, #fcd34d)",
                  color: "var(--warning-800, #92400e)", fontSize: 12
                }}>
                  <strong>Needs validation</strong>
                  {nodeOutputs[selectedNode.id]?.validationError && (
                    <div style={{ marginTop: 4 }}>{nodeOutputs[selectedNode.id].validationError}</div>
                  )}
                  <button
                    className="btn btnSm"
                    type="button"
                    style={{ marginTop: 6, fontSize: 11 }}
                    onClick={async () => {
                      try {
                        const result = await apiFetch<{ cleared: boolean; operationRestored: boolean; operation: Operation }>(
                          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/clear-validation`,
                          { method: "POST" }
                        );
                        if (result.operation) {
                          setStatus(result.operation.status);
                          setStatusReason(result.operation.status_reason ?? null);
                        }
                        void loadNodeOutputs();
                        toast.push({ kind: "success", message: "Validation cleared" });
                      } catch (e) {
                        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
                      }
                    }}
                  >Mark as validated</button>
                </div>
              )}
              {/* ADR-0146 — Schema lock. Rendered by the shell rather than each
                  inspector module: one control for every node type that produces
                  tabular output. Hidden for shared-provider-bound nodes, whose
                  shape is owned by the provider (§3.2). */}
              {supportsSchemaLock(selectedNode.data.nodeType) &&
                !isNodeBoundToSharedProvider(selectedNode) && (
                <div className="field">
                  <label
                    className="label"
                    htmlFor="op-node-schema-lock"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}
                  >
                    <input
                      id="op-node-schema-lock"
                      type="checkbox"
                      checked={Boolean(nodeSchemaLocks[selectedNode.id])}
                      disabled={Boolean(schemaLockBusy[selectedNode.id])}
                      onChange={(e) => void toggleNodeSchemaLock(selectedNode.id, e.target.checked)}
                    />
                    Schema lock
                    {nodeSchemaLocks[selectedNode.id] ? (
                      <span
                        className="pill"
                        title={schemaLockTitle(nodeSchemaLocks[selectedNode.id])}
                      >
                        Schema locked
                      </span>
                    ) : null}
                  </label>
                </div>
              )}
              {/* ADR-0129 — these node types own their Node label inside the
                  Info slot of their spine, so the outer wrapper skips it for them. */}
              {selectedNode.data.nodeType !== "provider" &&
              selectedNode.data.nodeType !== "plex_api" &&
              selectedNode.data.nodeType !== "tantable" &&
              selectedNode.data.nodeType !== "kpi_card" &&
              selectedNode.data.nodeType !== "reactive_visualization" &&
              selectedNode.data.nodeType !== "ai_provider" &&
              selectedNode.data.nodeType !== "watch" &&
              selectedNode.data.nodeType !== "file_provider" &&
              selectedNode.data.nodeType !== "persist" &&
              selectedNode.data.nodeType !== "transform" ? (
                  <div className="field">
                  <label className="label">Node label</label>
                    <input
                      className="input"
                    value={selectedNode.data.label}
                    onChange={(e) => setNodeData(selectedNode.id, (data) => ({ ...data, label: e.target.value }))}
                    />
                  </div>
              ) : null}
              {selectedNode.data.nodeType === "provider" ? (
                <OperationInspectorProviderScope
                  onOpenTrace={(id) => void openTrace(id, selectedNode.id)}
                  selectedNode={selectedNode}
                  nodeRunActive={Boolean(activeRunIdByNode[selectedNode.id])}
                  onStopExecution={stopSelectedNodeExecution}
                  operationMeta={operationMeta}
                  providerInspectorTab={providerInspectorTab}
                  setProviderInspectorTab={setProviderInspectorTab}
                  selectedProviderRef={selectedProviderRef}
                  onProviderSourceKindChange={(kind) => {
                    if (kind === "local") {
                      const localProviderId = crypto.randomUUID();
                      setLocalProviders((prev) => ({
                        ...prev,
                        [localProviderId]: createDefaultLocalProviderDraft(
                          defaultLocalConnectorId,
                          `${NODE_LABELS[selectedNode.data.nodeType]} provider`
                        )
                      }));
                      setNodeData(selectedNode.id, (data) => ({
                        ...data,
                        config: { ...data.config, provider: { type: "local", local_provider_id: localProviderId } }
                      }));
                    } else {
                      setNodeData(selectedNode.id, (data) => ({
                        ...data,
                        config: { ...data.config, provider: { type: "shared", provider_id: "" } }
                      }));
                    }
                  }}
                  onSharedProviderIdChange={(providerId) =>
                    setNodeData(selectedNode.id, (data) => ({
                      ...data,
                      config: { ...data.config, provider: { type: "shared", provider_id: providerId } }
                    }))
                  }
                  setNodeData={setNodeData}
                  providersForConnector={providers}
                  edges={edges}
                  nodes={nodes}
                  nodeOutputs={nodeOutputs}
                  columnOverrides={
                    (selectedNode.data.config as Record<string, unknown>).column_type_overrides as
                      | Record<string, string>
                      | undefined
                  }
                  onColumnDomainChange={(col, domain) =>
                    void changeNodeColumnDomain(selectedNode.id, col, domain)
                  }
                  retypeBusy={!!columnRetypeBusy[selectedNode.id]}
                  selectedProviderDraft={selectedProviderDraft as Parameters<typeof OperationInspectorProviderScope>[0]["selectedProviderDraft"]}
                  updateMaterialProviderDraft={updateMaterialProviderDraft as Parameters<typeof OperationInspectorProviderScope>[0]["updateMaterialProviderDraft"]}
                  onPromoteLocalProvider={() => void promoteSelectedLocalProvider()}
                  onDetachSharedProvider={() => {
                    if (selectedProviderRef?.type === "shared") {
                      void detachSharedProviderForNode(selectedNode.id, selectedProviderRef.provider_id);
                    }
                  }}
                  selectedProviderPreview={selectedProviderPreview}
                  executeSelectedProviderSql={executeSelectedProviderSql}
                  openNewParameterModal={openNewParameterModal}
                  openEditParameterModal={openEditParameterModal}
                  deleteParameterFromList={deleteParameterFromList}
                  bindingDescription={bindingDescription}
                  openAddBindingModal={openAddBindingModal}
                  openBindingModal={openBindingModal}
                  clearBinding={clearBinding}
                  connectors={connectors}
                  nodeCacheRefreshBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                  refreshProviderCache={refreshProviderCache}
                  nodeCacheLoading={nodeCacheLoading}
                  nodeCache={nodeCache}
                  nodeRunHistory={nodeRunHistory}
                  nodeRunHistoryLoading={nodeRunHistoryLoading}
                  loadNodeRunHistory={loadNodeRunHistory}
                  onSelectHistoryRun={(run) => setHistoryDetailEntry(nodeRunToDetail(run))}
                  pushToast={(t) => toast.push(t)}
                  operationVariableNames={operationVariables.map((v) => v.name)}
                />
              ) : null}

              {selectedNode.data.nodeType === "plex_api" ? (
                <OperationInspectorPlexApi
                  onOpenTrace={(id) => void openTrace(id, selectedNode.id)}
                  selectedNode={selectedNode}
                  operationMeta={operationMeta}
                  inspectorTab={plexInspectorTab}
                  setInspectorTab={setPlexInspectorTab}
                  selectedProviderRef={selectedProviderRef}
                  setNodeData={setNodeData}
                  integrations={plexIntegrations}
                  edges={edges}
                  nodes={nodes}
                  nodeOutputs={nodeOutputs}
                  selectedProviderDraft={selectedProviderDraft as Parameters<typeof OperationInspectorPlexApi>[0]["selectedProviderDraft"]}
                  updateMaterialProviderDraft={updateMaterialProviderDraft as Parameters<typeof OperationInspectorPlexApi>[0]["updateMaterialProviderDraft"]}
                  selectedProviderPreview={selectedProviderPreview}
                  executeSelectedPlexRequest={executeSelectedPlexRequest}
                  columnOverrides={
                    (selectedNode.data.config as Record<string, unknown>).column_type_overrides as
                      | Record<string, string>
                      | undefined
                  }
                  onColumnDomainChange={(col, domain) =>
                    void changeNodeColumnDomain(selectedNode.id, col, domain)
                  }
                  retypeBusy={!!columnRetypeBusy[selectedNode.id]}
                  openNewParameterModal={openNewParameterModal}
                  openEditParameterModal={openEditParameterModal}
                  deleteParameterFromList={deleteParameterFromList}
                  bindingDescription={bindingDescription}
                  openAddBindingModal={openAddBindingModal}
                  openBindingModal={openBindingModal}
                  clearBinding={clearBinding}
                  nodeCacheRefreshBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                  refreshProviderCache={refreshProviderCache}
                  nodeCacheLoading={nodeCacheLoading}
                  nodeCache={nodeCache}
                  nodeRunHistory={nodeRunHistory}
                  nodeRunHistoryLoading={nodeRunHistoryLoading}
                  loadNodeRunHistory={loadNodeRunHistory}
                  onSelectHistoryRun={(run) => setHistoryDetailEntry(nodeRunToDetail(run))}
                  pushToast={(t) => toast.push(t)}
                />
              ) : null}

              {selectedNode.data.nodeType === "ai_provider" ? (
                <OperationInspectorAiProvider
                  key={selectedNode.id}
                  operationId={operationId}
                  selectedNode={selectedNode}
                  operationMeta={operationMeta}
                  setNodeData={setNodeData}
                  queueAutoSave={queueAutoSave}
                  tenantAiModels={tenantAiModels}
                  accountDefaultAiModelId={selectedAiModelId}
                  aiChatBlocked={aiChatBlocked}
                  contextInputs={aiProviderContextInputs}
                  onNodeRunStatusChange={handleAiProviderRunStatus}
                  executingNodeIds={aiProviderExecutingIds}
                  onAuthoringChatBusyChange={setAiProviderAuthoringBusy}
                  onAiChatRateLimited={() => {
                    setAiChatCooldownUntil(Date.now() + AI_USAGE_LIMIT_COOLDOWN_MS);
                  }}
                />
              ) : null}

              {selectedNode.data.nodeType === "transform" ? (
                <OperationInspectorTransform
                  onOpenTrace={(id) => void openTrace(id, selectedNode.id)}
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationMeta={operationMeta}
                  transformActiveSlot={transformActiveSlot}
                  setTransformActiveSlot={setTransformActiveSlot}
                  getDirectParentNodes={getDirectParentNodes}
                  nodeOutputs={nodeOutputs}
                  setNodeData={setNodeData}
                  operationStatus={status}
                  transformForceRefresh={transformForceRefresh}
                  setTransformForceRefresh={setTransformForceRefresh}
                  onRunTransformPreview={() => void runTransformPreview(selectedNode.id)}
                  transformRunBusy={Boolean(activeRunIdByNode[selectedNode.id]) || !!transformRunBusyByNode[selectedNode.id]}
                  transformSending={transformSending}
                  onResetTransformChat={() => resetTransformChat()}
                  transformRunError={transformRunErrorByNode[selectedNode.id] ?? null}
                  transformInteractionMode={transformInteractionMode}
                  setTransformInteractionMode={setTransformInteractionMode}
                  tenantAiModels={tenantAiModels}
                  selectedAiModelId={selectedAiModelId}
                  onTenantAiModelChange={onTenantAiModelChange}
                  aiChatBlocked={aiChatBlocked}
                  transformChatScrollRef={transformChatScrollRef}
                  transformMessages={transformMessages}
                  transformPrompt={transformPrompt}
                  setTransformPrompt={setTransformPrompt}
                  onSendTransformPrompt={(message, onRestore) => void sendTransformPrompt(selectedNode.id, message, onRestore)}
                  onCancelTransformChat={() => void cancelTransformChat()}
                  transformSql={transformSql}
                  onTransformSqlChange={(v) => handleTransformSqlChange(selectedNode.id, v)}
                  transformJson={transformJson}
                  onTransformJsonChange={(v) => handleTransformJsonChange(selectedNode.id, v)}
                  onTransformSpecBlur={() => {
                    void save(undefined, { silent: true });
                  }}
                  transformJsonError={transformJsonError}
                  transformPreview={transformPreviewByNode[selectedNode.id] ?? null}
                  transformStats={transformStatsByNode[selectedNode.id] ?? null}
                  transformSubTab={transformSubTab}
                  setTransformSubTab={setTransformSubTab}
                  nodeCacheLoadingThisNode={!!nodeCacheLoading[selectedNode.id]}
                  nodeOutputRunAt={nodeOutputs[selectedNode.id]?.runAt}
                  nodeRunHistory={nodeRunHistory[selectedNode.id] ?? []}
                  nodeRunHistoryLoading={nodeRunHistoryLoading[selectedNode.id] ?? false}
                  onSelectHistoryRun={setSelectedNodeRun}
                  columnOverrides={
                    (selectedNode.data.config as Record<string, unknown>).column_type_overrides as
                      | Record<string, string>
                      | undefined
                  }
                  onColumnDomainChange={(col, domain) =>
                    void changeNodeColumnDomain(selectedNode.id, col, domain)
                  }
                  retypeBusy={!!columnRetypeBusy[selectedNode.id]}
                />
              ) : null}

              {selectedNode.data.nodeType === "persist" ? (
                <OperationInspectorPersist
                  // Remount when the selected persist node changes so all
                  // local state (loaded `pdo`, form fields, lastExecution)
                  // is dropped. Without this, switching from a bound persist
                  // node to a brand-new one carried the prior node's PDO
                  // summary forward and the inspector rendered the previous
                  // node's metadata against the new node.
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  // ADR-0095 — pass operation-level variables so the watermark
                  // binding picker can populate the bound-variable dropdown.
                  operationVariables={operationVariables}
                  // Bug fix 2026-06-10 — runExecute awaits flushAndSave before
                  // POSTing /execute-persist so the hub never reads stale spec
                  // (autosave race that looked like a cache hit).
                  flushAndSave={flushAndSave}
                />
              ) : null}

              {/* ADR-0102 — file_provider inspector dispatch. Sibling of
                  persist; owns ADR-0092's upload mechanics (parser config,
                  retention, column mappings, uploads history) relocated to
                  the new provider node. */}
              {selectedNode.data.nodeType === "file_provider" ? (
                <OperationInspectorFileProvider
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  // ADR-0159 — an upload writes the operation spec on the
                  // server, so this tab's token has to be re-synced from the
                  // upload's response the way a save re-syncs from its own.
                  rememberToken={rememberToken}
                  // ADR-0192 — the declared schema (Data slot) with the same
                  // retype gesture the provider inspectors use; the lock is
                  // the one switch, so the control is disabled while locked.
                  nodeOutputSchema={nodeOutputs[selectedNode.id]?.outputSchema ?? null}
                  columnOverrides={
                    (selectedNode.data.config as Record<string, unknown>).column_type_overrides as
                      | Record<string, string>
                      | undefined
                  }
                  onColumnDomainChange={(col, domain) =>
                    void changeNodeColumnDomain(selectedNode.id, col, domain)
                  }
                  retypeBusy={!!columnRetypeBusy[selectedNode.id]}
                  schemaLocked={{
                    locked: Boolean(nodeSchemaLocks[selectedNode.id]),
                    title: nodeSchemaLocks[selectedNode.id] ? schemaLockTitle(nodeSchemaLocks[selectedNode.id]) : null,
                  }}
                />
              ) : null}

              {/* ADR-0106 — model node inspectors. ADR-0129 — Setup (config) + AI
                  (advisor) spine; the ADR-0115 Config/Advisor button toggle is
                  retired. Run state still lives in the editor-scoped
                  MLNodeRunProvider, so tab switches are safe; the advisor panel
                  stays mounted via InspectorTabs keep-alive (draft/conversation
                  survive). Keyed by node type so a train↔predict↔evaluate switch
                  re-reads that type's last-used tab. */}
              {(selectedNode.data.nodeType === "model_train" || selectedNode.data.nodeType === "model_predict" || selectedNode.data.nodeType === "model_evaluate") ? (
                <InspectorTabs
                  key={selectedNode.data.nodeType}
                  surfaceId="operationEditor"
                  tabGroupId={selectedNode.data.nodeType}
                  activeSlot={modelInspectorSlot}
                  onSlotChange={setModelInspectorSlot}
                  tabs={[
                    {
                      slot: "setup",
                      content:
                        selectedNode.data.nodeType === "model_train" ? (
                          <OperationInspectorModelTrain
                            key={selectedNode.id}
                            selectedNode={selectedNode}
                            operationId={operationId}
                            operationName={name}
                            setNodeData={setNodeData}
                            flushAndSave={flushAndSave}
                          />
                        ) : selectedNode.data.nodeType === "model_predict" ? (
                          <OperationInspectorModelPredict
                            key={selectedNode.id}
                            selectedNode={selectedNode}
                            operationId={operationId}
                            setNodeData={setNodeData}
                            flushAndSave={flushAndSave}
                            pipelineModels={pipelineBindableModels}
                          />
                        ) : (
                          <OperationInspectorModelEvaluate
                            key={selectedNode.id}
                            selectedNode={selectedNode}
                            operationId={operationId}
                            setNodeData={setNodeData}
                            flushAndSave={flushAndSave}
                            pipelineModels={pipelineBindableModels}
                          />
                        ),
                    },
                    {
                      slot: "ai",
                      fill: true,
                      content: (
                        <NodeAdvisorPanel
                          key={selectedNode.id}
                          operationId={operationId}
                          nodeId={selectedNode.id}
                          applyModelConfigChange={applyModelConfigChange}
                          resolveModelName={(id) => tenantModelsForNames.find((m) => m.id === id)?.name ?? null}
                        />
                      ),
                    },
                  ]}
                />
              ) : null}

              {selectedNode.data.nodeType === "persistent_provider" ? (
                <OperationInspectorPersistentProvider
                  selectedNode={selectedNode}
                  setNodeData={setNodeData}
                  nodeOutputRecord={nodeOutputs[selectedNode.id]}
                  onRunNode={refreshProviderCache}
                  runBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                  cachedPayload={nodeCache[selectedNode.id]}
                  cacheLoading={nodeCacheLoading[selectedNode.id] ?? false}
                />
              ) : null}

              {selectedNode.data.nodeType === "platform_provider" ? (
                <OperationInspectorPlatformProvider
                  selectedNode={selectedNode}
                  setNodeData={setNodeData}
                  nodeOutputRecord={nodeOutputs[selectedNode.id]}
                  onRunNode={refreshProviderCache}
                  runBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                  cachedPayload={nodeCache[selectedNode.id]}
                  cacheLoading={nodeCacheLoading[selectedNode.id] ?? false}
                />
              ) : null}

              {selectedNode.data.nodeType === "loop_start" ? (
                <OperationInspectorLoopStart
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  nodes={nodes}
                  edges={edges}
                  nodeOutputRecord={nodeOutputs[selectedNode.id]}
                  onRunNode={refreshProviderCache}
                  runBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                />
              ) : null}

              {selectedNode.data.nodeType === "loop_end" ? (
                <OperationInspectorLoopEnd
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  setNodeData={setNodeData}
                  nodes={nodes}
                  edges={edges}
                  nodeOutputRecord={nodeOutputs[selectedNode.id]}
                  onRunNode={refreshProviderCache}
                  runBusy={nodeCacheRefreshBusy}
                  operationStatus={status}
                />
              ) : null}

              {selectedNode.data.nodeType === "reactive_visualization" ? (
                <OperationInspectorReactiveViz
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  flushAndSave={flushAndSave}
                  tenantAiModels={tenantAiModels}
                  selectedAiModelId={selectedAiModelId}
                  onTenantAiModelChange={onTenantAiModelChange}
                  setRunStatus={setRunStatus}
                  hasIncomingDataEdge={edges.some(
                    (e) => e.target === selectedNode.id && e.data?.edge_type === "data"
                  )}
                />
              ) : null}

              {selectedNode.data.nodeType === "watch" ? (
                <OperationInspectorWatch
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  operationName={name}
                  setNodeData={setNodeData}
                  flushAutoSave={flushAutoSave}
                />
              ) : null}

              {selectedNode.data.nodeType === "tantable" ? (
                <OperationInspectorTanTable
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  flushAndSave={flushAndSave}
                  tenantAiModels={tenantAiModels}
                  selectedAiModelId={selectedAiModelId}
                  onTenantAiModelChange={onTenantAiModelChange}
                  setRunStatus={setRunStatus}
                />
              ) : null}

              {selectedNode.data.nodeType === "kpi_card" ? (
                <OperationInspectorKpiCard
                  key={selectedNode.id}
                  selectedNode={selectedNode}
                  operationId={operationId}
                  setNodeData={setNodeData}
                  flushAndSave={flushAndSave}
                  tenantAiModels={tenantAiModels}
                  selectedAiModelId={selectedAiModelId}
                  onTenantAiModelChange={onTenantAiModelChange}
                  setRunStatus={setRunStatus}
                />
              ) : null}

            </div>
          ) : null}

          {selectedEdge ? (
            <OperationInspectorEdge
              edgeType={String(selectedEdge.data?.edge_type ?? "data")}
              source={selectedEdge.source}
              sourceHandle={selectedEdge.sourceHandle}
              target={selectedEdge.target}
              targetHandle={selectedEdge.targetHandle}
            />
          ) : null}

            </>
          </div>
        </InspectorFlyout>
      </div>

      {bindingModalOpen ? (
        <div className="modalOverlay" role="presentation" onClick={closeBindingModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="reportModalTitle">{bindingModalMode === "add" ? "Add Binding" : `Edit Binding: ${bindingModalParamName}`}</div>
            <div className="field">
              <label className="label">Target parameter</label>
                        <select
                          className="input"
                value={bindingModalParamName}
                          onChange={(e) => {
                  const nextParam = selectedProviderDraft?.parameters.find((param) => param.name === e.target.value);
                  if (!nextParam) return;
                  setBindingModalParamName(nextParam.name);
                  setBindingModalParamType(nextParam.type);
                  setBindingModalHasDefault(nextParam.default !== undefined && nextParam.default !== null);
                  setBindingModalSourceType(nextParam.default !== undefined && nextParam.default !== null ? "default" : "const");
                  setBindingConstText("");
                }}
              >
                {(selectedProviderDraft?.parameters ?? []).map((param) => (
                  <option key={`binding-target-${param.name}`} value={param.name}>
                    {param.name} ({param.type})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label">Source</label>
                        <select
                          className="input"
                value={bindingModalSourceType}
                onChange={(e) => setBindingModalSourceType(e.target.value as BindingSource["type"])}
              >
                {bindingModalHasDefault ? <option value="default">Provider default</option> : null}
                          <option value="const">Constant</option>
                <option value="window">Window</option>
                <option value="variable">Variable</option>
                {bindingModalAllowScope ? <option value="scope">Scope column</option> : null}
                        </select>
                      </div>
            {bindingModalSourceType === "const" ? (
                        <div className="field">
                          <label className="label">Constant value</label>
                          <input
                            className="input"
                  value={bindingConstText}
                  onChange={(e) => setBindingConstText(e.target.value)}
                  placeholder={bindingModalParamType === "bool" ? "true | false" : ""}
                          />
                        </div>
                      ) : null}
            {bindingModalSourceType === "window" ? (
              <div className="field">
                <label className="label">Window value</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "center" }}>
                  <select className="input" value={bindingWindowName} onChange={(e) => setBindingWindowName(e.target.value)}>
                            <option value="">Select window</option>
                    {operationWindows.map((windowDef) => (
                              <option key={windowDef.name} value={windowDef.name}>
                                {windowDef.name}
                              </option>
                            ))}
                          </select>
                          <select
                            className="input"
                    value={bindingWindowField}
                    onChange={(e) => setBindingWindowField(e.target.value as "start_at" | "end_at" | "start_date" | "end_date")}
                          >
                            <option value="start_at">start_at</option>
                            <option value="end_at">end_at</option>
                            <option value="start_date">start_date</option>
                            <option value="end_date">end_date</option>
                          </select>
                  <button
                    className="linkBtn menuLinkAction"
                    type="button"
                    onClick={() => {
                      setPendingWindowBinding({
                        nodeId: selectedNode?.id ?? "",
                        paramName: bindingModalParamName,
                        field: bindingWindowField
                      });
                      setEditingWindowIndex(null);
                      const nextWindow: WindowDef = {
                        name: `window_${operationWindows.length + 1}`,
                        type: "relative",
                        start_offset: { value: -6, unit: "month" },
                        end_offset: { value: 0, unit: "day" }
                      };
                      setWindowDraft(nextWindow);
                      setWindowStartOffsetText(String(nextWindow.start_offset.value));
                      setWindowEndOffsetText(String(nextWindow.end_offset.value));
                      setWindowsModalOpen(true);
                    }}
                  >
                    + Window
                  </button>
                </div>
                        </div>
                      ) : null}
            {bindingModalSourceType === "variable" ? (
              <div className="field">
                <label className="label">Variable</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
                  <select className="input" value={bindingVariableName} onChange={(e) => setBindingVariableName(e.target.value)}>
                    <option value="">Select variable</option>
                    {operationVariables.map((variable) => (
                      <option key={variable.name} value={variable.name}>
                        {variable.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="linkBtn menuLinkAction"
                    type="button"
                    onClick={() => {
                      setPendingVariableBinding({ nodeId: selectedNode?.id ?? "", paramName: bindingModalParamName });
                      setEditingVariableIndex(null);
                      setVariableDraft({
                        name: `var_${operationVariables.length + 1}`,
                        type: "string",
                        value: "",
                        description: ""
                      });
                      setVariablesModalOpen(true);
                    }}
                  >
                    + Variable
                  </button>
                </div>
              </div>
            ) : null}
            {bindingModalSourceType === "scope" ? (
                        <div className="field">
                          <label className="label">Scope column</label>
                <input className="input" value={bindingScopeColumn} onChange={(e) => setBindingScopeColumn(e.target.value)} />
              </div>
            ) : null}
            {bindingModalSourceType === "default" ? <div className="muted">Uses provider parameter default.</div> : null}
            <div className="actions">
              <button
                className="btn"
                type="button"
                onClick={() => {
                  if (bindingModalOriginalParamName) void clearBinding(bindingModalOriginalParamName);
                  else void clearBinding(bindingModalParamName);
                  closeBindingModal();
                }}
              >
                Delete
              </button>
              <button className="btn" type="button" onClick={closeBindingModal}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={saveBindingModal}>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {parameterModalOpen ? (
        <div className="modalOverlay" role="presentation" onClick={closeParameterModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="reportModalTitle">{editingParameterIndex === null ? "New Parameter" : "Edit Parameter"}</div>
            <div className="field">
              <label className="label">Name</label>
              <input
                className="input"
                value={parameterDraft.name}
                onChange={(e) => setParameterDraft((prev) => ({ ...prev, name: sanitizeIdentifier(e.target.value) }))}
              />
            </div>
            <div className="field">
              <label className="label">Type</label>
                          <select
                            className="input"
                value={parameterDraft.type}
                onChange={(e) => setParameterDraft((prev) => ({ ...prev, type: e.target.value as ProviderParamType }))}
              >
                <option value="string">string</option>
                <option value="int">int</option>
                <option value="decimal">decimal</option>
                <option value="bool">bool</option>
                <option value="date">date</option>
                <option value="datetime">datetime</option>
                          </select>
                        </div>
            <div className="field">
              <label className="label">Default</label>
              <input
                className="input"
                value={parameterDefaultText}
                onChange={(e) => setParameterDefaultText(e.target.value)}
                placeholder={parameterDraft.type === "bool" ? "true | false" : ""}
              />
            </div>
            <label className="field" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={parameterDraft.required}
                onChange={(e) => setParameterDraft((prev) => ({ ...prev, required: e.target.checked }))}
              />
              Required
            </label>
            <div className="field">
              <label className="label">Description</label>
              <input
                className="input"
                value={parameterDraft.description ?? ""}
                onChange={(e) => setParameterDraft((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="actions">
              {editingParameterIndex !== null ? (
                <button className="btn btnDanger" type="button" onClick={() => void deleteParameterModal()}>
                  Delete
                </button>
                      ) : null}
              <button className="btn" type="button" onClick={closeParameterModal}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={() => void saveParameterModal()}>
                Save
              </button>
                    </div>
          </div>
            </div>
          ) : null}

      {windowsModalOpen ? (
        <div className="modalOverlay" role="presentation" onClick={closeWindowModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="reportModalTitle">{editingWindowIndex === null ? "New Window" : "Edit Window"}</div>
                <div className="field">
              <label className="label">Name</label>
              <input
                className="input"
                value={windowDraft.name}
                onChange={(e) => setWindowDraft((prev) => ({ ...prev, name: sanitizeIdentifier(e.target.value) }))}
              />
            </div>
            <div className="field">
              <label className="label">Type</label>
              <select
                className="input"
                value={windowDraft.type}
                onChange={(e) =>
                  setWindowDraft((prev) => {
                    if (e.target.value === "absolute") {
                      return {
                        name: prev.name,
                        type: "absolute",
                        start_at: new Date().toISOString(),
                        end_at: new Date(Date.now() + 60 * 60 * 1000).toISOString()
                      };
                    }
                    const nextRelative: Extract<WindowDef, { type: "relative" }> = {
                      name: prev.name,
                      type: "relative",
                      start_offset: { value: -6, unit: "month" },
                      end_offset: { value: 0, unit: "day" }
                    };
                    setWindowStartOffsetText(String(nextRelative.start_offset.value));
                    setWindowEndOffsetText(String(nextRelative.end_offset.value));
                    return nextRelative;
                  })
                }
              >
                <option value="relative">Relative</option>
                <option value="absolute">Absolute</option>
              </select>
            </div>
            {windowDraft.type === "relative" ? (
              <>
                <div className="field">
                  <label className="label">Start offset <span className="muted" style={{ fontWeight: 400 }}>(from today, e.g. −6 = 6 months ago)</span></label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      value={windowStartOffsetText}
                    onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (!/^-?\d*$/.test(raw)) return;
                        setWindowStartOffsetText(raw);
                        if (/^-?\d+$/.test(raw)) {
                          setWindowDraft((prev) => {
                            const current = prev as Extract<WindowDef, { type: "relative" }>;
                            return { ...current, start_offset: { ...current.start_offset, value: Number.parseInt(raw, 10) } };
                          });
                      }
                    }}
                  />
                    <select
                      className="input"
                      value={windowDraft.start_offset.unit}
                      onChange={(e) =>
                        setWindowDraft((prev) => {
                          const current = prev as Extract<WindowDef, { type: "relative" }>;
                          return { ...current, start_offset: { ...current.start_offset, unit: e.target.value as (typeof WINDOW_UNITS)[number] } };
                        })
                      }
                    >
                      {WINDOW_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                </div>
                </div>
                <div className="field">
                  <label className="label">End offset <span className="muted" style={{ fontWeight: 400 }}>(from today, e.g. 3 = 3 months ahead)</span></label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      value={windowEndOffsetText}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (!/^-?\d*$/.test(raw)) return;
                        setWindowEndOffsetText(raw);
                        if (/^-?\d+$/.test(raw)) {
                          setWindowDraft((prev) => {
                            const current = prev as Extract<WindowDef, { type: "relative" }>;
                            return { ...current, end_offset: { ...current.end_offset, value: Number.parseInt(raw, 10) } };
                          });
                        }
                      }}
                    />
                    <select
                      className="input"
                      value={windowDraft.end_offset.unit}
                      onChange={(e) =>
                        setWindowDraft((prev) => {
                          const current = prev as Extract<WindowDef, { type: "relative" }>;
                          return { ...current, end_offset: { ...current.end_offset, unit: e.target.value as (typeof WINDOW_UNITS)[number] } };
                        })
                      }
                    >
                      {WINDOW_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
              ) : (
                <>
                  <div className="field">
                  <label className="label">Start datetime (ISO)</label>
                    <input
                      className="input"
                    value={windowDraft.start_at}
                      onChange={(e) =>
                      setWindowDraft((prev) => {
                        const current = prev as Extract<WindowDef, { type: "absolute" }>;
                        return { ...current, start_at: e.target.value };
                      })
                      }
                    />
                  </div>
                  <div className="field">
                  <label className="label">End datetime (ISO)</label>
                    <input
                      className="input"
                    value={windowDraft.end_at}
                      onChange={(e) =>
                      setWindowDraft((prev) => {
                        const current = prev as Extract<WindowDef, { type: "absolute" }>;
                        return { ...current, end_at: e.target.value };
                      })
                      }
                    />
                  </div>
                </>
              )}
            <div className="actions">
              {editingWindowIndex !== null ? (
                <button className="btn btnDanger" type="button" onClick={deleteWindowModal}>
                  Delete
                </button>
          ) : null}
              <button className="btn" type="button" onClick={closeWindowModal}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={saveWindowModal}>
                Save
              </button>
      </div>
          </div>
            </div>
          ) : null}

      {variablesModalOpen ? (
        <div className="modalOverlay" role="presentation" onClick={closeVariableModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="reportModalTitle">{editingVariableIndex === null ? "New Variable" : "Edit Variable"}</div>
            <div className="help" style={{ marginBottom: 10 }}>
              Reference this variable in a SQL Provider&apos;s query as <code className="mono">:name</code> (single colon).
              It is substituted before the query runs — numbers stay unquoted, text and dates are quoted automatically — so
              set the <strong>Type</strong> to match how the value is used.
            </div>
            <div className="field">
              <label className="label">Name</label>
              <input
                className="input"
                value={variableDraft.name}
                onChange={(e) => setVariableDraft((prev) => ({ ...prev, name: sanitizeIdentifier(e.target.value) }))}
              />
            </div>
            <div className="field">
              <label className="label">Type</label>
              <select
                className="input"
                value={variableDraft.type}
                onChange={(e) => setVariableDraft((prev) => ({ ...prev, type: e.target.value as OperationVariableType }))}
              >
                <option value="string">string</option>
                <option value="int">int</option>
                <option value="decimal">decimal</option>
                <option value="bool">bool</option>
                <option value="date">date</option>
                <option value="datetime">datetime</option>
              </select>
            </div>
            <div className="field">
              <label className="label">Value</label>
              <input
                className="input"
                type={variableDraft.type === "int" ? "number" : "text"}
                inputMode={variableDraft.type === "decimal" ? "decimal" : variableDraft.type === "int" ? "numeric" : undefined}
                value={String(variableDraft.value ?? "")}
                onChange={(e) => {
                  let raw = e.target.value;
                  let value: string | number | boolean | null = raw;
                  if (variableDraft.type === "int") {
                    value = raw === "" ? null : Number.parseInt(raw, 10);
                  } else if (variableDraft.type === "decimal") {
                    if (raw === "") {
                      value = null;
                    } else if (/^-?\d*\.?\d*$/.test(raw)) {
                      if (raw.startsWith(".")) raw = "0" + raw;
                      else if (raw.startsWith("-.")) raw = "-0" + raw.slice(1);
                      value = raw;
                    } else {
                      value = variableDraft.value;
                    }
                  } else if (variableDraft.type === "bool") {
                    value = raw.toLowerCase() === "true";
                  }
                  setVariableDraft((prev) => ({ ...prev, value }));
                }}
                placeholder={variableDraft.type === "bool" ? "true | false" : ""}
              />
            </div>
            <div className="field">
              <label className="label">Description</label>
              <input
                className="input"
                value={variableDraft.description ?? ""}
                onChange={(e) => setVariableDraft((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="actions">
              {editingVariableIndex !== null ? (
                <button className="btn btnDanger" type="button" onClick={deleteVariableModal}>
                  Delete
                </button>
          ) : null}
              <button className="btn" type="button" onClick={closeVariableModal}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={saveVariableModal}>
                Save
              </button>
      </div>
          </div>
        </div>
      ) : null}

      {traceModal || traceModalLoading ? (
        <div className="modalOverlay" role="presentation" onClick={() => { setTraceModal(null); setTraceFocusNodeId(null); }}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="reportModalTitle">
              Trace{traceModal ? ` — ${new Date(traceModal.run.created_at).toLocaleString()}` : ""}
            </div>
            {traceModalLoading && !traceModal ? (
              <div className="muted" style={{ padding: "12px 0" }}>Loading trace…</div>
            ) : traceModal ? (
              <RunDetailPanel
                run={traceModal.run}
                nodes={orderRunNodesByFlow(
                  [traceModal.run.target_node_id, ...(traceModal.run.additional_target_node_ids ?? [])],
                  traceModal.nodes
                )}
                // ADR-0130: Trace opened from a node's History scopes to that node
                // ∪ its upstream lineage; run-level openings (focus null) show all.
                focusNodeIds={
                  traceFocusNodeId
                    ? upstreamClosureIds(
                        edges.filter((e) => e.data?.edge_type === "data").map((e) => ({ source: e.source, target: e.target })),
                        traceFocusNodeId
                      )
                    : null
                }
                nodeLabel={(id) => {
                  const n = nodes.find((x) => x.id === id);
                  return { label: n?.data.label ?? null, nodeType: n?.data.nodeType ?? null };
                }}
              />
            ) : null}
            <div className="actions" style={{ marginTop: 12 }}>
              <button type="button" className="btn" onClick={() => { setTraceModal(null); setTraceFocusNodeId(null); }}>Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedNodeRun ? (
        <div className="modalOverlay" role="presentation" onClick={() => setSelectedNodeRun(null)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="reportModalTitle">
              Node run — {new Date(selectedNodeRun.started_at).toLocaleString()}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div className="field">
                <label className="label">Status</label>
                <div style={{ color: selectedNodeRun.status === "failed" ? "var(--danger-700, #dc2626)" : "var(--success-700, #16a34a)", fontWeight: 600 }}>
                  {selectedNodeRun.status}
                </div>
              </div>
              <div className="field">
                <label className="label">Duration</label>
                <div>{selectedNodeRun.from_cache ? "From cache" : `${selectedNodeRun.duration_ms} ms`}</div>
              </div>
              <div className="field">
                <label className="label">Rows</label>
                <div>{selectedNodeRun.row_count != null ? selectedNodeRun.row_count.toLocaleString() : "–"}</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div className="field">
                <label className="label">Started</label>
                <div>{new Date(selectedNodeRun.started_at).toLocaleString()}</div>
              </div>
              <div className="field">
                <label className="label">Finished</label>
                <div>{new Date(selectedNodeRun.finished_at).toLocaleString()}</div>
              </div>
            </div>
            {selectedNodeRun.run_note ? (
              <div className="field" style={{ marginBottom: 12 }}>
                <label className="label">Run</label>
                <div className="muted">{selectedNodeRun.run_note}</div>
              </div>
            ) : null}
            {selectedNodeRun.error ? (
              <div className="field" style={{ marginBottom: 12 }}>
                <label className="label">Error</label>
                <pre style={{ margin: 0, padding: 10, background: "var(--surface, #f8fafc)", border: "1px solid var(--border)", borderRadius: 6, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", color: "var(--danger-700, #dc2626)" }}>
                  {selectedNodeRun.error}
                </pre>
              </div>
            ) : null}
            <div className="field" style={{ marginBottom: 12 }}>
              <label className="label">Node</label>
              <div>
                {(() => {
                  // The step's canvas label, never its id on screen.
                  const n = nodes.find((x) => x.id === selectedNodeRun.node_id);
                  const typeLabel = (NODE_LABELS as Record<string, string>)[selectedNodeRun.node_type] ?? selectedNodeRun.node_type;
                  const stepLabel = n?.data.label?.trim() || (n ? NODE_LABELS[n.data.nodeType] : null);
                  return stepLabel ? `${stepLabel} (${typeLabel})` : typeLabel;
                })()}
              </div>
            </div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button className="btn" type="button" onClick={() => setSelectedNodeRun(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {historyDetailEntry ? (
        <div className="modalOverlay" role="presentation" onClick={() => setHistoryDetailEntry(null)}>
          <div className="modal modalScrollable" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="modalScrollHeader reportModalTitle">
              Run details — {new Date(historyDetailEntry.run_at).toLocaleString()}
            </div>
            <div className="modalScrollBody">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12, marginTop: 12 }}>
                <div className="field">
                  <label className="label">Status</label>
                  <div style={{ color: historyDetailEntry.status === "failed" ? "var(--danger-700)" : "var(--success-700, #16a34a)", fontWeight: 600 }}>
                    {historyDetailEntry.status}
                  </div>
                </div>
                <div className="field">
                  <label className="label">Duration</label>
                  <div>{historyDetailEntry.duration_ms ?? "–"} ms</div>
                </div>
                <div className="field">
                  <label className="label">Rows</label>
                  <div>{historyDetailEntry.row_count ?? "–"}</div>
                </div>
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label className="label">Node type</label>
                <div>{historyDetailEntry.node_type}</div>
              </div>
              {historyDetailEntry.error ? (
                <div className="field" style={{ marginBottom: 12 }}>
                  <label className="label">Error</label>
                  <div style={{ color: "var(--danger-700)", whiteSpace: "pre-wrap" }}>{historyDetailEntry.error}</div>
                </div>
              ) : null}
              {historyDetailEntry.api_call ? (
                <div className="field" style={{ marginBottom: 12 }}>
                  <label className="label">
                    Request body{historyDetailEntry.api_call.summary ? ` — ${historyDetailEntry.api_call.summary}` : ""}
                  </label>
                  {historyDetailEntry.api_call.body === null ? (
                    <div className="muted">Call never assembled — failure happened before the request body was built.</div>
                  ) : (
                    <pre style={{ margin: 0, padding: 10, background: "var(--surface, #f8fafc)", border: "1px solid var(--border)", borderRadius: 6, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                      {JSON.stringify(historyDetailEntry.api_call.body, null, 2)}
                    </pre>
                  )}
                </div>
              ) : null}
              {(historyDetailEntry.resolved_sql || Object.keys(historyDetailEntry.resolved_params).length > 0) ? (
                <>
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label className="label">Resolved parameters</label>
                    {Object.keys(historyDetailEntry.resolved_params).length === 0 ? (
                      <div className="muted">No parameters</div>
                    ) : (
                      <table className="table">
                        <thead>
                          <tr><th>Parameter</th><th>Value</th></tr>
                        </thead>
                        <tbody>
                          {Object.entries(historyDetailEntry.resolved_params).map(([paramKey, paramVal]) => (
                            <tr key={paramKey}>
                              <td><code>{paramKey}</code></td>
                              <td><code>{paramVal === null ? "NULL" : String(paramVal)}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {historyDetailEntry.resolved_sql ? (
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label className="label">Resolved SQL</label>
                      <pre style={{ margin: 0, padding: 10, background: "var(--surface, #f8fafc)", border: "1px solid var(--border)", borderRadius: 6, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                        {historyDetailEntry.resolved_sql}
                      </pre>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="modalScrollFooter actions" style={{ marginTop: 16 }}>
              <button className="btn" type="button" onClick={() => setHistoryDetailEntry(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
      </MLNodeRunProvider>
    </PageLayout>
  );
}
